import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { Buffer } from 'buffer';
import mapboxgl from 'mapbox-gl';
import turfCircle from '@turf/circle';
import 'mapbox-gl/dist/mapbox-gl.css';
import './App.css';
import { Wallet, isMobileBrowser, hasWalletConnect } from './wallet';
import { ContractClient } from './contract';
import { ReadOnlyContractClient } from './contract-readonly';
import { GamePanel } from './GamePanel';
import { ConfirmationOverlay } from './ConfirmationOverlay';
import { NotificationOverlay } from './NotificationOverlay';
import { SessionDetailsOverlay } from './SessionDetailsOverlay';
import { CountryManagementOverlay } from './CountryManagementOverlay';
import { CountryProfileOverlay } from './CountryProfileOverlay';
import ProfileOverlay from './components/ProfileOverlay';
import { createLiveSessionPaymentHandler } from './services/liveSessionPaymentService';
import { iso2ToNumeric, iso3ToIso2 } from './countryCodes';
import { extractCountryInfo, getFlagUrl } from './utils/countryVaultUtils';
import {
  ensureCountryOverlayLayers,
  syncBasemapAdminBoundaries,
  promoteCountryOverlayLayers,
  syncCountryBorderSource,
  reapplyCountryOverlay,
  invalidateCountryBorderCache,
} from './utils/countryOverlayStyle';
import { Horizon } from '@stellar/stellar-sdk';
import { geolinkApi, NearbyUser, NearbyNFT, NearbyContract, PendingDepositAction } from './services/geolinkApi';
import { PasskeySetupModal } from './components/PasskeySetupModal';
import PasskeyKitPanel from './components/PasskeyKitPanel';
import { passkeyKitService } from './services/passkeyKitService';
import { NavigationMenu } from './components/NavigationMenu';
import { attachMarkerTap } from './utils/markerTap';
import { LiveDirectoryOverlay } from './components/LiveDirectoryOverlay';
import AgentMarkerLayer, { type AgentListEntry } from './components/AgentMarkerLayer';
import AgentTeleportHUD from './components/map/AgentTeleportHUD';
import { useAgentTeleportMode } from './hooks/useAgentTeleportMode';
import { listAgents as listOperatorAgents } from './services/agentService';
import { getMovementStatus as getAgentMovementStatus, getActiveAgentCountries } from './services/agentMovementService';
import { LiveFab } from './components/LiveFab';
import { useLiveFeed } from './hooks/useLiveFeed';
import { DEFAULT_ALGORITHM_ID } from './services/liveFeedAlgorithms';
import { SettingsOverlay } from './components/SettingsOverlay';
import MapSceneController from './components/MapSceneController';
import { SmartContractsOverlay } from './components/SmartContractsOverlay';
import { GeoTrustDashboard } from './components/GeoTrustDashboard';
import { AdminOverlay } from './components/AdminOverlay';
import { BorderProposalsDashboard } from './components/BorderProposalsDashboard';
import { SessionsOverlay } from './components/SessionsOverlay';
import { PendingDepositsOverlay } from './components/PendingDepositsOverlay';
import { TrustGroupManager } from './components/TrustGroupManager';
import { RuleMatchDetailOverlay, type RuleMatchInfo } from './components/RuleMatchDetailOverlay';
import { listPendingDeposits } from './v2/services/pendingDepositService';
import { shortenStellarAddress } from './utils/stellarDisplay';
import WatchlistAdminOverlay from './components/WatchlistAdminOverlay';
import ComplianceBanner from './components/ComplianceBanner';
import { evaluateWalletAtLogin, WatchlistEvaluation } from './services/watchlistEnforcement';
import TelemetryLayer from './components/TelemetryLayer';
import RegisterTestEntityOverlay from './components/RegisterTestEntityOverlay';
import { SpatialObjectsMapLayer } from './components/spatial';
import { SpatialObjectsAdminOverlay, type SpatialAdminOpenParams } from './components/spatial/SpatialObjectsAdminOverlay';
import { executeSpatialContractCall } from './services/spatial/spatialContractExecution';
import { SpatialObjectsCountrySection } from './components/spatial/SpatialObjectsCountrySection';
import { MapEntityTooltip, tooltipBadgeStyle } from './components/map/MapEntityTooltip';
import { surfaceZIndex } from './components/map/mapSurfaces';
import { useOpenMajorSurface } from './components/map/mapOverlayStore';
import { getCountryBounds } from './components/spatial/countryBounds';
import { isMapClicksClaimed } from './components/spatial/mapClickClaim';
import { spatialFeaturesAt, tapTolerancePx } from './components/spatial/spatialLayerUtils';
import { buildingAwareZoom, maxRenderedBuildingHeightM } from './utils/mapAnimation';

// Helper function to construct NFT image URL (matching xyz-wallet exactly)
function cleanServerUrl(serverUrl: string | null | undefined): string | null {
  if (!serverUrl) return null;
  
  let baseUrl = serverUrl.trim();
  
  // Remove any existing /ipfs/ path and everything after it
  baseUrl = baseUrl.replace(/\/ipfs\/.*$/i, '');
  
  // Remove trailing slashes
  baseUrl = baseUrl.replace(/\/+$/, '');
  
  // Remove protocol if present (we'll add https://)
  baseUrl = baseUrl.replace(/^https?:\/\//i, '');
  
  // Add https:// protocol
  if (baseUrl) {
    return `https://${baseUrl}`;
  }
  
  return null;
}

function constructImageUrl(serverUrl: string | null | undefined, ipfsHash: string | null | undefined): string {
  if (!ipfsHash) {
    return 'https://via.placeholder.com/200x200?text=NFT';
  }
  
  const baseUrl = cleanServerUrl(serverUrl);
  if (!baseUrl) {
    // Fallback to public IPFS gateway
    return `https://ipfs.io/ipfs/${ipfsHash}`;
  }
  
  return `${baseUrl}/ipfs/${ipfsHash}`;
}

/** Canonical key for comparing Stellar addresses from GeoLink and trust groups */
function stellarAddrKey(addr: string | null | undefined): string {
  if (!addr || typeof addr !== 'string') return '';
  return addr.trim().toUpperCase();
}

// Set Mapbox access token
const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN;
if (mapboxToken) {
  mapboxgl.accessToken = mapboxToken;
  // console.log('[App] Mapbox token loaded');
} else {
  // console.error('[App] Mapbox token not found! Please set REACT_APP_MAPBOX_TOKEN in .env.local');
}

// ─── 3D mode helpers ─────────────────────────────────────────────────────────
const BUILDINGS_LAYER_ID = '3d-buildings';
/** Zoom level at and above which auto-3D adds the building extrusions. */
const ZOOM_3D_ON  = 15;
/** Zoom level below which auto-3D drops the buildings (hysteresis vs ON). */
const ZOOM_3D_OFF = 14;

/**
 * Make sure the GeoLink spatial-objects layers ALWAYS paint on top of
 * Mapbox's gray 3d-buildings (and below the place-name labels) by
 * moving each `gl-spatial-*` layer to sit immediately before the first
 * symbol/label layer.
 *
 * Mapbox's `addLayer(layer, before)` only positions the new layer
 * relative to the named layer — subsequent insertions can still end up
 * above us. Calling `moveLayer` after every potentially-occluding layer
 * change is the only deterministic way to keep the spatial overlays
 * visible at the user's footprint.
 */
function liftSpatialLayersAboveBuildings(m: mapboxgl.Map) {
  try {
    const layers = m.getStyle().layers ?? [];
    const labelLayer = layers.find(
      (l: any) => l.type === 'symbol' && (l.layout as any)?.['text-field']
    );
    const beforeId = labelLayer?.id;
    for (const l of layers) {
      if (typeof l.id !== 'string') continue;
      if (!l.id.startsWith('gl-spatial-')) continue;
      // The MapTiler raster overlay should stay BELOW the spatial
      // extrusions (it's a basemap-style raster layer), so leave it
      // where it is.
      if (l.id.includes('maptiler-raster')) continue;
      try {
        m.moveLayer(l.id, beforeId);
      } catch { /* layer may have just been removed */ }
    }
  } catch { /* style may not be ready yet */ }
}

function add3DBuildings(m: mapboxgl.Map) {
  if (m.getLayer(BUILDINGS_LAYER_ID)) return;
  // Mapbox-gl-js #11820: adding a fill-extrusion layer SYNCHRONOUSLY
  // inside a `styledata` / `style.load` / zoom event handler can leave
  // the new layer's internal `layout` Layout instance un-populated
  // until the next style recalculation. The very next render frame
  // then crashes inside `draw_fill_extrusion.ts` at
  // `layer.layout.get('fill-extrusion-edge-radius')` with
  // "Cannot read properties of undefined (reading 'get')". Defer one
  // macrotask so Mapbox finishes its style processing before we drop
  // a new fill-extrusion layer into it.
  setTimeout(() => {
    if (!m || m.getLayer(BUILDINGS_LAYER_ID)) return;
    try {
      const layers = m.getStyle()?.layers ?? [];
      // Order matters: pick the FIRST GeoLink spatial layer if any are
      // already mounted, so 3d-buildings inserts BELOW them and our
      // colored extrusions paint on top. Otherwise fall back to the
      // first symbol/label layer so 3d-buildings still stays under the
      // place-name labels for readability.
      const firstSpatial = layers.find((l: any) =>
        typeof l.id === 'string' &&
        l.id.startsWith('gl-spatial-') &&
        !l.id.includes('maptiler-raster'),
      );
      const labelLayer = layers.find(
        (l: any) => l.type === 'symbol' && (l.layout as any)?.['text-field']
      );
      const beforeId = firstSpatial?.id ?? labelLayer?.id;
      m.addLayer(
        {
          id: BUILDINGS_LAYER_ID,
          source: 'composite',
          'source-layer': 'building',
          filter: ['==', 'extrude', 'true'],
          type: 'fill-extrusion',
          minzoom: 14,
          // Mapbox 3.x's painter reads `layer.layout.get('fill-
          // extrusion-edge-radius')` unconditionally during the
          // translucent render pass — pass an explicit empty layout
          // so the Layout instance is always materialised even when
          // the layer is added during a style swap.
          layout: {},
          paint: {
            'fill-extrusion-color': '#aab0c0',
            'fill-extrusion-height': ['interpolate', ['linear'], ['zoom'], 15, 0, 15.05, ['get', 'height']],
            'fill-extrusion-base': ['interpolate', ['linear'], ['zoom'], 15, 0, 15.05, ['get', 'min_height']],
            'fill-extrusion-opacity': 0.65,
          },
        } as any,
        beforeId
      );
      // After adding 3d-buildings, deterministically lift any spatial
      // layers that already exist so they always paint on top.
      liftSpatialLayersAboveBuildings(m);
    } catch (_) { /* style may not include building source */ }
  }, 0);
}

function remove3DBuildings(m: mapboxgl.Map) {
  try { if (m.getLayer(BUILDINGS_LAYER_ID)) m.removeLayer(BUILDINGS_LAYER_ID); } catch (_) {}
}

function flyToWithMode(m: mapboxgl.Map, center: [number, number], zoomIn: boolean) {
  // Remove buildings immediately on zoom-out so they don't linger during flight
  if (!zoomIn) remove3DBuildings(m);
  m.flyTo({ center, zoom: zoomIn ? 18 : 2, pitch: zoomIn ? 60 : 0, bearing: zoomIn ? -12 : 0, speed: 1.6 });
  if (zoomIn) {
    m.once('moveend', () => add3DBuildings(m));
  }
  // Note: pitch/bearing are handled by flyTo options — do NOT call setPitch/setBearing separately
  // as that interrupts the flyTo animation
}
// ─────────────────────────────────────────────────────────────────────────────

// Helper function to calculate cell ID from lat/lon
function calculateCellId(lat: number, lng: number): number {
  // Simple grid-based cell calculation
  const gridSize = 1.0; // degrees
  const cellX = Math.floor((lng + 180) / gridSize);
  const cellY = Math.floor((lat + 90) / gridSize);
  return cellY * 360 + cellX;
}

// Convert GeoLink's string cell_id format (e.g., "34.230000_-118.232000") to numeric cell_id
function parseGeoLinkCellId(cellId: string | number): number {
  // If already a number, return as-is (backward compatibility)
  if (typeof cellId === 'number') {
    return cellId;
  }
  
  // Parse string format: "lat_lon" (e.g., "34.230000_-118.232000")
  try {
    const parts = cellId.split('_');
    if (parts.length >= 2) {
      const lat = parseFloat(parts[0]);
      const lon = parseFloat(parts[1]);
      if (!isNaN(lat) && !isNaN(lon)) {
        return calculateCellId(lat, lon);
      }
    }
  } catch (error) {
    // console.warn('[App] Failed to parse GeoLink cell_id:', cellId, error);
  }
  
  // Fallback: hash the string to get a deterministic numeric value
  // This ensures we always have a valid number even if format is unexpected
  let hash = 0;
  for (let i = 0; i < cellId.length; i++) {
    const char = cellId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

// Trustline Comparison Component
const TrustlineComparison: React.FC<{ myPublicKey: string; theirPublicKey: string }> = ({ myPublicKey, theirPublicKey }) => {
  const [loading, setLoading] = useState(true);
  const [commonAssets, setCommonAssets] = useState<Array<{ asset: string; myBalance: string; theirBalance: string }>>([]);
  const [onlyMyAssets, setOnlyMyAssets] = useState<Array<{ asset: string; balance: string }>>([]);
  const [onlyTheirAssets, setOnlyTheirAssets] = useState<Array<{ asset: string; balance: string }>>([]);

  useEffect(() => {
    const fetchTrustlines = async () => {
      setLoading(true);
      try {
        const server = new Horizon.Server('https://horizon-testnet.stellar.org');
        
        // Fetch both accounts' trustlines
        const [myAccount, theirAccount] = await Promise.all([
          server.loadAccount(myPublicKey),
          server.loadAccount(theirPublicKey)
        ]);

        // Extract trustlines (non-native balances)
        const myTls = myAccount.balances
          .filter((b: any) => b.asset_type !== 'native')
          .map((b: any) => ({
            asset: `${b.asset_code}:${b.asset_issuer}`,
            balance: b.balance
          }));
        
        const theirTls = theirAccount.balances
          .filter((b: any) => b.asset_type !== 'native')
          .map((b: any) => ({
            asset: `${b.asset_code}:${b.asset_issuer}`,
            balance: b.balance
          }));

        // Process trustlines to find differences

        // Find common assets
        const myAssetMap = new Map(myTls.map(tl => [tl.asset, tl.balance]));
        const theirAssetMap = new Map(theirTls.map(tl => [tl.asset, tl.balance]));
        
        const common: Array<{ asset: string; myBalance: string; theirBalance: string }> = [];
        const onlyMine: Array<{ asset: string; balance: string }> = [];
        const onlyTheirs: Array<{ asset: string; balance: string }> = [];

        myTls.forEach(tl => {
          if (theirAssetMap.has(tl.asset)) {
            common.push({
              asset: tl.asset,
              myBalance: tl.balance,
              theirBalance: theirAssetMap.get(tl.asset)!
            });
          } else {
            onlyMine.push(tl);
          }
        });

        theirTls.forEach(tl => {
          if (!myAssetMap.has(tl.asset)) {
            onlyTheirs.push(tl);
          }
        });

        setCommonAssets(common);
        setOnlyMyAssets(onlyMine);
        setOnlyTheirAssets(onlyTheirs);
      } catch (error) {
        // console.error('[TrustlineComparison] Failed to fetch trustlines:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTrustlines();
  }, [myPublicKey, theirPublicKey]);

  if (loading) {
    return (
      <div className="marker-popup-field" style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #eee' }}>
        <label>Loading Trustlines...</label>
      </div>
    );
  }

  return (
    <div className="marker-popup-field" style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #eee' }}>
      <label style={{ fontWeight: 700, marginBottom: '8px' }}>Trustline Comparison</label>
      
      {commonAssets.length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#666', marginBottom: '4px' }}>
            Common Assets ({commonAssets.length}):
          </div>
          <div style={{ maxHeight: '150px', overflowY: 'auto', fontSize: '11px' }}>
            {commonAssets.map((asset, idx) => (
              <div key={idx} style={{ padding: '4px', backgroundColor: '#f9f9f9', borderRadius: '4px', marginBottom: '4px' }}>
                <div style={{ fontWeight: 600 }}>{asset.asset.split(':')[0]}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#666' }}>
                  <span>You: {parseFloat(asset.myBalance).toFixed(2)}</span>
                  <span>Them: {parseFloat(asset.theirBalance).toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {onlyMyAssets.length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#666', marginBottom: '4px' }}>
            Only You Have ({onlyMyAssets.length}):
          </div>
          <div style={{ maxHeight: '100px', overflowY: 'auto', fontSize: '11px' }}>
            {onlyMyAssets.map((asset, idx) => (
              <div key={idx} style={{ padding: '4px', backgroundColor: '#fff3cd', borderRadius: '4px', marginBottom: '2px' }}>
                {asset.asset.split(':')[0]} ({parseFloat(asset.balance).toFixed(2)})
              </div>
            ))}
          </div>
        </div>
      )}

      {onlyTheirAssets.length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#666', marginBottom: '4px' }}>
            Only They Have ({onlyTheirAssets.length}):
          </div>
          <div style={{ maxHeight: '100px', overflowY: 'auto', fontSize: '11px' }}>
            {onlyTheirAssets.map((asset, idx) => (
              <div key={idx} style={{ padding: '4px', backgroundColor: '#d1ecf1', borderRadius: '4px', marginBottom: '2px' }}>
                {asset.asset.split(':')[0]} ({parseFloat(asset.balance).toFixed(2)})
              </div>
            ))}
          </div>
        </div>
      )}

      {commonAssets.length === 0 && onlyMyAssets.length === 0 && onlyTheirAssets.length === 0 && (
        <div style={{ fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
          No trustlines found for comparison
        </div>
      )}
    </div>
  );
};

/**
 * Compact (22 × 22) icon chip style used by the wallet panel's
 * quick-peek row (👤 / 🎥 / 💎).  Defined module-level so the object
 * reference is stable and doesn't allocate on every render.
 */
const profileChipStyle: React.CSSProperties = {
  width: 22,
  height: 22,
  padding: 0,
  margin: '0 1px',
  fontSize: 13,
  lineHeight: 1,
  border: 'none',
  borderRadius: 6,
  background: 'transparent',
  color: '#334155',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

interface AppProps {
  /** Pre-set the ProfileOverlay target on first mount.  Used by the
   *  /geoai/:publicKey deep-link route in src/index.tsx so the visitor
   *  lands directly on a public profile without needing to connect a
   *  wallet first.  Anonymous viewers stay anonymous (isOwner=false →
   *  contract returns the redacted public-profile shape). */
  initialProfileWallet?: string;
}
const App: React.FC<AppProps> = ({ initialProfileWallet }) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [mapZoom, setMapZoom] = useState<number>(2); // Track map zoom level
  const [showSettings, setShowSettings] = useState(false);
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [contractClient, setContractClient] = useState<ContractClient | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [lastAnchorEvent, setLastAnchorEvent] = useState<{
    eventId: string;
    eventType: string;
    occurredAt: string;
    txHash?: string;
    cellId?: string | number;
  } | null>(null);
  // Tracks whether the connected wallet is the main admin OR any country admin
  // (used to show the Border Proposals menu item to country admins too).
  const [isAdminOrCountryAdmin, setIsAdminOrCountryAdmin] = useState(false);

  /**
   * Protocol admin, resolved only from `GeoTrustCore.get_admin()`.
   *
   * Starts false and stays false until the contract says otherwise. It used
   * to be seeded from `localStorage.geotrust_isAdmin`, which meant a browser
   * that had once been admin came back as admin before any contract read
   * happened — admin failing open on a cache. The state this protects is
   * country admin assignment and emergency intervention, so the only
   * acceptable answer to "we could not check" is no.
   */
  const [isAdmin, setIsAdmin] = useState(false);
  const [walletAddress, setWalletAddress] = useState<string | null>(null);
  const [mainAdminAddress, setMainAdminAddress] = useState<string | null>(null);

  // Pipe the connected wallet into the GeoLink API client singleton on every
  // change. The client uses this to stamp `X-Public-Key` on every outgoing
  // request and to scope list calls via `?public_key=`, per the multi-tenant
  // scoping integration guide §3 (docs/geolink-multi-tenant-scoping-response.md).
  // When the wallet is cleared we revert to API-key-only mode so passive
  // Wallet-Provider / Data-Consumer callers (no `?public_key=`) keep their
  // existing unscoped behaviour.
  useEffect(() => {
    geolinkApi.setConnectedPublicKey(walletAddress);
  }, [walletAddress]);
  const [overlayMinimized, setOverlayMinimized] = useState(false);
  const [allowedCountries, setAllowedCountries] = useState<Set<number>>(new Set<number>());
  const [defaultAllowAll, setDefaultAllowAll] = useState<boolean>(false);
  const [countryDataLoaded, setCountryDataLoaded] = useState<boolean>(false);
  // Alpha-2 country code of the user's most-recently-resolved location.
  // Drives the AgentMarkerLayer's WS subscription so we only stream
  // agent positions inside the country the user is currently viewing.
  const [currentCountryAlpha2, setCurrentCountryAlpha2] = useState<string | null>(null);
  const [totalCountries, setTotalCountries] = useState<number>(203);
  // ISO-2 countries where THIS operator's own agents are currently
  // running/paused. Merged into `watchedAgentCountries` so an operator
  // always sees their own agents on the map even when their camera is in
  // a different country (e.g. you teleport an agent to Portugal but keep
  // your map over the US — without this the PT agent would never paint).
  const [myAgentCountries, setMyAgentCountries] = useState<string[]>([]);
  // ISO-2 countries with ANY running/paused agent, across every operator.
  // Public (wallet-independent) so a teleported agent (e.g. in Portugal)
  // paints for every viewer regardless of camera position or whether a
  // wallet is connected — not just for its own operator.
  const [activeAgentCountries, setActiveAgentCountries] = useState<string[]>([]);

  /**
   * Set of ISO 3166-1 alpha-2 countries we want AgentMarkerLayer to
   * subscribe to. Resolution order, first match wins for the *base*
   * (extras from the URL are always merged on top):
   *
   *   1. The camera's resolved country (if geolocation succeeded).
   *   2. The build-time `REACT_APP_DEFAULT_AGENT_COUNTRIES` env var,
   *      e.g. `REACT_APP_DEFAULT_AGENT_COUNTRIES=US,CA,MX,BR`. This
   *      gives anonymous, no-GPS, no-URL-param visitors something
   *      meaningful on the map without forcing them through the
   *      "Share my location" prompt. Ops can change it per
   *      environment without code changes.
   *   3. URL override `?agentCountries=US,CA,...` is *always* merged
   *      on top of whatever the base is, so fleet operators can
   *      deep-link a watch list and never lose the camera country
   *      they're physically in.
   *
   * Order is irrelevant after the merge — AgentMarkerLayer dedupes
   * + uppercases internally and the hook key is sort-stable.
   */
  const watchedAgentCountries = useMemo(() => {
    const base: string[] = [];
    if (currentCountryAlpha2) {
      base.push(currentCountryAlpha2.toUpperCase());
    } else {
      // Fall back to ops-controlled default countries when we have no
      // GPS lock yet (or the user denied location). Empty / unset env
      // means "stay dormant" — same as the original behaviour.
      const fallback = (process.env.REACT_APP_DEFAULT_AGENT_COUNTRIES || '')
        .split(',')
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean);
      for (const c of fallback) if (!base.includes(c)) base.push(c);
    }
    try {
      const params = new URLSearchParams(window.location.search);
      const extra = params.get('agentCountries');
      if (extra) {
        for (const c of extra.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean)) {
          if (!base.includes(c)) base.push(c);
        }
      }
    } catch { /* SSR / hostile env — ignore and stick with the camera country */ }
    // Always include the countries where this operator's own agents are
    // active, so they stay visible on the map regardless of camera country.
    for (const c of myAgentCountries) {
      if (c && !base.includes(c)) base.push(c);
    }
    // Plus every country with a live agent globally (wallet-independent),
    // so teleported agents paint for all viewers even when disconnected.
    for (const c of activeAgentCountries) {
      if (c && !base.includes(c)) base.push(c);
    }
    return base;
  }, [currentCountryAlpha2, myAgentCountries, activeAgentCountries]);

  // Track the countries of the operator's own running/paused agents so the
  // marker layer keeps a subscription open for them even when the camera is
  // elsewhere. Polls lightly (every 30s) and only while a wallet is
  // connected; failures are swallowed so the map never breaks on a hiccup.
  useEffect(() => {
    if (!walletAddress) { setMyAgentCountries([]); return; }
    let cancelled = false;
    const operator = walletAddress;

    const refresh = async () => {
      try {
        const agents = await listOperatorAgents({ operator }).catch(() => []);
        if (cancelled || agents.length === 0) { if (!cancelled) setMyAgentCountries([]); return; }
        const statuses = await Promise.all(
          agents.map((a) =>
            getAgentMovementStatus(a.address)
              .then((s) => s)
              .catch(() => null),
          ),
        );
        if (cancelled) return;
        const countries = new Set<string>();
        for (const s of statuses) {
          if (s && (s.status === 'running' || s.status === 'paused') && s.country) {
            countries.add(s.country.toUpperCase());
          }
        }
        setMyAgentCountries((prev) => {
          const next = Array.from(countries).sort();
          // Avoid needless state churn (which would rebuild WS subs) when
          // the set is unchanged.
          if (prev.length === next.length && prev.every((c, i) => c === next[i])) return prev;
          return next;
        });
      } catch { /* swallow — best effort */ }
    };

    void refresh();
    const id = setInterval(refresh, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [walletAddress]);

  // Track EVERY country with a live agent (across all operators) so the
  // marker layer keeps a subscription open for them regardless of camera
  // position or wallet connection. This is the wallet-independent
  // counterpart to `myAgentCountries`: without it, a teleported agent
  // (e.g. Portugal) only paints for its own operator while connected.
  // Polls lightly (every 30s); failures are swallowed.
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const countries = await getActiveAgentCountries();
        if (cancelled) return;
        const next = countries.map((c) => c.toUpperCase()).filter(Boolean).sort();
        setActiveAgentCountries((prev) => {
          // Avoid needless state churn (which would rebuild WS subs).
          if (prev.length === next.length && prev.every((c, i) => c === next[i])) return prev;
          return next;
        });
      } catch { /* swallow — best effort */ }
    };
    void refresh();
    const id = setInterval(refresh, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // ── Live location tracking (auto-update yellow marker while moving) ───────
  type MovementMode = 'stationary' | 'walking' | 'driving' | 'flying';

  const MODE_RANK: Record<MovementMode, number> = { stationary: 0, walking: 1, driving: 2, flying: 3 };

  const [isLiveTracking,  setIsLiveTracking]  = useState(false);
  const [movementMode,    setMovementMode]    = useState<MovementMode>('stationary');
  /** User-selected override — null means "use GPS detection" */
  const [manualMode,      setManualMode]      = useState<MovementMode | null>(null);
  /** What the map/UI actually uses: manual override wins if it's ≥ detected speed */
  const effectiveMode: MovementMode = manualMode ?? movementMode;

  const watchPositionIdRef      = useRef<number | null>(null);
  const livePlayerMarkerRef     = useRef<mapboxgl.Marker | null>(null);
  const liveTrackingLastApiRef  = useRef<number>(0);
  const prevPositionRef         = useRef<[number, number] | null>(null);
  /**
   * Wallet addresses of users currently broadcasting live.  Populated
   * by the always-polling `liveMarkerFeed` hook further down and
   * consumed by `updateOtherUserMarkers` (which reads the ref at
   * marker-creation time) plus a DOM-sync effect (which toggles the
   * `.live` class on already-rendered markers when the set changes).
   * A ref rather than state so updating the set doesn't re-run the
   * expensive marker rebuild pipeline.
   */
  const liveBroadcasterWalletsRef = useRef<Set<string>>(new Set());
  /** Stable refs so watchPosition callbacks always read fresh values */
  const effectiveModeRef        = useRef<MovementMode>('stationary');
  /** Rolling speed history for smoothing noisy GPS speed values */
  const speedHistoryRef         = useRef<number[]>([]);
  /** Consecutive count of the same raw mode — used for hysteresis */
  const pendingModeRef          = useRef<{ mode: MovementMode; count: number }>({ mode: 'stationary', count: 0 });
  /** Max interval (ms) between GeoLink API pushes while live-tracking */
  const LIVE_API_INTERVAL = 12_000;

  // Keep effectiveModeRef in sync
  useEffect(() => { effectiveModeRef.current = effectiveMode; }, [effectiveMode]);

  // Immediately apply camera transition when effective mode changes while live
  useEffect(() => {
    if (!isLiveTracking || !map.current?.loaded() || !playerLocation) return;
    if (teleportActiveRef.current) return;   // teleport owns the camera
    const followCfg: Record<string, { zoom: number; pitch: number; duration: number } | null> = {
      walking:    { zoom: 17, pitch: 15,  duration: 600  },
      driving:    { zoom: 15, pitch: 45,  duration: 700  },
      flying:     { zoom: 12, pitch: 55,  duration: 900  },
      stationary: null,
    };
    const cfg = followCfg[effectiveMode];
    if (cfg) {
      map.current.easeTo({
        center:   [playerLocation[0], playerLocation[1]],
        zoom:     cfg.zoom,
        pitch:    cfg.pitch,
        bearing:  map.current.getBearing(),
        duration: cfg.duration,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveMode, isLiveTracking]);

  // If GPS detects a faster mode than the manual override, clear the override
  useEffect(() => {
    if (manualMode !== null && MODE_RANK[movementMode] > MODE_RANK[manualMode]) {
      setManualMode(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movementMode]);

  const detectMode = (speed: number | null): MovementMode => {
    // Raw classification from a single smoothed-speed value (m/s)
    if (speed === null || speed < 0.4) return 'stationary';
    if (speed < 3.0) return 'walking';  // 0.4–3 m/s  ≈ 1.4–11 km/h
    if (speed < 70)  return 'driving';  // 3–70 m/s   ≈ 11–252 km/h
    return 'flying';
  };

  /** Set manual mode — only allowed if target rank ≥ detected rank */
  const handleSetMode = (mode: MovementMode) => {
    if (MODE_RANK[mode] < MODE_RANK[movementMode]) return; // GPS says you're faster
    setManualMode(prev => prev === mode ? null : mode);
  };

  /** Clear manual override, return to GPS auto-detection */
  const handleClearMode = () => setManualMode(null);

  /** Bearing (degrees) from point A to point B */
  const calcBearing = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const lat1R = lat1 * Math.PI / 180;
    const lat2R = lat2 * Math.PI / 180;
    const y = Math.sin(dLng) * Math.cos(lat2R);
    const x = Math.cos(lat1R) * Math.sin(lat2R) - Math.sin(lat1R) * Math.cos(lat2R) * Math.cos(dLng);
    return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
  };
  // Ref mirror of countryDataLoaded — readable inside map event callbacks without stale closure
  const countryDataLoadedRef = useRef<boolean>(false);
  const [playerLocation, setPlayerLocation] = useState<[number, number] | null>(null);
  const [sessionLink, setSessionLink] = useState<string>('');
  const [walletError, setWalletError] = useState<string | null>(null);
  const [showOtherUsers, setShowOtherUsers] = useState(true);
  const [maxDistance, setMaxDistance] = useState(20000); // km - default to 20000 (max allowed)
  const [otherUsers, setOtherUsers] = useState<Array<{ id: string; location: [number, number]; distance: number }>>([]);
  // Note: nearbyNFTs and nearbyContracts are fetched but not yet displayed in UI
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [nearbyNFTs, setNearbyNFTs] = useState<NearbyNFT[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [nearbyContracts, setNearbyContracts] = useState<NearbyContract[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // Note: Sessions are managed entirely on-chain, not in GeoLink
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [isWalletConnecting, setIsWalletConnecting] = useState(false);
  // Ref so the stable MutationObserver closure can read the latest value without
  // re-registering observers on every render.
  const isWalletConnectingRef = useRef(false);
  const [pendingDeposits, setPendingDeposits] = useState<PendingDepositAction[]>([]);
  const [pendingGeoLinkCount, setPendingGeoLinkCount] = useState(0);
  const [dashboardInitialSection, setDashboardInitialSection] = useState<string | undefined>(undefined);
  const hasCheckedInRef = useRef(false);
  const [readOnlyClient, setReadOnlyClient] = useState<ReadOnlyContractClient | null>(null);
  const [activeSessions, setActiveSessions] = useState<Array<{ 
    sessionId: number; 
    player1: string | null; 
    player2: string | null; 
    state: string;
    p1CellId?: number;
    p2CellId?: number;
    p1Country?: number;
    p2Country?: number;
    createdLedger?: number;
    players?: Array<{ address: string; cell_id: number; asset_tag: any; country: number; location_proof?: any }>;
    isPublic?: boolean;
    is_public?: boolean;
    /** Trust-group scope for private live-presence sessions (empty for public). */
    trustGroupIds?: number[];
  }>>([]);
  const [accountBalance, setAccountBalance] = useState<string | null>(null);
  const [userCurrentSession, setUserCurrentSession] = useState<number | null>(null);
  const [pendingSessionJoin, setPendingSessionJoin] = useState<number | null>(null);
  // Track sessions that the user has explicitly ended (so we don't re-add them)
  const endedSessionsRef = useRef<Set<number>>(new Set());
  // Track the most recently joined session to prevent checkUserSession from overwriting it
  const recentlyJoinedSessionRef = useRef<number | null>(null);
  const [gamePanelMinimized, setGamePanelMinimized] = useState(false);
  /** Wallets from trust groups (Trust overlay chips); used when applyTrustGroupMapFilter is on */
  const [trustGroupMapAllowlist, setTrustGroupMapAllowlist] = useState<string[]>([]);
  const [applyTrustGroupMapFilter, setApplyTrustGroupMapFilter] = useState(false);
  const trustGroupMapMemberSet = useMemo(() => {
    const s = new Set<string>();
    for (const a of trustGroupMapAllowlist) {
      const k = stellarAddrKey(a);
      if (k) s.add(k);
    }
    return s;
  }, [trustGroupMapAllowlist]);

  /** Count of nearby wallets shown on the map under current filters */
  const gamePanelNearbyOthersCount = useMemo(() => {
    if (!showOtherUsers) return 0;
    const inRange = otherUsers.filter((u) => u.distance <= maxDistance);
    if (!applyTrustGroupMapFilter) return inRange.length;
    if (trustGroupMapMemberSet.size === 0) return 0;
    return inRange.filter((u) => trustGroupMapMemberSet.has(stellarAddrKey(u.id))).length;
  }, [showOtherUsers, otherUsers, maxDistance, applyTrustGroupMapFilter, trustGroupMapMemberSet]);

  /**
   * Live snapshot of every AI agent currently rendered by
   * AgentMarkerLayer (any watched country). The marker layer owns the
   * WebSocket subscription and emits a fresh array on every change;
   * we keep it here so the GamePanel can render an "AI agents (N)"
   * sibling pill next to "Others on map (N)". The previous behaviour
   * silently dropped agents because that counter was scoped to
   * GeoLink human sessions only.
   */
  const [agentMarkerSnapshot, setAgentMarkerSnapshot] = useState<AgentListEntry[]>([]);

  /**
   * Cinematic teleport / follow mode for the AI agents rendered by
   * AgentMarkerLayer. The hook owns the camera animation phase
   * machine, the route-line layer, every timer, and the
   * pre-teleport selection panel state. App.tsx only:
   *
   *   1. forwards the live agents snapshot it already maintains,
   *   2. routes agent-marker clicks into `requestPanel(pk)`,
   *   3. renders <AgentTeleportHUD /> once near the map,
   *   4. passes `selectedAgentId` back into AgentMarkerLayer so the
   *      focused marker glows + every other agent dims.
   *
   * No camera calls live in App.tsx itself — the hook is the single
   * source of truth so cleanup-on-exit stays trivially correct.
   */
  // Hoisted out of the original "Map settings state" block (which
  // lives further down the file) so the teleport hook below can see
  // them — the hook needs `mapStyle` for its "Original" style chip
  // snapshot and `setMapStyle` to keep this component's mirror in
  // sync when the HUD's quick-style buttons fire.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mapStyle, setMapStyle] = useState<string>('mapbox://styles/mapbox/light-v11');

  const teleport = useAgentTeleportMode({
    map:      map.current,
    agents:   agentMarkerSnapshot,
    // Forward the wallet pk so the teleport hub can show "@G…XYZ
    // is hosting" on every viewer's HUD. Anonymous (null) is fine
    // — the hub just falls back to the server-assigned member id.
    walletPk: walletAddress || null,
    // Plumb the map-style state through so the teleport HUD can
    // surface a Dark / Satellite / Original chip cluster. The
    // hook calls `map.setStyle(url)` itself; the callback below
    // keeps THIS component's mapStyle in sync so the
    // SettingsOverlay dropdown and any other style-aware
    // consumers reflect what teleport just did.
    mapStyle:         mapStyle,
    onMapStyleChange: setMapStyle,
  });

  /**
   * Mirror of `teleport.enabled` into a ref so the legacy
   * camera-mover code paths can early-return without us having to
   * thread the teleport state through every effect's dep array.
   *
   * Cinematic teleport mode owns the camera while it's active —
   * any other code that would call `easeTo` / `flyTo` (live-tracking
   * GPS follow, the globe auto-spin, etc.) MUST bail out, otherwise
   * the next animation frame after our `flyTo` will visibly
   * teleport the camera back where they want it (the bug that left
   * the user "stuck on Entering" with no visible zoom-in).
   */
  const teleportActiveRef = useRef(false);
  useEffect(() => {
    teleportActiveRef.current = teleport.enabled;
  }, [teleport.enabled]);

  const handleTrustGroupMemberFilterChange = useCallback((memberAddresses: string[]) => {
    const next = memberAddresses.map((a) => stellarAddrKey(a)).filter(Boolean);
    setTrustGroupMapAllowlist(next);
    if (next.length === 0) setApplyTrustGroupMapFilter(false);
  }, []);

  // "Show only this trust group" from a session panel: resolve the
  // group(s)' member wallets on-chain and drive the existing map filter
  // so the map narrows to exactly the people scoped into that session.
  const handleShowOnlyTrustGroups = useCallback(async (groupIds: number[]) => {
    if (!contractClient || !Array.isArray(groupIds) || groupIds.length === 0) return;
    try {
      const addrs = new Set<string>();
      await Promise.all(groupIds.map(async (id) => {
        try {
          const g = await contractClient.getTrustGroup(id);
          const owner = (g && (g.owner ?? g.admin)) as string | undefined;
          if (owner) addrs.add(String(owner));
          if (g && Array.isArray(g.members)) g.members.forEach((m: any) => addrs.add(String(m)));
        } catch { /* skip unreadable group */ }
      }));
      if (addrs.size === 0) return;
      handleTrustGroupMemberFilterChange(Array.from(addrs));
      setApplyTrustGroupMapFilter(true);
      setActiveOverlay(null);
    } catch { /* leave filter unchanged on failure */ }
  }, [contractClient, handleTrustGroupMemberFilterChange]);

  // Navigation and overlay states
  const [activeOverlay, setActiveOverlay] = useState<string | null>(null);
  // Hand-off slot for the country tooltip → spatial admin panel flow:
  // setting these params auto-opens the panel, applies the params,
  // and clears itself once consumed.
  const [spatialAdminOpenParams, setSpatialAdminOpenParams] = useState<SpatialAdminOpenParams | null>(null);
  // 'floating' (draggable top-right card, used by country tooltip) vs
  // 'centered' (modal-style sheet, used when opened from the hamburger
  // menu). Default is floating so the country-scoped flow keeps its
  // current behaviour without callers having to opt in.
  const [spatialAdminOpenMode, setSpatialAdminOpenMode] = useState<'floating' | 'centered'>('floating');
  const openSpatialAdminWith = useCallback((params: SpatialAdminOpenParams) => {
    setSpatialAdminOpenParams(params);
    setSpatialAdminOpenMode('floating');
    setActiveOverlay('spatial-objects');
  }, []);

  // ── Compliance (watchlist / deny-list) ─────────────────────────────────────
  // Server-evaluated post-login decision.  Persistent banner for BLOCKED /
  // WARN; null for CLEAR.  Dismissal is advisory-only (WARN can be
  // acknowledged; BLOCKED is a hard stop and forces disconnect).
  const [complianceEvaluation, setComplianceEvaluation] = useState<WatchlistEvaluation | null>(null);
  const [complianceDismissed, setComplianceDismissed] = useState(false);
  // Countries this wallet may administer in the compliance UI.  Populated
  // dynamically as the user hovers/clicks on countries — the country-admin
  // check in CountryManagementOverlay wiring already resolves admin status
  // per country, and we accumulate that here so the overlay can scope the
  // dropdown.  A main admin gets the full ISO-3166 range via `canEditGlobal`.
  const [adminCountryScope, setAdminCountryScope] = useState<Set<number>>(new Set());
  
  // Confirmation and notification overlay states
  const [confirmationState, setConfirmationState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    type?: 'danger' | 'warning' | 'info';
    onConfirm: () => void;
  } | null>(null);
  
  const [notificationState, setNotificationState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type?: 'success' | 'error' | 'info' | 'warning';
    autoClose?: number;
  } | null>(null);
  
  // Country overlay state
  const [showCountryManagementOverlay, setShowCountryManagementOverlay] = useState(false);
  const [showCountryProfileOverlay, setShowCountryProfileOverlay] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<{ code: number; name: string } | null>(null);
  // Per-wallet ProfileOverlay target: null = closed, otherwise the address being viewed
  // (my own when equal to walletAddress, someone else's otherwise).
  // `initialProfileWallet` (from the /geoai/:publicKey deep-link) is
  // used as the initial value so first paint already has the overlay
  // mounted with the correct wallet — avoids a flash of the map.
  const [profileOverlayFor, setProfileOverlayFor] = useState<string | null>(initialProfileWallet ?? null);
  // Tab the ProfileOverlay should open on.  Defaults to 'profile'; the
  // wallet/game panel quick-peek icon chips (👤 / 🎥 / 💎) set this
  // explicitly so a single tap deep-links straight into Live or
  // Trustlines without the user having to re-tap inside the overlay.
  const [profileOverlayTab, setProfileOverlayTab] = useState<'profile' | 'live' | 'trustlines'>('profile');
  // Mirrors whether the ProfileOverlay currently has a live session
  // running (own broadcast or watching someone else).  While true we
  // won't yank the overlay to a different wallet on marker clicks —
  // the minimised pill keeps the stream alive while the user browses.
  const [liveSessionActive, setLiveSessionActive] = useState(false);
  // The connected user's own trust groups (id+name), used to populate
  // the "Go Live → specific trust group(s)" selector in ProfileOverlay.
  // Loaded lazily when the owner opens their own profile overlay.
  const [myTrustGroups, setMyTrustGroups] = useState<{ id: number; name: string }[]>([]);
  useEffect(() => {
    const isOwnProfile = !!walletAddress && profileOverlayFor === walletAddress;
    if (!isOwnProfile || !contractClient) { setMyTrustGroups([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const ids = await contractClient.listMyTrustGroups();
        const groups = await Promise.all((ids || []).map(async (id) => {
          try {
            const g = await contractClient.getTrustGroup(id);
            return { id, name: (g && (g.name as string)) || `Group #${id}` };
          } catch {
            return { id, name: `Group #${id}` };
          }
        }));
        if (!cancelled) setMyTrustGroups(groups);
      } catch {
        if (!cancelled) setMyTrustGroups([]);
      }
    })();
    return () => { cancelled = true; };
  }, [walletAddress, profileOverlayFor, contractClient]);
  // Incremented every time admin saves border changes; propagated to dashboard/other
  // maps so they re-fetch countries.geojson and redraw the updated polygon.
  const [borderRevision, setBorderRevision] = useState(0);
  
  // Country hover tooltip state
  const [hoveredCountry, setHoveredCountry] = useState<{
    countryCode: number;
    countryName: string;
    iso2: string | null;
    position: { x: number; y: number };
  } | null>(null);
  const [vaultBalance, setVaultBalance] = useState<number | null>(null);
  const [smartAccountVaultBalance, setSmartAccountVaultBalance] = useState<number | null>(null);
  const [totalVaultBalance, setTotalVaultBalance] = useState<number | null>(null);
  const [isLoadingVaultBalance, setIsLoadingVaultBalance] = useState(false);
  const [hoveredVaultInfo, setHoveredVaultInfo] = useState<{ registered: boolean; enabled: boolean } | null>(null);
  
  // Track which country we're currently fetching vault info for (to prevent race conditions)
  const currentFetchingCountryRef = useRef<string | null>(null);
  // Track current hovered country code for reliable border detection
  const currentHoveredCountryCodeRef = useRef<number | null>(null);
  
  // Force tooltip re-render when country changes (using state so it triggers re-render)
  const [tooltipRenderKey, setTooltipRenderKey] = useState<number>(0);
  
  // Track last country enter to prevent duplicate calls from mousemove
  const lastCountryEnterRef = useRef<{ countryCode: number; timestamp: number } | null>(null);

  // Tooltip interactivity – prevent hiding tooltip while user hovers it
  const tooltipHoveredRef = useRef(false);
  const countryLeaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const tooltipLockedPosRef = useRef<{ x: number; y: number } | null>(null);

  // Tooltip mini-search state
  const [tooltipSearchVal, setTooltipSearchVal] = useState('');
  const [tooltipSearchResults, setTooltipSearchResults] = useState<any[]>([]);
  const [tooltipSearchLoading, setTooltipSearchLoading] = useState(false);
  const tooltipSearchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mobile tooltip: track which country is currently shown in the tooltip
  // so the second tap on the same country opens the profile instead of re-showing tooltip
  const mobileTooltipCountryRef = useRef<number | null>(null);
  // Prevent the generic map click handler from dismissing tooltip
  // on the same tap that opened it.
  const suppressNextEmptyMapClickRef = useRef(false);
  // Viewport-based mobile detection to avoid false positives on touch-enabled desktops
  const [isMobileViewport, setIsMobileViewport] = useState(window.innerWidth <= 768);
  // Ref always mirrors the state so map event-handler closures (which capture a
  // stale closure value) can still read the live viewport state.
  const isMobileViewportRef = useRef(window.innerWidth <= 768);
  useEffect(() => {
    isMobileViewportRef.current = isMobileViewport;
  }, [isMobileViewport]);
  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobileViewport(mobile);
      isMobileViewportRef.current = mobile;
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /**
   * The deliberate surface already open over the map, if any.
   *
   * A conversation, an event panel or an object inspector was opened on
   * purpose. On a phone there is no room for a country card beside one,
   * and raising one anyway is how a tap aimed at a building ended up
   * showing a country instead. Read through the Phase 2 coordinator
   * rather than a flag of its own, so the rule stays in one place.
   *
   * Mirrored into a ref because the map handlers are registered once
   * and would otherwise capture whatever was open at registration.
   */
  const openMajorSurface = useOpenMajorSurface();
  const openMajorSurfaceRef = useRef(openMajorSurface);
  useEffect(() => {
    openMajorSurfaceRef.current = openMajorSurface;
  }, [openMajorSurface]);

  // Auto-minimize wallet panel while mobile tooltip is visible.
  // Do not auto-restore; user can restore manually.
  useEffect(() => {
    if (!isMobileViewport) return;
    if (hoveredCountry && walletAddress && !overlayMinimized) {
      setOverlayMinimized(true);
    }
  }, [hoveredCountry, isMobileViewport, walletAddress, overlayMinimized]);
  
  // Session details overlay state
  const [showSessionDetailsOverlay, setShowSessionDetailsOverlay] = useState(false);
  const [sessionDetailsData, setSessionDetailsData] = useState<any>(null);
  const [showPasskeySetupModal, setShowPasskeySetupModal] = useState(false);
  const [passkeyRefreshTrigger, setPasskeyRefreshTrigger] = useState(0);
  const [countryNameCache, setCountryNameCache] = useState<Map<number, string>>(new Map());
  
  // Refs for NFT and contract markers (matching xyz-wallet pattern)
  const nftMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const nftCirclesRef = useRef<string[]>([]);
  // Contract pins are square DOM markers (matching the dashboard map),
  // resized on zoom via the zoomend handler.
  const contractMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const contractCirclesRef = useRef<string[]>([]);
  
  const userCirclesRef = useRef<string[]>([]);
  
  // Ref to track currently hovered marker's radius circle
  const hoveredRadiusCircleRef = useRef<{ sourceId: string; layerId: string } | null>(null);
  
  // Helper function to show radius circle on marker hover
  const showRadiusCircle = useCallback((
    location: [number, number],
    radiusMeters: number,
    // Optional dashboard-parity styling. Defaults keep the generic indigo solid
    // circle used for user/NFT/cell hovers; contract rule hovers pass the
    // rule-type colour + dashed [3,2] so both maps' rule circles look identical.
    opts?: { color?: string; dashed?: boolean; fillOpacity?: number; lineOpacity?: number },
  ) => {
    if (!map.current || !map.current.isStyleLoaded()) {
      // console.warn('[App] showRadiusCircle: Map not ready', { mapReady: !!map.current, styleLoaded: map.current?.isStyleLoaded() });
      return;
    }
    const circleColor   = opts?.color ?? '#6366f1';
    const circleFillOp  = opts?.fillOpacity ?? 0.1;
    const circleLineOp  = opts?.lineOpacity ?? 0.6;
    
    // Ensure location coordinates are numbers
    const lng = typeof location[0] === 'number' ? location[0] : parseFloat(String(location[0]));
    const lat = typeof location[1] === 'number' ? location[1] : parseFloat(String(location[1]));
    
    if (isNaN(lng) || isNaN(lat)) {
      // console.error('[App] showRadiusCircle: Invalid coordinates', { location, lng, lat });
      return;
    }
    
    // console.log('[App] showRadiusCircle called', { location: [lng, lat], radiusMeters });
    
    // Remove any existing hover circle (must remove layers before source)
    if (hoveredRadiusCircleRef.current) {
      const { sourceId, layerId } = hoveredRadiusCircleRef.current;
      const fillLayerId = `${layerId}-fill`;
      
      // Remove layers first
      if (map.current.getLayer(fillLayerId)) {
        map.current.removeLayer(fillLayerId);
      }
      if (map.current.getLayer(layerId)) {
        map.current.removeLayer(layerId);
      }
      // Then remove source
      if (map.current.getSource(sourceId)) {
        map.current.removeSource(sourceId);
      }
      hoveredRadiusCircleRef.current = null;
    }
    
    // Create new radius circle
    const sourceId = `hover-radius-${Date.now()}`;
    const layerId = `${sourceId}-layer`;
    
    // Build the circle as a true geodesic buffer via @turf/circle. The old
    // flat lat/lng offset treated one degree of longitude as ~111 km
    // everywhere, stretching catastrophically at high latitudes; Turf draws
    // a proper great-circle ring. We still clamp the display radius to keep
    // hover feedback sane (visual feedback, not cartographic truth).
    const MAX_DISPLAY_RADIUS_M = 150_000; // 150 km — beyond this the circle becomes visual noise
    const clampedRadius = Math.min(Math.max(radiusMeters, 100), MAX_DISPLAY_RADIUS_M);
    const circle = turfCircle([lng, lat], clampedRadius / 1000, {
      steps: 96, // smooth on retina without becoming expensive
      units: 'kilometers',
    }) as GeoJSON.Feature<GeoJSON.Polygon>;
    
    try {
      map.current.addSource(sourceId, { type: 'geojson', data: circle });
      
      // Add fill layer (semi-transparent)
      map.current.addLayer({
        id: `${layerId}-fill`,
        type: 'fill',
        source: sourceId,
        paint: {
          'fill-color': circleColor,
          'fill-opacity': circleFillOp
        }
      });
      
      // Add outline layer (dashed for rule hovers, solid otherwise)
      map.current.addLayer({
        id: layerId,
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': circleColor,
          'line-width': 2,
          'line-opacity': circleLineOp,
          ...(opts?.dashed ? { 'line-dasharray': [3, 2] as [number, number] } : {}),
        }
      });
      
      hoveredRadiusCircleRef.current = { sourceId, layerId };
      // console.log('[App] showRadiusCircle: Successfully added radius circle', { sourceId, layerId });
    } catch (error) {
      // console.error('[App] Error adding hover radius circle:', error);
    }
  }, []);
  
  // Helper function to hide radius circle
  const hideRadiusCircle = useCallback(() => {
    if (!map.current || !hoveredRadiusCircleRef.current) return;
    
    const { sourceId, layerId } = hoveredRadiusCircleRef.current;
    const fillLayerId = `${layerId}-fill`;
    
    try {
      // Remove layers first (in reverse order of creation)
      if (map.current.getLayer(fillLayerId)) {
        map.current.removeLayer(fillLayerId);
      }
      if (map.current.getLayer(layerId)) {
        map.current.removeLayer(layerId);
      }
      // Then remove source
      if (map.current.getSource(sourceId)) {
        map.current.removeSource(sourceId);
      }
      hoveredRadiusCircleRef.current = null;
    } catch (error) {
      // console.error('[App] Error removing hover radius circle:', error);
    }
  }, []);
  
  // Debounce refs for marker updates
  const markerUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastMarkerUpdateRef = useRef<number>(0);
  
  // Track previous marker data to prevent unnecessary updates
  const previousUserMarkersRef = useRef<string>('');
  const previousContractMarkersRef = useRef<string>('');
  const globeSpinningRef     = useRef(false);
  const globeIdleTimerRef    = useRef<NodeJS.Timeout | null>(null);
  const globeSpinIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  
  // Map settings state — `mapStyle` / `setMapStyle` are declared
  // higher up (right above the teleport hook) so the cinematic can
  // see them; the rest of the map settings live here.
  const [mapFilters, setMapFilters] = useState({
    showUsers: true,
    showNFTs: true,
    showContracts: true,
  });
  
  // 3D controls state
  const [enable3D, setEnable3D] = useState(true);
  const [pitch] = useState(0); // Read-only: SettingsOverlay manages pitch directly on map
  const [bearing] = useState(0); // Read-only: SettingsOverlay manages bearing directly on map
  const [showBuildings, setShowBuildings] = useState(false);
  // GeoLibre-inspired scene controls (applied by <MapSceneController>).
  // These persist across reloads (see the persistence effect below) so the
  // globe/terrain/atmosphere choice doesn't reset — and, crucially, so
  // MapSceneController doesn't flatten the globe back to mercator on refresh.
  const readSceneFlag = (key: string, dflt: boolean): boolean => {
    try {
      const v = localStorage.getItem(key);
      return v === null ? dflt : v === '1';
    } catch { return dflt; }
  };
  const [showTerrain, setShowTerrain] = useState<boolean>(() => readSceneFlag('geotrust:show-terrain', false));
  const [showGlobe, setShowGlobe] = useState<boolean>(() => readSceneFlag('geotrust:show-globe', true));
  const [showAtmosphere, setShowAtmosphere] = useState<boolean>(() => readSceneFlag('geotrust:show-atmosphere', false));
  const [terrainExaggeration, setTerrainExaggeration] = useState<number>(() => {
    try {
      const v = parseFloat(localStorage.getItem('geotrust:terrain-exaggeration') || '');
      return Number.isFinite(v) && v > 0 ? v : 1.5;
    } catch { return 1.5; }
  });

  // Persist the scene toggles so the user's globe/terrain/atmosphere choice
  // survives a page reload (matches the `geotrust:auto-3d-buildings` pattern).
  useEffect(() => {
    try {
      localStorage.setItem('geotrust:show-globe', showGlobe ? '1' : '0');
      localStorage.setItem('geotrust:show-atmosphere', showAtmosphere ? '1' : '0');
      localStorage.setItem('geotrust:show-terrain', showTerrain ? '1' : '0');
      localStorage.setItem('geotrust:terrain-exaggeration', String(terrainExaggeration));
    } catch { /* ignore */ }
  }, [showGlobe, showAtmosphere, showTerrain, terrainExaggeration]);

  // Auto-3D-buildings: when on, the map automatically swaps in the
  // Mapbox 3D building extrusions whenever the user zooms in past
  // ZOOM_3D_THRESHOLD, and removes them when zooming back out. The
  // user can flip the toggle on/off; the choice persists across
  // reloads so power users who hate the perspective shift can keep it
  // off.
  const [auto3DBuildings, setAuto3DBuildings] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem('geotrust:auto-3d-buildings');
      return v === null ? true : v === '1';
    } catch { return true; }
  });
  // Mirror as a ref so the (long-lived) Mapbox zoom handler can read
  // the live value without being recreated every render.
  const auto3DBuildingsRef = useRef<boolean>(auto3DBuildings);

  /** Guard flag while the auto-fit easeTo is in flight — prevents the
   *  ensuing zoomend from re-running the fit (which would oscillate). */
  const autoFitInFlightRef = useRef<boolean>(false);
  /** Cooldown — once a fit has just run, suppress further fits for a
   *  few seconds so manual nudges aren't always overridden. */
  const autoFitCooldownUntilRef = useRef<number>(0);
  /** Last scheduled fit's setTimeout handle, so a follow-up zoom event
   *  can cancel it before tiles even rasterise. */
  const autoFitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    auto3DBuildingsRef.current = auto3DBuildings;
    try { localStorage.setItem('geotrust:auto-3d-buildings', auto3DBuildings ? '1' : '0'); } catch { /* ignore */ }

    // Apply the new toggle state immediately at the current zoom — so
    // turning auto-3D on at zoom 17 instantly shows buildings, and
    // turning it off instantly removes them.
    const m = map.current;
    if (!m) return;
    const z = m.getZoom();
    if (auto3DBuildings && z >= ZOOM_3D_ON) {
      add3DBuildings(m);
    } else if (!auto3DBuildings) {
      remove3DBuildings(m);
    }
  }, [auto3DBuildings]);
  
  // Apply filters to marker visibility
  useEffect(() => {
    if (!map.current) return;
    
    // Update user markers visibility
    document.querySelectorAll('.other-user-marker, .session-user-marker').forEach((marker) => {
      (marker as HTMLElement).style.display = mapFilters.showUsers ? 'flex' : 'none';
    });
    
    // Update NFT markers visibility
    nftMarkersRef.current.forEach((marker) => {
      const el = marker.getElement();
      if (el) {
        el.style.display = mapFilters.showNFTs ? 'flex' : 'none';
      }
    });
    
    // Update contract pin (square DOM marker) visibility.
    contractMarkersRef.current.forEach((marker) => {
      const el = marker.getElement();
      if (el) {
        el.style.display = mapFilters.showContracts ? 'flex' : 'none';
      }
    });
  }, [mapFilters, nftMarkersRef, contractMarkersRef]);

  // Stable ref so loadCountryOverlay (empty-deps useCallback) can call the latest updateCountryOverlay
  const updateCountryOverlayRef = useRef<() => void>(() => {});

  // Own reference to the GeoJSON data — avoids relying on Mapbox's private source._data
  const geojsonDataRef = useRef<any>(null);

  // Prefetch /api/countries as early as App mounts so borders can paint on
  // the first map `load` without waiting for a cold network round-trip.
  const countriesPrefetchRef = useRef<Promise<GeoJSON.FeatureCollection | null> | null>(null);
  if (!countriesPrefetchRef.current) {
    countriesPrefetchRef.current = fetch('/api/countries', {
      headers: { 'Cache-Control': 'no-cache' },
    })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
  }

  // Instantly re-attach green/red borders from the in-memory cache (no
  // network). Used after basemap style swaps — setStyle wipes custom layers
  // and waiting on a full /api/countries refetch is what made borders
  // "disappear for a minute". Mesh is cached by FeatureCollection identity,
  // so this stays cheap when geojsonDataRef hasn't changed.
  const reapplyCountryOverlayFromCache = useCallback(() => {
    const m = map.current;
    const geojson = geojsonDataRef.current;
    if (!m || !geojson) return;
    reapplyCountryOverlay(m, geojson);
  }, []);
  const reapplyCountryOverlayFromCacheRef = useRef(reapplyCountryOverlayFromCache);
  reapplyCountryOverlayFromCacheRef.current = reapplyCountryOverlayFromCache;

  // Stable ref to the country resolver so JSX callbacks can call it without
  // being inside the map effect closure.
  const resolveCountryRef = useRef<((lng: number, lat: number, nearest?: boolean) => { code: number; name: string; iso2: string | null } | null) | null>(null);

  // Stable wrapper handed to the spatial objects admin panel so it can
  // show a country column without any network calls. GeoLink's admin
  // rows omit `country_code`, so the panel resolves each centroid
  // against the countries GeoJSON this map already has loaded.
  const resolveSpatialRowCountry = useCallback(
    (lng: number, lat: number, nearest?: boolean) =>
      resolveCountryRef.current?.(lng, lat, nearest) ?? null,
    [],
  );

  // Stable ref to the vault info loader so flyToResult can trigger it after moveend.
  const loadVaultInfoRef = useRef<((iso2Upper: string) => Promise<void>) | null>(null);

  // Show basemap country (admin-0) borders under the green/red policy overlay;
  // keep province (admin-1) lines hidden.
  const syncBasemapCountryBorders = useCallback(() => {
    const m = map.current;
    if (!m) return;
    syncBasemapAdminBoundaries(m);
  }, []);
  const syncBasemapCountryBordersRef = useRef(syncBasemapCountryBorders);
  syncBasemapCountryBordersRef.current = syncBasemapCountryBorders;

  // Define updateCountryOverlay first (used by loadCountryOverlay)
  const updateCountryOverlay = useCallback(() => {
    if (!map.current || !map.current.getSource('countries')) {
      // console.log('[App] updateCountryOverlay: Map or countries source not ready');
      return;
    }

    const source = map.current.getSource('countries') as mapboxgl.GeoJSONSource;
    // Prefer our own ref over source._data (which is a private Mapbox internal and can be unreliable)
    const data = (geojsonDataRef.current || (source as any)._data) as GeoJSON.FeatureCollection;

    if (!data || !data.features) {
      // console.log('[App] updateCountryOverlay: No data or features');
      return;
    }

    // console.log('[App] updateCountryOverlay: Updating', data.features.length, 'features', {
    //   allowedCount: allowedCountries.size,
    //   defaultAllowAll,
    //   allowedCountriesArray: Array.from(allowedCountries).slice(0, 10) // First 10 for debugging
    // });

    data.features.forEach((feature) => {
      // Ensure properties object exists
      if (!feature.properties) {
        feature.properties = {};
      }
      
      // Try ISO_NUMERIC first, then convert from ISO2/ISO3
      let countryCode = feature.properties.ISO_NUMERIC;
      if (!countryCode) {
        // Check feature-level id (ISO3) and properties
        const iso3 = feature.id;
        const iso2 = feature.properties?.ISO2;
        
        if (iso2) {
          countryCode = iso2ToNumeric(iso2);
        } else if (iso3) {
          // Convert ISO3 to ISO2 first, then to numeric
          // Ensure iso3 is a string
          const iso3Str = typeof iso3 === 'string' ? iso3 : String(iso3);
          const iso2FromIso3 = iso3ToIso2(iso3Str);
          if (iso2FromIso3) {
            countryCode = iso2ToNumeric(iso2FromIso3);
          }
        }
        
        // Store it for future use
        if (countryCode) {
          feature.properties.ISO_NUMERIC = countryCode;
        }
      }
      
      // Match the dashboard map's policy coloring (DashboardCountryVaults.isAllowed):
      //   - no policy data loaded yet (empty set) → `allowed` stays undefined → grey
      //   - unresolvable country code → undefined → grey
      //   - otherwise boolean → green (#00FF00) / red (#FF0000)
      // Previously this forced EVERY country to a boolean via `defaultAllowAll`,
      // which painted the whole world green (or red) and looked nothing like
      // the dashboard's grey-until-known borders.
      let allowed: boolean | undefined;

      if (countryCode && allowedCountries.size > 0) {
        // Convert to number if it's a string
        const code = typeof countryCode === 'string' ? parseInt(countryCode, 10) : countryCode;

        // Logic:
        // - If defaultAllowAll is true: country is allowed UNLESS it's in the denied list (allowedCountries acts as denylist)
        // - If defaultAllowAll is false: country is allowed ONLY if it's in the allowed list
        if (defaultAllowAll) {
          // Default allow all: allowedCountries is actually a denylist
          allowed = !allowedCountries.has(code);
        } else {
          // Default deny all: allowedCountries is an allowlist
          allowed = allowedCountries.has(code);
        }
      }

      if (allowed === undefined) {
        // Grey "unknown" border — the outline paint checks `typeof allowed
        // === 'boolean'`, so the property must be absent (not false).
        delete feature.properties.allowed;
      } else {
        feature.properties.allowed = allowed;
      }
      
      // Count updates for debugging (commented out - was only used in console.log)
      // if (wasAllowedBool !== feature.properties.allowed) {
      //   _updatedCount++;
      // }
      // 
      // // Count allowed vs denied
      // if (feature.properties.allowed) {
      //   _allowedCount++;
      // } else {
      //   _deniedCount++;
      // }
    });

    // console.log('[App] updateCountryOverlay: Updated', updatedCount, 'features', {
    //   allowed: allowedCount,
    //   denied: deniedCount,
    //   total: data.features.length
    // });
    
    // Update the source with modified data — pass a shallow copy so Mapbox detects the change.
    // Keep geojsonDataRef on this same object so style-swap reapply hits the
    // TopoJSON mesh cache (same FC reference → no expensive rebuild).
    const next = { ...data };
    geojsonDataRef.current = next;
    source.setData(next);
    // Unique-edge outline only (no polygon stroke / fill-outline doubles).
    invalidateCountryBorderCache();
    syncCountryBorderSource(map.current, next);
    ensureCountryOverlayLayers(map.current);
    promoteCountryOverlayLayers(map.current);
  }, [defaultAllowAll, allowedCountries]);

  // Keep the ref in sync so loadCountryOverlay (which has [] deps) always calls the latest version
  useEffect(() => {
    updateCountryOverlayRef.current = updateCountryOverlay;
  }, [updateCountryOverlay]);

  // Stop live tracking on unmount so the watchPosition doesn't leak
  useEffect(() => {
    return () => {
      if (watchPositionIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchPositionIdRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Call updateCountryOverlay when defaultAllowAll or allowedCountries changes
  // Only update if country data has been loaded AND overlay source exists to prevent race conditions
  useEffect(() => {
    // Don't update if data hasn't been loaded yet OR if overlay source doesn't exist
    if (!countryDataLoaded) {
      // console.log('[App] Country data not loaded yet, skipping overlay update');
      return;
    }
    
    // Don't update if overlay source doesn't exist yet
    if (!map.current || !map.current.getSource('countries')) {
      // console.log('[App] Countries source not ready yet, skipping overlay update');
      return;
    }
    
    // Use a delay to ensure state has fully updated and propagated
    const timer = setTimeout(() => {
      if (map.current && map.current.loaded() && map.current.isStyleLoaded()) {
        const source = map.current.getSource('countries');
        if (source) {
          // Get the latest state values directly (not from closure) to ensure we have the updated values
          // console.log('[App] defaultAllowAll or allowedCountries changed, updating overlay', { 
          //   defaultAllowAll, 
          //   allowedCount: allowedCountries.size,
          //   allowedCountriesArray: Array.from(allowedCountries).slice(0, 10), // First 10 for debugging
          //   countryDataLoaded,
          //   note: 'Using latest state values from closure'
          // });
          // updateCountryOverlay is a useCallback, so it will use the latest defaultAllowAll and allowedCountries
          updateCountryOverlay();
        } else {
          // console.warn('[App] Countries source not ready yet for overlay update');
        }
      } else {
        // console.warn('[App] Map not ready yet for overlay update', {
        //   loaded: map.current?.loaded(),
        //   styleLoaded: map.current?.isStyleLoaded()
        // });
      }
    }, 50); // Small delay to let Mapbox finish its current render tick
    return () => clearTimeout(timer);
  }, [defaultAllowAll, allowedCountries, updateCountryOverlay, countryDataLoaded]);

  // Also update overlay when countryDataLoaded becomes true (for initial load)
  // But only if defaultAllowAll and allowedCountries have been set (not at initial values)
  useEffect(() => {
    if (countryDataLoaded && map.current && map.current.getSource('countries')) {
      // Check if we have actual data (not just initial values)
      // If defaultAllowAll is false and allowedCountries is empty, we might still be loading
      // Wait a bit more to ensure state has propagated
      const timer = setTimeout(() => {
        if (map.current && map.current.getSource('countries')) {
          // console.log('[App] Country data just loaded, updating overlay', {
          //   defaultAllowAll,
          //   allowedCount: allowedCountries.size,
          //   countryDataLoaded
          // });
          updateCountryOverlay();
        }
      }, 50); // Small delay to let Mapbox finish its current render tick
      return () => clearTimeout(timer);
    }
  }, [countryDataLoaded, updateCountryOverlay, defaultAllowAll, allowedCountries]);

  // Basemap style picker (Settings / tooltip) calls setStyle. Diff updates may
  // never emit `style.load`, so also re-attach green/red borders whenever
  // `mapStyle` changes and the map goes idle.
  const prevMapStyleForBordersRef = useRef<string | null>(null);
  useEffect(() => {
    const m = map.current;
    if (!m || !geojsonDataRef.current) return;
    if (prevMapStyleForBordersRef.current === null) {
      prevMapStyleForBordersRef.current = mapStyle;
      return;
    }
    if (prevMapStyleForBordersRef.current === mapStyle) return;
    prevMapStyleForBordersRef.current = mapStyle;

    const reattach = () => reapplyCountryOverlayFromCacheRef.current();
    reattach();
    let raf = 0;
    raf = requestAnimationFrame(reattach);
    const onIdle = () => reattach();
    try { m.once('idle', onIdle); } catch { /* ignore */ }
    return () => {
      cancelAnimationFrame(raf);
      try { m.off('idle', onIdle); } catch { /* ignore */ }
    };
  }, [mapStyle]);

  // Define loadCountryOverlay (uses updateCountryOverlay).
  // Pass `{ force: true }` after admin border edits so we refetch geometry
  // instead of reusing the in-memory cache.
  const loadCountryOverlay = useCallback(async (opts?: { force?: boolean }) => {
    if (!map.current) {
      // console.warn('[App] Map not available for country overlay');
      return;
    }

    // Style swap / remount: if we already have GeoJSON in memory, re-attach
    // layers immediately — skip the network + TopoJSON rebuild. Callers that
    // need fresh geometry (Manage Country edits) pass `{ force: true }`.
    if (!opts?.force && geojsonDataRef.current?.features?.length) {
      reapplyCountryOverlayFromCacheRef.current();
      return;
    }

    try {
      // Prefer the mount-time prefetch; fall back to a fresh fetch.
      let geojson: any = null;
      try {
        if (!opts?.force && countriesPrefetchRef.current) {
          geojson = await countriesPrefetchRef.current;
          // One-shot: subsequent loads refetch so admin border edits land.
          countriesPrefetchRef.current = null;
        }
        if (!geojson) {
          const response = await fetch('/api/countries', {
            headers: { 'Cache-Control': 'no-cache' },
          });
          if (response.ok) {
            geojson = await response.json();
          }
        }
        if (!geojson) {
          // console.warn('[App] countries.geojson not found, skipping country overlay');
          return;
        }
      } catch (error) {
        // console.warn('[App] Failed to load countries.geojson, skipping overlay:', error);
        return;
      }
      invalidateCountryBorderCache();

      // Initialize allowed property for all features
      if (geojson.features) {
        geojson.features.forEach((feature: any) => {
          if (!feature.properties) {
            feature.properties = {};
          }
          // Set default allowed value (will be updated by updateCountryOverlay)
          // Try ISO_NUMERIC first, then convert from ISO2/ISO3
          let countryCode = feature.properties.ISO_NUMERIC;
          if (!countryCode) {
            // Check feature-level id (ISO3) and properties
            const iso3 = feature.id;
            const iso2 = feature.properties?.ISO2;
            
            if (iso2) {
              countryCode = iso2ToNumeric(iso2);
            } else if (iso3) {
              // Convert ISO3 to ISO2 first, then to numeric
              // Ensure iso3 is a string
              const iso3Str = typeof iso3 === 'string' ? iso3 : String(iso3);
              const iso2FromIso3 = iso3ToIso2(iso3Str);
              if (iso2FromIso3) {
                countryCode = iso2ToNumeric(iso2FromIso3);
              }
            }
            
            // Store it for future use
            if (countryCode) {
              feature.properties.ISO_NUMERIC = countryCode;
            }
          }
          
          // Don't set allowed here - it will be set by updateCountryOverlay after data is loaded
          // This prevents race conditions where overlay is initialized before policy data is loaded
          // Set to undefined initially, updateCountryOverlay will set it properly
          feature.properties.allowed = undefined;
        });
      }

      // Count features that successfully resolve to a numeric country code —
      // used to keep the legend/admin counts consistent without a hardcoded number.
      if (geojson.features) {
        const mappable = geojson.features.filter((f: any) => f.properties?.ISO_NUMERIC).length;
        if (mappable > 0) setTotalCountries(mappable);
      }

      // Always keep our own reference — independent of Mapbox internals
      geojsonDataRef.current = geojson;

      if (map.current.getSource('countries')) {
        (map.current.getSource('countries') as mapboxgl.GeoJSONSource).setData(geojson);
      } else {
        map.current.addSource('countries', {
          type: 'geojson',
          data: geojson,
        });
      }

      syncCountryBorderSource(map.current, geojson);
      ensureCountryOverlayLayers(map.current);
      syncBasemapCountryBordersRef.current();

      // Fire updateCountryOverlay once the source is loaded AND country data is available.
      // If data isn't ready yet, skip here — the countryDataLoaded useEffect will handle it.
      const handleSourceReady = (e: any) => {
        if (e.sourceId === 'countries' && e.isSourceLoaded) {
          map.current?.off('sourcedata', handleSourceReady);
          if (countryDataLoadedRef.current) {
            updateCountryOverlayRef.current();
          }
          if (map.current) promoteCountryOverlayLayers(map.current);
          // If data not loaded yet, the countryDataLoaded useEffect will paint when ready
        }
      };
      map.current.on('sourcedata', handleSourceReady);

      // Fallback: also run after a short delay in case the sourcedata event already fired.
      // Only paint if data is ready; otherwise the countryDataLoaded useEffect handles it.
      setTimeout(() => {
        map.current?.off('sourcedata', handleSourceReady);
        if (countryDataLoadedRef.current) {
          updateCountryOverlayRef.current();
        }
        if (map.current) {
          ensureCountryOverlayLayers(map.current);
          promoteCountryOverlayLayers(map.current);
          syncBasemapCountryBordersRef.current();
        }
      }, 200);
      // Deck mounts async after map load — keep outline above interleaved layers.
      setTimeout(() => {
        if (map.current) {
          ensureCountryOverlayLayers(map.current);
          promoteCountryOverlayLayers(map.current);
        }
      }, 800);
    } catch (error) {
      // console.error('Failed to load countries GeoJSON:', error);
    }
  }, []); // No dependencies - this function only loads the GeoJSON, doesn't use state

  // Define initializeMap before the useEffect that uses it
  const initializeMap = useCallback((container: HTMLDivElement) => {
    // console.log('[App] initializeMap called with container:', container);

    // Check if Mapbox token is available
    const token = process.env.REACT_APP_MAPBOX_TOKEN;
    if (!token) {
      // console.error('[App] Mapbox token not found! Check .env.local file');
      return;
    }
    
    // console.log('[App] Mapbox token found, proceeding with map initialization');

    // Ensure map container is visible and properly sized
    // Double-check container exists and has style before accessing
    if (!container || !container.style) {
      // console.warn('[App] Container or style not available when setting styles');
      return;
    }
    
    container.style.display = 'block';
    container.style.width = '100%';
    container.style.height = '100%';
    container.style.position = 'absolute';
    container.style.top = '0';
    container.style.left = '0';
    // console.log('[App] Map container ready:', {
    //   width: container.offsetWidth,
    //   height: container.offsetHeight,
    //   display: window.getComputedStyle(container).display
    // });

    try {
      if (!container) {
        // console.error('[App] Cannot create map: container is null');
        return;
      }
      
      // Ensure mapStyle has the full URL format
      const initialStyle = mapStyle && mapStyle.startsWith('mapbox://') 
        ? mapStyle 
        : `mapbox://styles/mapbox/${mapStyle || 'light-v11'}`;
      
      map.current = new mapboxgl.Map({
        container: container,
        style: initialStyle,
        center: [0, 0],
        zoom: 2,
        pitch: pitch,
        bearing: bearing,
        attributionControl: false,
        // MSAA on the WebGL context. Without it, the interleaved deck.gl
        // MapboxOverlay renders thin basemap lines (admin-0 borders, geofences)
        // un-antialiased. deck.gl also requires this for interleaved layers.
        antialias: true,
        // Required so the teleport auto-broadcast can draw the live map
        // canvas into the agent's broadcast stream (and recording) via
        // ctx.drawImage. Without it, a WebGL canvas read on a later
        // frame yields a blank buffer. Modest GPU-memory cost.
        preserveDrawingBuffer: true,
      });
      
      // Update state to ensure it has the full URL format
      if (!mapStyle.startsWith('mapbox://')) {
        setMapStyle(initialStyle);
      }

      const handleMapLoad = () => {
        // console.log('[App] Map loaded successfully');
        if (map.current) {
          // Set initial zoom state for logo visibility
          setMapZoom(map.current.getZoom());

          syncBasemapCountryBordersRef.current();

          loadCountryOverlay().then(() => {
            // console.log('[App] Country overlay loaded - will be updated when country data is loaded');
            // Don't update overlay here - let the useEffect that watches countryDataLoaded handle it
            // This ensures we don't update with stale/empty data
            // The useEffect at line 427 will call updateCountryOverlay when countryDataLoaded becomes true
          });
          // NFT markers will be rendered automatically via useEffect when nearbyNFTs changes
          // Admin check will happen automatically via useEffect when wallet and client are ready
        }
      };
      map.current.on('load', handleMapLoad);

      // ── Globe auto-spin ────────────────────────────────────────────────────
      // After 30 s of inactivity the globe rotates very slowly (one revolution
      // every ~5 minutes).  Any map interaction stops the spin and restarts the
      // 30-second idle countdown.
      //
      // Implementation uses setInterval (not moveend-chain) so the spin is
      // self-healing: if a tick is skipped (map still loading, easeTo error, etc.)
      // the next tick picks up automatically.
      const IDLE_SPIN_DELAY_MS   = 30_000; // 30 seconds idle before spin starts
      const SPIN_SECONDS_PER_REV = 300;    // 5 min / revolution → very slow
      const SPIN_MAX_ZOOM        = 4;      // don't spin when zoomed in past this
      const SPIN_TICK_MS         = 1000;   // advance globe by 1 deg/s

      const spinStep = () => {
        if (!globeSpinningRef.current || !map.current) return;
        if (teleportActiveRef.current) return;   // teleport owns the camera
        // Skip this tick if the map/style isn't ready (next tick will retry automatically)
        if (!map.current.isStyleLoaded() || !map.current.loaded()) return;
        if (map.current.getZoom() > SPIN_MAX_ZOOM) {
          // User zoomed in — stop the interval until next idle cycle
          stopSpin();   // sets globeSpinningRef=false, clears interval
          scheduleIdleSpin();
          return;
        }
        const degPerSec = 360 / SPIN_SECONDS_PER_REV;
        const center = map.current.getCenter();
        center.lng -= degPerSec;
        try {
          // duration slightly longer than tick so animation is smooth between ticks
          map.current.easeTo({ center, duration: SPIN_TICK_MS * 1.1, easing: (n: number) => n });
        } catch { /* skip this tick — interval will retry next second */ }
      };

      const stopSpin = () => {
        globeSpinningRef.current = false;
        if (globeSpinIntervalRef.current) {
          clearInterval(globeSpinIntervalRef.current);
          globeSpinIntervalRef.current = null;
        }
        map.current?.stop();
      };

      const startSpin = () => {
        if (!map.current) return;
        // Don't start if already zoomed in — a future idle cycle will check again
        if (map.current.getZoom() > SPIN_MAX_ZOOM) { scheduleIdleSpin(); return; }
        // Dismiss any open country tooltip (user went idle, clean up the UI)
        if (countryLeaveTimerRef.current) { clearTimeout(countryLeaveTimerRef.current); countryLeaveTimerRef.current = null; }
        tooltipHoveredRef.current = false;
        setHoveredCountry(null);
        globeSpinningRef.current = true;
        if (globeSpinIntervalRef.current) return; // already running
        spinStep(); // fire first step immediately
        globeSpinIntervalRef.current = setInterval(spinStep, SPIN_TICK_MS);
      };

      const scheduleIdleSpin = () => {
        if (globeIdleTimerRef.current) clearTimeout(globeIdleTimerRef.current);
        globeIdleTimerRef.current = setTimeout(startSpin, IDLE_SPIN_DELAY_MS);
      };

      // Stop spin + restart idle countdown on direct map interactions
      (['mousedown', 'touchstart', 'wheel', 'dblclick'] as const).forEach(evt => {
        map.current?.on(evt, () => {
          stopSpin();
          scheduleIdleSpin();
        });
      });

      // Also reset idle timer on window-level activity (keyboard, UI button clicks)
      const onWindowActivity = () => {
        stopSpin();
        scheduleIdleSpin();
      };
      window.addEventListener('keydown',    onWindowActivity, { passive: true });
      window.addEventListener('pointerdown', onWindowActivity, { passive: true });

      // Kick off the first idle countdown
      scheduleIdleSpin();
      // ── End globe auto-spin ────────────────────────────────────────────────

      map.current.on('error', (e: any) => {
        // console.error('[App] Map error:', e);
        if (e.error && e.error.message) {
          // console.error('[App] Map error details:', e.error.message);
        }
        // If style loading fails, try to fallback to a working style
        if (e.error && e.error.message && e.error.message.includes('style')) {
          // console.warn('[App] Map style error detected, attempting fallback to streets-v12');
          if (map.current) {
            try {
              map.current.setStyle('mapbox://styles/mapbox/streets-v12');
              setMapStyle('mapbox://styles/mapbox/streets-v12');
            } catch (fallbackError) {
              // console.error('[App] Fallback style also failed:', fallbackError);
            }
          }
        }
      });

      // Track zoom level for logo visibility
      map.current.on('zoom', () => {
        if (map.current) {
          setMapZoom(map.current.getZoom());
        }
      });

      map.current.on('zoomend', () => {
        if (map.current) {
          const z = map.current.getZoom();
          setMapZoom(z);

          // Resize NFT and contract markers based on zoom level: smaller when
          // zoomed out, larger as the user zooms in.
          const size = z >= 14 ? 64 : z >= 10 ? 40 : 32;
          nftMarkersRef.current.forEach(m => {
            const el = m.getElement();
            el.style.width = `${size}px`;
            el.style.height = `${size}px`;
          });
          contractMarkersRef.current.forEach(m => {
            const el = m.getElement();
            el.style.width = `${size}px`;
            el.style.height = `${size}px`;
            el.style.fontSize = size >= 64 ? '32px' : size >= 40 ? '22px' : '16px';
          });

          // Auto-disable 3D when user manually scrolls out — UNLESS
          // teleport mode is driving the camera. Without this guard
          // the brief mid-arc dip below z=14 in a long-hop flyTo
          // queues a competing easeTo that cancels the teleport
          // animation, leaving the user stranded mid-air with the
          // HUD frozen on "Flying…" and no camera motion.
          if (z < ZOOM_3D_OFF && !teleportActiveRef.current) {
            remove3DBuildings(map.current);
            // Only reset pitch/bearing if not already flat (and no flyTo in progress)
            if (map.current.getPitch() > 0 && !map.current.isMoving()) {
              map.current.easeTo({ pitch: 0, bearing: 0, duration: 600 });
            }
          }
          // Mirror behaviour: auto-add the 3D extrusions whenever the
          // user has zoomed in deep enough (scroll wheel, pinch,
          // double-click, marker fly — any path), as long as the auto-
          // 3D toggle is on. flyToWithMode also adds them after its
          // animation, but the previous build relied solely on that
          // path which left manual-zoom users with a flat map.
          if (z >= ZOOM_3D_ON && auto3DBuildingsRef.current) {
            add3DBuildings(map.current);

            // Building-aware auto-fit: schedule a check ~450 ms after
            // the user lands. The composite/building tiles need a few
            // frames to rasterise — querying immediately returns no
            // features even when a tower is right under the camera.
            // If a tall building (>200 m) is detected and the current
            // zoom is significantly tighter than buildingAwareZoom()
            // would prefer, we ease out to fit it. This mirrors what
            // the teleport mode does on arrival, but with a softer
            // cooldown so we don't fight the operator's manual zooms.
            const teleportLive = teleportActiveRef.current;
            const inCooldown   = Date.now() < autoFitCooldownUntilRef.current;
            if (!autoFitInFlightRef.current && !teleportLive && !inCooldown) {
              if (autoFitTimerRef.current) clearTimeout(autoFitTimerRef.current);
              autoFitTimerRef.current = setTimeout(() => {
                autoFitTimerRef.current = null;
                const m = map.current;
                if (!m) return;
                if (autoFitInFlightRef.current) return;
                if (teleportActiveRef.current) return;
                if (!auto3DBuildingsRef.current) return;
                if (m.isMoving()) return; // user is moving the camera again — skip

                const currentZoom = m.getZoom();
                if (currentZoom < ZOOM_3D_ON) return;

                const maxH = maxRenderedBuildingHeightM(m);
                if (maxH <= 200) return; // ordinary buildings — no fit needed

                const targetZoom = buildingAwareZoom(maxH, currentZoom);
                // Only animate when the delta is meaningful — a 0.05
                // tweak is invisible and just costs another moveend.
                if (currentZoom - targetZoom < 0.3) return;

                autoFitInFlightRef.current = true;
                autoFitCooldownUntilRef.current = Date.now() + 5000;
                m.easeTo({ zoom: targetZoom, duration: 1000 });
                m.once('moveend', () => {
                  autoFitInFlightRef.current = false;
                });
              }, 450);
            }
          }
        }
      });

      // Track whether this is the very first style.load (initial map load).
      // On initial load the data is already being fetched by loadData(); we
      // must NOT run reloadCountryData() or we risk wiping allowedCountries
      // with an empty/stale RPC response and turning everything red.
      let styleLoadFiredOnce = false;

      // Re-attach green/red borders after every basemap style settle.
      // IMPORTANT: `setStyle` does NOT always fire `style.load` (diff updates
      // emit only `styledata`). Reapply from the in-memory cache immediately
      // — do NOT wait on /api/countries or the contract RPC (that was the
      // "borders gone for a minute" bug when switching dark/light/etc.).
      const reattachCountryBorders = () => {
        syncBasemapCountryBordersRef.current();
        reapplyCountryOverlayFromCacheRef.current();
        // Re-assert a few times as Mapbox finishes settling the new style
        // (same pattern as MapSceneController for globe/terrain).
        requestAnimationFrame(() => reapplyCountryOverlayFromCacheRef.current());
        try {
          map.current?.once('idle', () => reapplyCountryOverlayFromCacheRef.current());
        } catch { /* ignore */ }
      };

      map.current.on('style.load', () => {
        // Re-add 3D buildings if we were zoomed in when the style reloaded
        if (map.current && map.current.getZoom() >= 15) {
          add3DBuildings(map.current);
        }

        // Skip on the very first style.load — that's the initial map load.
        // The loadCountryOverlay() on map `load` already handles first paint.
        if (!styleLoadFiredOnce) {
          styleLoadFiredOnce = true;
          syncBasemapCountryBordersRef.current();
          return;
        }

        reattachCountryBorders();
      });
      
      // Add Navigation Control (zoom in/out) - position top-left, below search
      const navControl = new mapboxgl.NavigationControl({
        showCompass: true,
        showZoom: true,
        visualizePitch: true
      });
      map.current.addControl(navControl, 'top-left');
      
      // Add Geolocate Control (my location) - position top-left, below navigation control
      const geolocateControl = new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true
        },
        trackUserLocation: true,
        showUserHeading: true
      });
      map.current.addControl(geolocateControl, 'top-left');

      // Scale bar (GeoLibre-style) — bottom-left, metric.
      const scaleControl = new mapboxgl.ScaleControl({ maxWidth: 120, unit: 'metric' });
      map.current.addControl(scaleControl, 'bottom-left');
      
      // Add country click handler
      const getCountryCodeFromFeature = (feature: mapboxgl.MapboxGeoJSONFeature): number | null => {
        const directCode = feature.properties?.ISO_NUMERIC;
        if (directCode) return Number(directCode);
        const iso2 = feature.properties?.ISO2 || feature.properties?.ISO_A2;
        if (iso2) {
          const code = iso2ToNumeric(String(iso2));
          if (code) return code;
        }
        if (feature.id) {
          const iso3 = typeof feature.id === 'string' ? feature.id : String(feature.id);
          const iso2FromIso3 = iso3ToIso2(iso3);
          if (iso2FromIso3) {
            const code = iso2ToNumeric(iso2FromIso3);
            if (code) return code;
          }
        }
        return null;
      };

      const getCountryFeaturesAtPoint = (
        point: { x: number; y: number },
        tolerancePx = 0,
        layers: string[] = ['countries-fill']
      ) => {
        if (!map.current) return [] as mapboxgl.MapboxGeoJSONFeature[];
        const existingLayers = layers.filter((id) => !!map.current?.getLayer(id));
        if (existingLayers.length === 0) return [] as mapboxgl.MapboxGeoJSONFeature[];
        if (tolerancePx <= 0) {
          return map.current.queryRenderedFeatures(point as any, { layers: existingLayers });
        }
        const bbox: [[number, number], [number, number]] = [
          [point.x - tolerancePx, point.y - tolerancePx],
          [point.x + tolerancePx, point.y + tolerancePx],
        ];
        return map.current.queryRenderedFeatures(bbox as any, { layers: existingLayers });
      };

      const getGeometryCenter = (feature: mapboxgl.MapboxGeoJSONFeature): [number, number] | null => {
        const geom: any = feature.geometry;
        if (!geom?.coordinates) return null;
        let minLng = Infinity;
        let maxLng = -Infinity;
        let minLat = Infinity;
        let maxLat = -Infinity;
        const walk = (coords: any): void => {
          if (!Array.isArray(coords)) return;
          if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
            const lng = coords[0];
            const lat = coords[1];
            if (lng < minLng) minLng = lng;
            if (lng > maxLng) maxLng = lng;
            if (lat < minLat) minLat = lat;
            if (lat > maxLat) maxLat = lat;
            return;
          }
          coords.forEach(walk);
        };
        walk(geom.coordinates);
        if (!isFinite(minLng) || !isFinite(maxLng) || !isFinite(minLat) || !isFinite(maxLat)) return null;
        return [(minLng + maxLng) / 2, (minLat + maxLat) / 2];
      };

      const pickBestCountryFeature = (
        features: mapboxgl.MapboxGeoJSONFeature[],
        point: { x: number; y: number }
      ): mapboxgl.MapboxGeoJSONFeature | null => {
        if (!map.current || features.length === 0) return null;
        // De-duplicate by country code first (fill+outline may both match)
        const byCode = new Map<number, mapboxgl.MapboxGeoJSONFeature>();
        for (const feature of features) {
          const code = getCountryCodeFromFeature(feature);
          if (!code) continue;
          if (!byCode.has(code)) byCode.set(code, feature);
        }
        const candidates = Array.from(byCode.values());
        if (candidates.length === 0) return null;
        if (candidates.length === 1) return candidates[0];

        // Pick candidate whose geometry center projects closest to tap point
        let best: mapboxgl.MapboxGeoJSONFeature | null = null;
        let bestDist = Number.POSITIVE_INFINITY;
        for (const candidate of candidates) {
          const center = getGeometryCenter(candidate);
          if (!center) continue;
          const p = map.current.project({ lng: center[0], lat: center[1] });
          const dx = p.x - point.x;
          const dy = p.y - point.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < bestDist) {
            bestDist = d2;
            best = candidate;
          }
        }
        return best || candidates[0];
      };

      const getCountryCodeForSampling = (feature: mapboxgl.MapboxGeoJSONFeature): number | null => {
        const info = extractCountryInfo(feature as any);
        if (info?.numeric) return Number(info.numeric);
        return getCountryCodeFromFeature(feature);
      };

      // Border-crossing style resolver for clicks:
      // sample several nearby points and choose the country with the strongest presence.
      const pickCountryFeatureBySampling = (
        point: { x: number; y: number },
        radiusPx: number
      ): mapboxgl.MapboxGeoJSONFeature | null => {
        if (!map.current) return null;

        const half = Math.max(4, Math.round(radiusPx / 2));
        const offsets: Array<[number, number]> = [
          [0, 0],
          [half, 0], [-half, 0], [0, half], [0, -half],
          [radiusPx, 0], [-radiusPx, 0], [0, radiusPx], [0, -radiusPx],
          [half, half], [half, -half], [-half, half], [-half, -half],
          [radiusPx, radiusPx], [radiusPx, -radiusPx], [-radiusPx, radiusPx], [-radiusPx, -radiusPx],
        ];

        type Candidate = { feature: mapboxgl.MapboxGeoJSONFeature; count: number; minD2: number };
        const byCode = new Map<number, Candidate>();

        for (const [dx, dy] of offsets) {
          const samplePoint = { x: point.x + dx, y: point.y + dy };
          const sampleFeatures = getCountryFeaturesAtPoint(samplePoint, 0, ['countries-fill']);
          if (sampleFeatures.length === 0) continue;
          const feature = sampleFeatures[0];
          const code = getCountryCodeForSampling(feature);
          if (!code) continue;
          const d2 = dx * dx + dy * dy;
          const prev = byCode.get(code);
          if (!prev) byCode.set(code, { feature, count: 1, minD2: d2 });
          else {
            prev.count += 1;
            if (d2 < prev.minD2) {
              prev.minD2 = d2;
              prev.feature = feature;
            }
          }
        }

        if (byCode.size === 0) return null;
        const ranked = Array.from(byCode.values()).sort((a, b) => {
          if (b.count !== a.count) return b.count - a.count;
          return a.minD2 - b.minD2;
        });
        return ranked[0].feature;
      };

      // GeoJSON fallback for mobile taps when rendered-feature hit-testing misses.
      const isPointInRing = (lng: number, lat: number, ring: number[][]): boolean => {
        let inside = false;
        for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
          const xi = ring[i][0], yi = ring[i][1];
          const xj = ring[j][0], yj = ring[j][1];
          const intersects = ((yi > lat) !== (yj > lat)) && (lng < ((xj - xi) * (lat - yi)) / ((yj - yi) || 1e-12) + xi);
          if (intersects) inside = !inside;
        }
        return inside;
      };

      const pointInFeature = (lng: number, lat: number, feature: any): boolean => {
        const geom = feature?.geometry;
        if (!geom?.coordinates) return false;
        if (geom.type === 'Polygon') {
          const rings: number[][][] = geom.coordinates;
          if (!rings?.length) return false;
          if (!isPointInRing(lng, lat, rings[0])) return false;
          for (let i = 1; i < rings.length; i++) {
            if (isPointInRing(lng, lat, rings[i])) return false; // inside hole
          }
          return true;
        }
        if (geom.type === 'MultiPolygon') {
          const polys: number[][][][] = geom.coordinates;
          for (const poly of polys) {
            if (!poly?.length) continue;
            if (!isPointInRing(lng, lat, poly[0])) continue;
            let inHole = false;
            for (let i = 1; i < poly.length; i++) {
              if (isPointInRing(lng, lat, poly[i])) {
                inHole = true;
                break;
              }
            }
            if (!inHole) return true;
          }
        }
        return false;
      };

      const extractCountryMeta = (feature: any): { code: number | null; name: string; iso2: string | null } => {
        const p = feature?.properties || {};
        const code = p.ISO_NUMERIC ? Number(p.ISO_NUMERIC) : (p.ISO2 ? iso2ToNumeric(String(p.ISO2)) : (p.ISO_A2 ? iso2ToNumeric(String(p.ISO_A2)) : null));
        const name = String(p.name || p.NAME || '');
        // Prefer explicit ISO2/ISO_A2 property; fall back to deriving from ISO3 feature id
        // (countries.geojson stores ISO3 as the feature id, not as a property).
        let iso2: string | null = p.ISO2 ? String(p.ISO2) : (p.ISO_A2 ? String(p.ISO_A2) : null);
        if (!iso2 && feature?.id) {
          const iso3 = typeof feature.id === 'string' ? feature.id : String(feature.id);
          iso2 = iso3ToIso2(iso3) || null;
        }
        return { code: code || null, name, iso2 };
      };

      const resolveCountryFromGeoJson = (
        lng: number,
        lat: number,
        useNearestFallback = true
      ): { code: number; name: string; iso2: string | null } | null => {
        const normalizeLng = (value: number) => {
          let n = value;
          while (n > 180) n -= 360;
          while (n < -180) n += 360;
          return n;
        };
        const lngNorm = normalizeLng(lng);
        const data = geojsonDataRef.current as any;
        const features = data?.features;
        if (!Array.isArray(features)) return null;

        // 1) exact point-in-polygon match
        for (const f of features) {
          // Try normalized lng and +/-360 variants for dateline/world-copy safety.
          if (!pointInFeature(lngNorm, lat, f) && !pointInFeature(lngNorm + 360, lat, f) && !pointInFeature(lngNorm - 360, lat, f)) continue;
          const meta = extractCountryMeta(f);
          if (meta.code) return { code: meta.code, name: meta.name || `Country ${meta.code}`, iso2: meta.iso2 };
        }

        if (!useNearestFallback) return null;

        // 2) nearest bbox-center fallback for edge taps on tiny countries
        let best: { code: number; name: string; iso2: string | null } | null = null;
        let bestDist = Number.POSITIVE_INFINITY;
        const zoom = map.current?.getZoom() ?? 8;
        const maxDistDeg = Math.max(0.12, 0.6 - (zoom * 0.03)); // larger allowance when zoomed out
        for (const f of features) {
          const meta = extractCountryMeta(f);
          if (!meta.code) continue;
          const geom = f?.geometry;
          if (!geom?.coordinates) continue;
          let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
          const walk = (coords: any): void => {
            if (!Array.isArray(coords)) return;
            if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
              const x = coords[0], y = coords[1];
              if (x < minLng) minLng = x;
              if (x > maxLng) maxLng = x;
              if (y < minLat) minLat = y;
              if (y > maxLat) maxLat = y;
              return;
            }
            coords.forEach(walk);
          };
          walk(geom.coordinates);
          if (!isFinite(minLng) || !isFinite(maxLng) || !isFinite(minLat) || !isFinite(maxLat)) continue;
          const cx = (minLng + maxLng) / 2;
          const cy = (minLat + maxLat) / 2;
          const dx = cx - lngNorm;
          const dy = cy - lat;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < bestDist) {
            bestDist = d;
            best = { code: meta.code, name: meta.name || `Country ${meta.code}`, iso2: meta.iso2 };
          }
        }
        return bestDist <= maxDistDeg ? best : null;
      };

      // Expose resolver to JSX callbacks (flyToResult, etc.) via stable ref.
      resolveCountryRef.current = resolveCountryFromGeoJson;

      // Globe-aware sampling: sample around the click in screen space,
      // unproject each point, then resolve country from GeoJSON.
      const resolveCountryFromGeoJsonByScreenSampling = (
        point: { x: number; y: number },
        radiusPx: number
      ): { code: number; name: string; iso2: string | null } | null => {
        if (!map.current) return null;
        const half = Math.max(4, Math.round(radiusPx / 2));
        const offsets: Array<[number, number]> = [
          [0, 0],
          [half, 0], [-half, 0], [0, half], [0, -half],
          [radiusPx, 0], [-radiusPx, 0], [0, radiusPx], [0, -radiusPx],
          [half, half], [half, -half], [-half, half], [-half, -half],
          [radiusPx, radiusPx], [radiusPx, -radiusPx], [-radiusPx, radiusPx], [-radiusPx, -radiusPx],
          [Math.round(radiusPx * 0.75), 0], [0, Math.round(radiusPx * 0.75)],
          [-Math.round(radiusPx * 0.75), 0], [0, -Math.round(radiusPx * 0.75)],
        ];

        type Scored = { code: number; name: string; iso2: string | null; count: number; minD2: number };
        const scored = new Map<number, Scored>();

        for (const [dx, dy] of offsets) {
          const sample = map.current.unproject({ x: point.x + dx, y: point.y + dy } as any);
          const hit = resolveCountryFromGeoJson(sample.lng, sample.lat, false); // exact polygon only
          if (!hit) continue;
          const d2 = dx * dx + dy * dy;
          const prev = scored.get(hit.code);
          if (!prev) {
            scored.set(hit.code, { ...hit, count: 1, minD2: d2 });
          } else {
            prev.count += 1;
            if (d2 < prev.minD2) prev.minD2 = d2;
          }
        }

        if (scored.size === 0) return null;
        const ranked = Array.from(scored.values()).sort((a, b) => {
          if (b.count !== a.count) return b.count - a.count;
          return a.minD2 - b.minD2;
        });
        const best = ranked[0];
        return { code: best.code, name: best.name, iso2: best.iso2 };
      };

      const handleCountryClick = async (e: mapboxgl.MapMouseEvent) => {
        // Yield to the spatial admin overlay if it's currently waiting
        // for a map click (e.g., "Import 3D building" or "Draw geometry"
        // mode). Otherwise the country tooltip would steal the click
        // and pop on top of the building the operator was trying to
        // import.
        if (isMapClicksClaimed()) return;

        // On a phone, a surface the visitor opened on purpose owns the
        // screen. A country card beside a conversation leaves neither
        // readable, and the country is the one nobody asked for.
        if (isMobileViewportRef.current && openMajorSurfaceRef.current) return;

        // Mobile taps can land on borders/anti-aliased edges and miss exact hit testing.
        // Use a small tolerance radius to match desktop's practical border behavior.
        // Use ref (not closure state) so we always read the live viewport flag.
        const touchDevice = isMobileViewportRef.current;
        const zoom = map.current?.getZoom() ?? 8;
        const adaptiveTolerance = tapTolerancePx({ touch: touchDevice, zoom });

        // Yield to the GeoLink spatial-objects subsystem when the click
        // landed on one of its layers — the SpatialObjectsMapLayer has
        // its own click handler that opens an interaction panel for the
        // tapped building / floor / zone / slice, and the country
        // overlay would otherwise cover that with the country tooltip.
        //
        // Padded by the same tolerance the country resolution below
        // uses. Yielding on an exact pixel and then resolving a country
        // with 44 px of slack means every near miss goes to the country
        // — which on a phone is most of them.
        if (spatialFeaturesAt(map.current, e.point, adaptiveTolerance).length > 0) return;

        // Mobile path: use globe-aware screen sampling against GeoJSON first.
        const geoPrimary = touchDevice
          ? (resolveCountryFromGeoJsonByScreenSampling(e.point as any, adaptiveTolerance)
            || resolveCountryFromGeoJson(e.lngLat.lng, e.lngLat.lat, true))
          : null;

        let features = [] as mapboxgl.MapboxGeoJSONFeature[];
        let sampledFeature: mapboxgl.MapboxGeoJSONFeature | null = null;
        if (!geoPrimary && touchDevice) {
          sampledFeature = pickCountryFeatureBySampling(e.point as any, adaptiveTolerance);
        }
        if (!geoPrimary && !sampledFeature) {
          features = getCountryFeaturesAtPoint(e.point as any, 0, ['countries-fill']);
          if (features.length === 0 && touchDevice) {
            features = getCountryFeaturesAtPoint(e.point as any, adaptiveTolerance, ['countries-fill']);
          }
        }

        const hitFeature = sampledFeature || (features.length > 0 ? pickBestCountryFeature(features, e.point as any) : null);
        const geoFallback = geoPrimary || ((!hitFeature && touchDevice) ? resolveCountryFromGeoJson(e.lngLat.lng, e.lngLat.lat) : null);
        if (hitFeature || geoFallback) {
          let countryCode = hitFeature ? hitFeature.properties?.ISO_NUMERIC : geoFallback?.code;
          if (!countryCode) {
            const iso3 = hitFeature?.id;
            const iso2 = hitFeature ? (hitFeature.properties?.ISO2 || hitFeature.properties?.ISO_A2) : geoFallback?.iso2;
            
            if (iso2) {
              countryCode = iso2ToNumeric(iso2);
            } else if (iso3) {
              const iso3Str = typeof iso3 === 'string' ? iso3 : String(iso3);
              const iso2FromIso3 = iso3ToIso2(iso3Str);
              if (iso2FromIso3) {
                countryCode = iso2ToNumeric(iso2FromIso3);
              }
            }
          }
          
          if (countryCode) {
            const code = Number(countryCode);
            
            // Get country name from cache or GeoJSON
            let countryName: string = countryNameCache.get(code) || '';
            if (!countryName) {
              const name = hitFeature ? (hitFeature.properties?.name || hitFeature.properties?.NAME) : geoFallback?.name;
              if (name) {
                countryName = String(name);
                const newCache = new Map(countryNameCache);
                newCache.set(code, countryName);
                setCountryNameCache(newCache);
              } else {
                // Fallback: try to load from GeoJSON
                try {
                  const response = await fetch('/api/countries', { headers: { 'Cache-Control': 'no-cache' } });
                  if (response.ok) {
                    const geojson = await response.json();
                    const feature = geojson.features.find((f: any) => {
                      const fCode = f.properties?.ISO_NUMERIC;
                      return fCode && Number(fCode) === code;
                    });
                    if (feature) {
                      countryName = feature.properties?.name || feature.properties?.NAME || `Country ${code}`;
                      const newCache = new Map(countryNameCache);
                      newCache.set(code, countryName);
                      setCountryNameCache(newCache);
                    } else {
                      countryName = `Country ${code}`;
                    }
                  } else {
                    countryName = `Country ${code}`;
                  }
                } catch (error) {
                  // console.error('[App] Failed to load country name:', error);
                  countryName = `Country ${code}`;
                }
              }
            }
            
            // Ensure countryName is not empty
            if (!countryName) {
              countryName = `Country ${code}`;
            }
            
            // Check if user is admin (main admin or country admin)
            const isMainAdmin = !!(walletAddress && mainAdminAddress && walletAddress === mainAdminAddress);
            let isCountryAdmin = false;
            
            if (walletAddress && !isMainAdmin) {
              try {
                // Use read-only client to avoid Freighter prompts
                const readOnlyClient = new ReadOnlyContractClient();
                const admin = await readOnlyClient.getCountryAdmin(code);
                isCountryAdmin = admin === walletAddress;
              } catch (error) {
                // Ignore errors - user is not country admin
              }
            }
            
            // On mobile: tap shows tooltip card; opening country profile happens from tooltip action
            if (isMobileViewportRef.current) {
              suppressNextEmptyMapClickRef.current = true;
              mobileTooltipCountryRef.current = code;
              const iso2 = hitFeature ? (hitFeature.properties?.ISO2 || hitFeature.properties?.ISO_A2 || null) : (geoFallback?.iso2 || null);
              setHoveredCountry({ countryCode: code, countryName, iso2, position: { x: e.point.x, y: e.point.y } });
              return;
            }

            // Desktop → open overlay on click
            mobileTooltipCountryRef.current = null;

            // Let the navigation menu know this wallet manages at least one country
            if (isCountryAdmin && !isAdminOrCountryAdmin) {
              setIsAdminOrCountryAdmin(true);
            }
            if (isCountryAdmin) {
              setAdminCountryScope((prev) => {
                if (prev.has(code)) return prev;
                const next = new Set(prev);
                next.add(code);
                return next;
              });
            }

            // Open appropriate overlay
            if (isMainAdmin || isCountryAdmin) {
              setSelectedCountry({ code, name: countryName });
              setShowCountryManagementOverlay(true);
            } else {
              setSelectedCountry({ code, name: countryName });
              setShowCountryProfileOverlay(true);
            }
          }
        }
      };

      // Desktop: use layer-specific click (works fine in Mercator).
      // Mobile: handled by the global handler below (globe-mode safe).
      // IMPORTANT: register layer handlers always; they will be skipped inside
      // handleCountryClick for mobile (where isMobileViewportRef.current is true).
      map.current.on('click', 'countries-fill', handleCountryClick);
      map.current.on('click', 'countries-outline', handleCountryClick);
      map.current.getCanvas().style.cursor = 'pointer';

      // Global click handler – primary country-detection path for mobile.
      // Uses e.lngLat (Mapbox computes this correctly for globe mode at any screen
      // position) so country resolution never depends on rendered tile hit-testing.
      const handleMobileGlobalClick = (e: mapboxgl.MapMouseEvent) => {
        // Always read the live ref – not the stale closure value.
        if (!isMobileViewportRef.current) return;

        // Same yield as the desktop handler — give priority to the
        // spatial admin overlay's pending click claim (import / draw)
        // before anything else.
        if (isMapClicksClaimed()) return;

        // And to a deliberate surface, for the same reason: this is the
        // handler that was raising a country card over an open
        // conversation on every stray tap.
        if (openMajorSurfaceRef.current) return;

        const zoom = map.current?.getZoom() ?? 8;

        // Same spatial-objects yield as the desktop handler — when the
        // tap landed on a GeoLink building/floor/zone/slice we leave it
        // to the SpatialObjectsMapLayer panel. Padded for a fingertip,
        // since this handler only ever runs on a touch viewport.
        if (spatialFeaturesAt(map.current, e.point, tapTolerancePx({ touch: true, zoom })).length > 0) {
          return;
        }

        // 1) Exact point-in-polygon using e.lngLat — Mapbox computes this correctly
        //    for globe mode regardless of where the country sits on the sphere.
        let resolved = resolveCountryFromGeoJson(e.lngLat.lng, e.lngLat.lat, false);

        // 2) Screen-space sampling around the tap (helps edge taps / small countries).
        if (!resolved) {
          resolved = resolveCountryFromGeoJsonByScreenSampling(
            e.point as any, tapTolerancePx({ touch: true, zoom }),
          );
        }

        // 3) Nearest-country geographic fallback (zoomed-out tiny countries).
        if (!resolved) {
          resolved = resolveCountryFromGeoJson(e.lngLat.lng, e.lngLat.lat, true);
        }

        if (resolved) {
          // Pre-populate the tooltip immediately so it shows without waiting for
          // the async admin/vault checks inside handleCountryClick.
          const cachedName = countryNameCache.get(resolved.code) || resolved.name || `Country ${resolved.code}`;
          mobileTooltipCountryRef.current = resolved.code;
          setHoveredCountry({
            countryCode: resolved.code,
            countryName: cachedName,
            iso2: resolved.iso2,
            position: { x: e.point.x, y: e.point.y },
          });
          // Also call handleCountryClick so vault-balance and admin state load correctly.
          // It will overwrite setHoveredCountry with the same data (or richer name).
          handleCountryClick(e);
        } else {
          // Tapped ocean / nothing – dismiss tooltip.
          mobileTooltipCountryRef.current = null;
          setHoveredCountry(null);
          setVaultBalance(null);
          setTotalVaultBalance(null);
          setIsLoadingVaultBalance(false);
          setHoveredVaultInfo(null);
          setTooltipSearchVal('');
          setTooltipSearchResults([]);
        }
      };
      map.current.on('click', handleMobileGlobalClick);

      // ── Vault info loader ────────────────────────────────────────────────────
      // Extracted from handleCountryMouseEnter so it can also be called from
      // flyToResult after the fly animation ends.
      const loadVaultInfo = async (iso2Upper: string): Promise<void> => {
        const currentWalletAddress = walletAddressRef.current;
        if (!currentWalletAddress || !iso2Upper || !/^[A-Z]{2}$/.test(iso2Upper)) return;

        currentFetchingCountryRef.current = iso2Upper;
        setIsLoadingVaultBalance(true);
        setVaultBalance(null);
        setSmartAccountVaultBalance(null);
        setHoveredVaultInfo(null);

        try {
          const readOnlyClient = new ReadOnlyContractClient();
          const vaultInfoPromise = readOnlyClient.getCountryInfo(iso2Upper);
          const timeoutPromise = new Promise<null>((_, reject) =>
            setTimeout(() => reject(new Error('Timeout')), 3000)
          );

          let vaultInfo: any = null;
          try {
            vaultInfo = await Promise.race([vaultInfoPromise, timeoutPromise]) as any;
            if (currentFetchingCountryRef.current !== iso2Upper) return;
          } catch {
            if (currentFetchingCountryRef.current !== iso2Upper) return;
            vaultInfo = null;
          }

          if (currentFetchingCountryRef.current !== iso2Upper) return;

          if (!vaultInfo) {
            setHoveredVaultInfo({ registered: false, enabled: false });
            setVaultBalance(null);
            setIsLoadingVaultBalance(false);
            return;
          }

          setHoveredVaultInfo({ registered: true, enabled: vaultInfo.enabled });

          if (!vaultInfo.enabled) {
            setVaultBalance(null);
            setIsLoadingVaultBalance(false);
            return;
          }

          const assetAddress = process.env.REACT_APP_NATIVE_XLM_SAC_ADDRESS ||
            'CDLZFC3SYJYDZT7K67VZ75HPJVIEUVNIXF47ZG2FB2RMQQVU2HHGCYSC';
          const contractId = process.env.REACT_APP_V2_VAULT_ID;
          const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

          if (!contractId) { setIsLoadingVaultBalance(false); return; }

          const balanceUrl = `${backendUrl}/api/smart-wallet/get-balance`;
          const totalBalanceUrl = `${backendUrl}/api/smart-wallet/get-total-vault-balance`;

          const balancePromises: Promise<any>[] = [
            fetch(totalBalanceUrl, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ contractId, countryCode: iso2Upper, assetAddress, rpcUrl: process.env.REACT_APP_SOROBAN_RPC_URL })
            }).then(r => r.json()).catch(() => ({ success: false, balance: 0 })),
          ];

          const linkedSmartAccount = currentWalletAddress
            ? passkeyKitService.getAccountByWallet(currentWalletAddress)
            : null;

          if (currentWalletAddress) {
            balancePromises.push(
              fetch(balanceUrl, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ contractId, userAddress: currentWalletAddress, countryCode: iso2Upper, assetAddress, rpcUrl: process.env.REACT_APP_SOROBAN_RPC_URL })
              }).then(r => r.json()).catch(() => ({ success: false, balance: 0 }))
            );
            if (linkedSmartAccount) {
              balancePromises.push(
                fetch(balanceUrl, {
                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ contractId, userAddress: linkedSmartAccount.accountAddress, countryCode: iso2Upper, assetAddress, rpcUrl: process.env.REACT_APP_SOROBAN_RPC_URL })
                }).then(r => r.json()).catch(() => ({ success: false, balance: 0 }))
              );
            }
          }

          const results = await Promise.all(balancePromises);
          if (currentFetchingCountryRef.current !== iso2Upper) return;

          setTotalVaultBalance(results[0].success ? (Number(results[0].balance) || 0) : 0);
          const walletBal = (currentWalletAddress && results[1]?.success) ? (Number(results[1].balance) || 0) : 0;
          const smartBal  = (linkedSmartAccount   && results[2]?.success) ? (Number(results[2].balance) || 0) : 0;
          setVaultBalance(currentWalletAddress ? walletBal : null);
          setSmartAccountVaultBalance(linkedSmartAccount ? smartBal : null);
        } catch {
          if (currentFetchingCountryRef.current !== iso2Upper) return;
          setHoveredVaultInfo({ registered: false, enabled: false });
          setVaultBalance(null);
          setSmartAccountVaultBalance(null);
          setTotalVaultBalance(null);
        } finally {
          if (currentFetchingCountryRef.current === iso2Upper) setIsLoadingVaultBalance(false);
        }
      };

      // Expose vault loader to JSX callbacks (flyToResult, etc.)
      loadVaultInfoRef.current = loadVaultInfo;

      // Add hover handlers for vault balance tooltip
      const handleCountryMouseEnter = async (e: mapboxgl.MapMouseEvent) => {
        // While the spatial admin is waiting for a map click (import /
        // draw mode), don't even start a country hover — the operator
        // is targeting a building, not the country.
        if (isMapClicksClaimed()) return;

        // A touch device has no hover. What it has is a tap, which the
        // browser dresses up as `mouseenter` before the click arrives —
        // so this handler ran on every tap and raised a country card
        // through a path the click yield never sees. The matching leave
        // handler has always skipped on mobile; this is the other half.
        if (isMobileViewportRef.current) return;

        // Yield to a spatial object under the pointer for the same
        // reason the click handler does: a hover card over the building
        // being inspected covers the thing it describes.
        if (spatialFeaturesAt(map.current, e.point).length > 0) return;

        // Cancel any pending leave-dismiss so moving between countries is instant.
        if (countryLeaveTimerRef.current) {
          clearTimeout(countryLeaveTimerRef.current);
          countryLeaveTimerRef.current = null;
        }

        // console.log('[App] handleCountryMouseEnter triggered');
        const features = map.current!.queryRenderedFeatures(e.point, {
          layers: ['countries-fill'],
        });

        // console.log('[App] Features found:', features.length);
        if (features.length > 0) {
          const feature = features[0];
          // console.log('[App] Feature properties:', JSON.stringify(feature.properties).substring(0, 200));
          // console.log('[App] Feature id:', feature.id);
          
          // Use extractCountryInfo utility to properly extract country info from GeoJSON
          const countryInfo = extractCountryInfo(feature);
          if (countryInfo) {
            // console.log('[App] Extracted country info:', 
            //   'name=' + countryInfo.name,
            //   'iso2=' + countryInfo.iso2,
            //   'iso3=' + (countryInfo.iso3 || 'null'),
            //   'numeric=' + (countryInfo.numeric || 'null')
            // );
          } else {
            // console.log('[App] Extracted country info: null');
          }
          
          let countryCode: number | null = null;
          let iso2: string | null = null;
          let countryName: string = 'Unknown Country';
          
          if (countryInfo) {
            // Use the extracted info
            iso2 = countryInfo.iso2;
            countryCode = countryInfo.numeric || null;
            countryName = countryInfo.name;
          } else {
            // Fallback: try to get from properties directly
            countryCode = feature.properties?.ISO_NUMERIC ? Number(feature.properties.ISO_NUMERIC) : null;
            iso2 = feature.properties?.ISO2 || null;
            countryName = feature.properties?.name || feature.properties?.NAME || 'Unknown Country';
            
            // If we have countryCode but no iso2, try to convert
            if (countryCode && !iso2) {
              // Try to get from feature.id (ISO3) or properties
              const iso3 = feature.id || feature.properties?.id || feature.properties?.ISO3;
              if (iso3) {
                const iso3Str = typeof iso3 === 'string' ? iso3 : String(iso3);
                const convertedIso2 = iso3ToIso2(iso3Str);
                if (convertedIso2) {
                  iso2 = convertedIso2;
                }
              }
            }
            
            // If we have iso2 but no countryCode, convert
            if (iso2 && !countryCode) {
              countryCode = iso2ToNumeric(iso2);
            }
          }
          
          // Get current wallet address from ref (to avoid stale closure)
          const currentWalletAddress = walletAddressRef.current;
          
          // console.log('[App] Extracted country data:', { 
          //   countryCode: countryCode !== null ? countryCode : 'null', 
          //   iso2: iso2 || 'null', 
          //   countryName: countryName || 'null', 
          //   walletAddress: currentWalletAddress ? currentWalletAddress.substring(0, 8) + '...' : 'missing' 
          // });
          
          if (countryCode) {
            // Ensure ISO2 is uppercase for consistency with contract storage
            const iso2Upper = iso2 ? iso2.toUpperCase() : null;
            
            // console.log('[App] Setting hovered country:', { 
            //   countryCode, 
            //   countryName, 
            //   iso2Upper, 
            //   walletAddress: currentWalletAddress ? currentWalletAddress.substring(0, 8) + '...' : 'missing' 
            // });
            
            // Check if this is a different country (not just a position update)
            const isDifferentCountry = !hoveredCountry || hoveredCountry.countryCode !== countryCode;
            
            // Only proceed if we have a valid country code
            if (countryCode !== null) {
              // Capture values for use in callbacks (TypeScript type narrowing)
              const finalCountryCode = countryCode;
              const finalCountryName = countryName;
              const finalIso2Upper = iso2Upper;
              const finalPosition = { x: e.point.x, y: e.point.y };
              
              // Check if we just processed this country (prevent duplicate calls from mousemove)
              const now = Date.now();
              const lastEnter = lastCountryEnterRef.current;
              if (lastEnter && lastEnter.countryCode === finalCountryCode && (now - lastEnter.timestamp) < 50) {
                // console.log('[App] Skipping duplicate handleCountryMouseEnter for', finalCountryCode);
                return;
              }
              
              // Update last enter tracking
              lastCountryEnterRef.current = { countryCode: finalCountryCode, timestamp: now };
              
              if (isDifferentCountry) {
                // Force tooltip re-render by incrementing the key (this triggers a re-render)
                setTooltipRenderKey(prev => prev + 1);
                // Clear old vault state FIRST when switching countries
                // Use flushSync to force synchronous state updates
                flushSync(() => {
                  setVaultBalance(null);
                  setTotalVaultBalance(null);
                  setHoveredVaultInfo(null);
                  setIsLoadingVaultBalance(false);
                });
              }
              
              // Immediately update the hovered country (this will update the tooltip right away)
              // Use flushSync to force synchronous update so tooltip updates immediately
              flushSync(() => {
                setHoveredCountry({
                  countryCode: finalCountryCode,
                  countryName: finalCountryName,
                  iso2: finalIso2Upper,
                  position: finalPosition
                });
                // Update ref for reliable border detection
                currentHoveredCountryCodeRef.current = finalCountryCode;
              });
            }
            
            // Load vault info and balance if user is logged in and ISO2 is available
            // Variables only used in commented-out console.log
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const _hasWallet = !!currentWalletAddress;
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const _hasIso2 = !!iso2Upper;
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const _iso2Value = iso2Upper || 'null';
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const _regexPass = iso2Upper ? /^[A-Z]{2}$/.test(iso2Upper) : false;
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const _allMet = !!(currentWalletAddress && iso2Upper && /^[A-Z]{2}$/.test(iso2Upper));
            // console.log('[App] Checking vault conditions:', 
            //   'hasWallet=' + hasWallet,
            //   'hasIso2=' + hasIso2,
            //   'iso2=' + iso2Value,
            //   'regexPass=' + regexPass,
            //   'allMet=' + allMet
            // );
            
            if (currentWalletAddress && iso2Upper && /^[A-Z]{2}$/.test(iso2Upper)) {
              await loadVaultInfo(iso2Upper);
            } else if (walletAddress && !iso2) {
              // User is logged in but ISO2 not available
              setHoveredVaultInfo(null);
              setIsLoadingVaultBalance(false);
            }
          }
        }
      };
      
      const handleCountryMouseLeave = () => {
        // On touch devices the tooltip is dismissed explicitly (close button / tap outside),
        // not by leaving the country boundary
        if (isMobileViewportRef.current) return;

        // Don't hide if user's mouse is already over the tooltip itself
        if (tooltipHoveredRef.current) return;

        // Give the user 1 second to move their cursor onto the tooltip before dismissing.
        if (countryLeaveTimerRef.current) clearTimeout(countryLeaveTimerRef.current);
        countryLeaveTimerRef.current = setTimeout(() => {
          countryLeaveTimerRef.current = null;
          // Re-check: user may have moved onto the tooltip during the grace period.
          if (tooltipHoveredRef.current) return;
          currentFetchingCountryRef.current = null;
          currentHoveredCountryCodeRef.current = null;
          setHoveredCountry(null);
          tooltipLockedPosRef.current = null;
          setVaultBalance(null);
          setTotalVaultBalance(null);
          setIsLoadingVaultBalance(false);
          setHoveredVaultInfo(null);
          setTooltipSearchVal('');
          setTooltipSearchResults([]);
        }, 1000);
      };
      
      // Track last mouse move time to throttle position updates
      let lastMouseMoveTime = 0;
      const MOUSE_MOVE_THROTTLE = 16; // ~60fps
      let lastLoggedCountryCode: number | null = null;
      
      const handleCountryMouseMove = (e: mapboxgl.MapMouseEvent) => {
        if (isMapClicksClaimed()) return;
        // Same reason as the enter handler: a tap emits this too, and
        // it can promote itself into the enter logic at a border.
        if (isMobileViewportRef.current) return;
        if (spatialFeaturesAt(map.current, e.point).length > 0) return;

        const now = Date.now();
        const timeSinceLastMove = now - lastMouseMoveTime;
        
        // Check if we're still over the same country
        const features = map.current!.queryRenderedFeatures(e.point, {
          layers: ['countries-fill'],
        });

        if (features.length > 0) {
          const feature = features[0];
          const countryInfo = extractCountryInfo(feature);
          
          // Get country code from the feature
          let countryCode: number | null = null;
          if (countryInfo?.numeric) {
            countryCode = countryInfo.numeric;
          } else if (feature.properties?.ISO_NUMERIC) {
            countryCode = Number(feature.properties.ISO_NUMERIC);
          } else {
            // Try to get ISO2 and convert
            const iso2 = countryInfo?.iso2 || feature.properties?.ISO2;
            if (iso2) {
              countryCode = iso2ToNumeric(iso2);
            }
          }
          
          // Log when country code changes (for debugging)
          if (countryCode && countryCode !== lastLoggedCountryCode) {
            // console.log('[App] 🗺️ MouseMove: Detected country code', countryCode, 'currentHoveredRef:', currentHoveredCountryCodeRef.current);
            lastLoggedCountryCode = countryCode;
          }
          
          // If we've moved to a different country, trigger mouseenter logic
          // Use ref instead of state for reliable comparison (state might be stale)
          if (countryCode && countryCode !== currentHoveredCountryCodeRef.current) {
            // console.log('[App] 🚨 Border detected: Country changed from', currentHoveredCountryCodeRef.current, 'to', countryCode);
            // Clear the ref to cancel any in-flight requests for the old country
            currentFetchingCountryRef.current = null;
            // Immediately trigger enter for new country
            handleCountryMouseEnter(e);
            lastMouseMoveTime = now;
            return;
          }
          
          // Update position only if tooltip is not locked (user hasn't hovered it)
          if (hoveredCountry && countryCode === hoveredCountry.countryCode && timeSinceLastMove >= MOUSE_MOVE_THROTTLE && !tooltipHoveredRef.current) {
            setHoveredCountry(prev => prev ? {
              ...prev,
              position: { x: e.point.x, y: e.point.y }
            } : null);
            lastMouseMoveTime = now;
          }
        } else if (hoveredCountry) {
          // No features found but we have a hovered country - might have moved outside
          // Don't clear immediately, wait for mouseleave
        }
      };
      
      map.current.on('mouseenter', 'countries-fill', handleCountryMouseEnter);
      map.current.on('mouseleave', 'countries-fill', handleCountryMouseLeave);
      map.current.on('mousemove', 'countries-fill', handleCountryMouseMove);
      
      return () => {
        map.current?.off('click', 'countries-fill', handleCountryClick);
        map.current?.off('click', 'countries-outline', handleCountryClick);
        map.current?.off('click', handleMobileGlobalClick);
        map.current?.off('mouseenter', 'countries-fill', handleCountryMouseEnter);
        map.current?.off('mouseleave', 'countries-fill', handleCountryMouseLeave);
        map.current?.off('mousemove', 'countries-fill', handleCountryMouseMove);
        if (map.current) {
          map.current.getCanvas().style.cursor = '';
        }
        // Clean up globe-spin resources
        globeSpinningRef.current = false;
        if (globeIdleTimerRef.current) clearTimeout(globeIdleTimerRef.current);
        if (globeSpinIntervalRef.current) { clearInterval(globeSpinIntervalRef.current); globeSpinIntervalRef.current = null; }
        window.removeEventListener('keydown',     onWindowActivity);
        window.removeEventListener('pointerdown', onWindowActivity);
      };
    } catch (error) {
      // console.error('[App] Failed to initialize map:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadCountryOverlay, mapStyle, pitch, bearing, updateCountryOverlay]);

  useEffect(() => {
    // Only initialize map once - if map already exists, don't re-initialize
    if (map.current) {
      // console.log('[App] Map already exists, skipping initialization');
      return;
    }
    
    // console.log('[App] Map initialization useEffect running');
    
    // Wait for the ref to be attached to the DOM
    // Use a small delay to ensure the ref is attached
    const timer = setTimeout(() => {
      // console.log('[App] Map initialization timer fired');
      // console.log('[App] mapContainer.current:', mapContainer.current);
      // console.log('[App] map.current:', map.current);
      
      // Double-check map doesn't exist (might have been created in the meantime)
      if (map.current) {
        // console.log('[App] Map already exists, skipping initialization');
        return;
      }
      
      if (!mapContainer.current) {
        // console.warn('[App] mapContainer.current is null');
        return;
      }
      
      // Get the container and verify it has a style property
      const container = mapContainer.current;
      if (!container) {
        // console.warn('[App] Container is null');
        return;
      }
      
      // Check if container is actually a DOM element with style property
      if (!container.style || typeof container.style === 'undefined') {
        // console.warn('[App] Container style property is undefined or not available');
        return;
      }
      
      // console.log('[App] Calling initializeMap');
      initializeMap(container);
    }, 100); // Increased delay to 100ms to ensure DOM is ready
    
    return () => {
      clearTimeout(timer);
      // Don't cleanup map here - let it persist across re-renders
      // Only cleanup on unmount
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount (initializeMap changes when country policy changes, but we don't want to re-initialize the map)

  // Update country overlay when country policy or map changes.
  // Guard behind countryDataLoadedRef so we never paint with stale empty data.
  useEffect(() => {
    if (!countryDataLoadedRef.current) return; // data not ready yet

    const updateOverlayIfReady = () => {
      if (map.current && map.current.loaded()) {
        const source = map.current.getSource('countries');
        if (source) {
          updateCountryOverlay();
          return true;
        }
      }
      return false;
    };

    if (!updateOverlayIfReady()) {
      const retryTimer = setTimeout(() => {
        updateOverlayIfReady();
      }, 300);
      return () => clearTimeout(retryTimer);
    }
  }, [allowedCountries, defaultAllowAll, updateCountryOverlay]);

  // Ref to store connectWallet function so it can be accessed in updateSessionMarkers
  const connectWalletRef = useRef<(() => Promise<void>) | null>(null);
  // Ref to store current walletAddress so event listeners always have the latest value
  const walletAddressRef = useRef<string | null>(null);
  // Refs mirroring the live-session / profile-overlay state so the
  // marker tap handlers (attached once when the marker is created)
  // can decide synchronously whether a tap should open a profile or
  // be suppressed because a minimised live session is already
  // holding the overlay.  Without these refs, the stale closure sees
  // the initial render's values and either misses the guard or fires
  // a React state round-trip that produces a one-frame popup flash.
  const profileOverlayForRef = useRef<string | null>(null);
  const liveSessionActiveRef = useRef<boolean>(false);

  // Update walletAddressRef whenever walletAddress changes
  useEffect(() => {
    walletAddressRef.current = walletAddress;
  }, [walletAddress]);

  // Mirror profileOverlayFor + liveSessionActive into refs.  Marker
  // tap handlers are registered once when a marker is created and
  // capture whatever these values were at that moment; without the
  // refs they'd never see updates and the "don't clobber a minimised
  // live session" guard would silently misfire.
  useEffect(() => { profileOverlayForRef.current = profileOverlayFor; }, [profileOverlayFor]);
  useEffect(() => { liveSessionActiveRef.current = liveSessionActive; }, [liveSessionActive]);

  // Convert cellId back to approximate coordinates (center of cell)
  const cellIdToCoordinates = useCallback((cellId: number, addRandomization: boolean = false): [number, number] => {
    const cellX = cellId % 360;
    const cellY = Math.floor(cellId / 360);
    // Center of the cell (1 degree grid)
    let lng = cellX * 1.0 - 180 + 0.5;
    let lat = cellY * 1.0 - 90 + 0.5;
    
    // Add randomization within the cell for privacy when not logged in
    if (addRandomization) {
      // Random offset within ±0.4 degrees (stays within cell boundaries)
      lng += (Math.random() - 0.5) * 0.8;
      lat += (Math.random() - 0.5) * 0.8;
    }
    
    return [lng, lat];
  }, []);

  // Show markers from active sessions to entice users
  const updateSessionMarkers = useCallback((sessions: Array<{ 
    sessionId: number; 
    player1: string | null; 
    player2: string | null; 
    state: string;
    p1CellId?: number;
    p2CellId?: number;
    p1Country?: number;
    p2Country?: number;
    players?: Array<{ address: string; cell_id: number; asset_tag?: any; country?: number; location_proof?: any }>;
  }>) => {
    if (!map.current) return;
    
    // Remove existing session markers
    document.querySelectorAll('.session-user-marker').forEach(m => m.remove());
    
    // console.log('[App] updateSessionMarkers: Processing', sessions.length, 'sessions');
    
    // When not logged in, use randomized positions to protect privacy
    const useRandomizedPositions = !walletAddress;
    
    // Add markers for players in active sessions
    // Support both old format (player1/player2) and new format (players array)
    sessions.forEach(session => {
      // Helper function to add a marker for a player
      const addPlayerMarker = (playerAddress: string, cellId: number, country?: number) => {
        // Skip if this is the current user's wallet (don't show own marker)
        if (walletAddressRef.current && playerAddress === walletAddressRef.current) {
          return;
        }
        
        // Convert cell_id to approximate lat/lon
        const [lng, lat] = cellIdToCoordinates(cellId, useRandomizedPositions);
        
        const el = document.createElement('div');
        el.className = 'marker marker-opponent session-user-marker';
        const publicKeyShort = `${playerAddress.slice(0, 4)}...${playerAddress.slice(-4)}`;
        el.innerHTML = `<div class="marker-label">👤<div class="marker-public-key">${publicKeyShort}</div></div>`;
        // Match other marker CSS (NFT, contract, other-user markers)
        el.style.cssText = `
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          position: absolute;
          width: 24px;
          height: 24px;
          transform: translate(-50%, -50%);
          pointer-events: auto;
        `;
        el.title = `${playerAddress} - Session #${session.sessionId}`;
        
        // Add hover event listeners to show radius circle
        el.addEventListener('mouseenter', () => {
          const radiusMeters = maxDistance * 1000; // Convert km to meters
          showRadiusCircle([lng, lat], radiusMeters);
        });
        
        el.addEventListener('mouseleave', () => {
          hideRadiusCircle();
        });
        
        attachMarkerTap(el, {
          onTap: () => {
            // When not logged in, prompt to connect wallet instead of showing profile.
            // walletAddressRef always holds the latest value — the closure captured by
            // the marker on creation is too stale to trust.
            if (!walletAddressRef.current) {
              if (window.confirm('Connect your wallet to view this account on the map and join sessions.')) {
                if (connectWalletRef.current) {
                  connectWalletRef.current().catch(() => { /* ignore */ });
                }
              }
              return;
            }
            // Session markers still surface the join-session popup
            // (the logic that lets players accept/decline the live
            // game session).  `sessionId` is what tells the popup's
            // useEffect to *not* swap to ProfileOverlay, so we keep
            // the selectedMarker path here on purpose.
            setSelectedMarker({
              type: 'opponent',
              location: [lng, lat],
              publicKey: playerAddress,
              sessionId: session.sessionId,
              cellId: cellId,
              country: country,
            });
          },
        });
        
        try {
          new mapboxgl.Marker({ element: el })
            .setLngLat([lng, lat])
            .addTo(map.current!);
        } catch (error) {
          // console.error('[App] Failed to add session marker:', error);
        }
      };
      
      // Check players array (new format - public sessions)
      if (session.players && Array.isArray(session.players)) {
        session.players.forEach((player: any) => {
          if (player.address && player.cell_id) {
            addPlayerMarker(player.address, player.cell_id, player.country);
          }
        });
      }
      
      // Check player1/player2 (old format - private sessions)
      if (session.player1 && session.p1CellId) {
        addPlayerMarker(session.player1, session.p1CellId, session.p1Country);
      }
      
      if (session.player2 && session.p2CellId) {
        addPlayerMarker(session.player2, session.p2CellId, session.p2Country);
      }
    });
  }, [walletAddress, cellIdToCoordinates, maxDistance, showRadiusCircle, hideRadiusCircle]);
  
  // Track if fetchActiveSessions is currently running to prevent concurrent executions
  const isFetchingSessionsRef = useRef(false);

  // Stable ref so fetchActiveSessions never re-creates just because updateSessionMarkers changed.
  // Without this, cellIdToCoordinates changes → updateSessionMarkers changes →
  // fetchActiveSessions useCallback recreates → interval restarts immediately → infinite loop.
  const updateSessionMarkersRef = useRef(updateSessionMarkers);
  useEffect(() => { updateSessionMarkersRef.current = updateSessionMarkers; }, [updateSessionMarkers]);

  // Fetch active sessions (poll recent session IDs)
  // Track known active session IDs to optimize polling
  const knownActiveSessionIdsRef = useRef<Set<number>>(new Set());
  const maxSessionIdSeenRef = useRef<number>(0);

  const fetchActiveSessions = useCallback(async () => {
    if (!readOnlyClient) return;
    
    // Prevent concurrent executions
    if (isFetchingSessionsRef.current) {
      return;
    }
    
    isFetchingSessionsRef.current = true;

    try {
      type SessionEntry = { 
        sessionId: number; 
        player1: string | null; 
        player2: string | null; 
        state: string;
        p1CellId?: number;
        p2CellId?: number;
        p1Country?: number;
        p2Country?: number;
        createdLedger?: number;
        players?: Array<{ address: string; cell_id: number; asset_tag: any; country: number; location_proof?: any }>;
        isPublic?: boolean;
        is_public?: boolean;
        trustGroupIds?: number[];
      };

      // ── PHASE 1: fast path — use backend /active endpoint ─────────────────
      // One HTTP call returns all active sessions scanned server-side.
      // Falls back to individual RPC calls if the endpoint is unavailable.
      const LS_MAX_KEY  = 'geotrust_maxSessionId';
      const LS_IDS_KEY  = 'geotrust_knownSessionIds';

      // ── Restore from localStorage and render cached markers immediately ──────
      const LS_SESSIONS_KEY = 'geotrust_cachedSessions';
      const cachedMax = parseInt(localStorage.getItem(LS_MAX_KEY) || '0', 10);
      const cachedIds: number[] = JSON.parse(localStorage.getItem(LS_IDS_KEY) || '[]');
      if (cachedMax > maxSessionIdSeenRef.current) {
        maxSessionIdSeenRef.current = cachedMax;
      }
      cachedIds.forEach(id => knownActiveSessionIdsRef.current.add(id));

      // Show cached session markers immediately (no RPC wait) so the map feels instant.
      // They will be replaced with fresh data once the backend /active call returns.
      const isFirstCall = maxSessionIdSeenRef.current === 0 || !isFetchingSessionsRef.current;
      if (isFirstCall) {
        try {
          const cachedSessionsRaw = localStorage.getItem(LS_SESSIONS_KEY);
          if (cachedSessionsRaw) {
            const cachedSessions: SessionEntry[] = JSON.parse(cachedSessionsRaw);
            if (Array.isArray(cachedSessions) && cachedSessions.length > 0) {
              setActiveSessions(cachedSessions);
              if (map.current && map.current.loaded()) {
                updateSessionMarkersRef.current(cachedSessions);
              }
            }
          }
        } catch { /* ignore parse errors */ }
      }

      let sessions: SessionEntry[] = [];
      let usedBackendEndpoint = false;

      try {
        // Pass known active IDs + current maxId so the server only checks those
        // + a small forward window — never scans hundreds of IDs in bulk.
        const knownIdsParam = Array.from(knownActiveSessionIdsRef.current).join(',');
        const maxIdParam    = maxSessionIdSeenRef.current;
        const backendBase = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
          ? `${window.location.protocol}//${window.location.hostname}:8080`
          : '';
        const qs = new URLSearchParams();
        if (knownIdsParam) qs.set('knownIds', knownIdsParam);
        qs.set('maxId', String(maxIdParam));

        // Abort after 12 seconds so a sluggish RPC day doesn't leave the UI hanging
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 12000);
        const resp = await fetch(`${backendBase}/api/v2/session/active?${qs.toString()}`, {
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        if (resp.ok) {
          const data = await resp.json();
          if (Array.isArray(data.sessions)) {
            usedBackendEndpoint = true;
            data.sessions.forEach((item: any) => {
              const s = item.session;
              const entry: SessionEntry = {
                sessionId: item.sessionId,
                player1: s.player1 || null,
                player2: s.player2 || null,
                state: s.state || 'Unknown',
                p1CellId: s.p1CellId || s.p1_cell_id,
                p2CellId: s.p2CellId || s.p2_cell_id,
                p1Country: s.p1Country || s.p1_country,
                p2Country: s.p2Country || s.p2_country,
                createdLedger: s.createdLedger || s.created_ledger,
                players: s.players || undefined,
                isPublic: s.isPublic || s.is_public || false,
                is_public: s.is_public || s.isPublic || false,
                trustGroupIds: Array.isArray(s.trustGroupIds) ? s.trustGroupIds : undefined,
              };
              sessions.push(entry);
              knownActiveSessionIdsRef.current.add(item.sessionId);
              maxSessionIdSeenRef.current = Math.max(maxSessionIdSeenRef.current, item.sessionId);
            });
            // Update maxId from response even if no sessions found (tells us the range is clear)
            if (typeof data.maxId === 'number' && data.maxId > maxSessionIdSeenRef.current) {
              maxSessionIdSeenRef.current = data.maxId;
            }
          }
        }
      } catch (err: any) {
        // AbortError = timeout; other errors = endpoint unavailable.
        // Either way fall through to individual RPC calls (or just use cache).
        if (err?.name === 'AbortError') {
          // Timed out — markers already showing from cache; skip fallback RPC calls too
          // to avoid hammering a slow testnet.
          usedBackendEndpoint = true; // prevent fallback from running
        }
      }

      // ── PHASE 2: fallback — individual RPC calls ───────────────────────────
      // Used when the backend /active endpoint is unreachable.
      if (!usedBackendEndpoint) {
        const sessionIdsToCheck = new Set<number>();

        if (userCurrentSession !== null) {
          sessionIdsToCheck.add(userCurrentSession);
          knownActiveSessionIdsRef.current.add(userCurrentSession);
        }
        knownActiveSessionIdsRef.current.forEach(id => sessionIdsToCheck.add(id));

        const checkRange = 20;
        if (maxSessionIdSeenRef.current === 0) {
          for (let id = 1; id <= 100; id++) sessionIdsToCheck.add(id);
        } else {
          const end = Math.min(200, maxSessionIdSeenRef.current + checkRange);
          for (let id = Math.max(1, maxSessionIdSeenRef.current); id <= end; id++) sessionIdsToCheck.add(id);
          for (let id = 1; id <= 10; id++) sessionIdsToCheck.add(id);
        }

        const idsArray = Array.from(sessionIdsToCheck).sort((a, b) => a - b);
        const batchSize = maxSessionIdSeenRef.current === 0 ? 50 : 10;

        for (let i = 0; i < idsArray.length; i += batchSize) {
          const batchPromises = idsArray.slice(i, i + batchSize).map(checkId =>
            readOnlyClient.getSession(checkId)
              .then(session => {
                if (session && (session.state === 'Waiting' || session.state === 'Active')) {
                  knownActiveSessionIdsRef.current.add(checkId);
                  maxSessionIdSeenRef.current = Math.max(maxSessionIdSeenRef.current, checkId);
                  return {
                    sessionId: checkId,
                    player1: session.player1 || null,
                    player2: session.player2 || null,
                    state: session.state || 'Unknown',
                    p1CellId: session.p1CellId || session.p1_cell_id,
                    p2CellId: session.p2CellId || session.p2_cell_id,
                    p1Country: session.p1Country || session.p1_country,
                    p2Country: session.p2Country || session.p2_country,
                    createdLedger: session.createdLedger || session.created_ledger,
                    players: session.players || undefined,
                    isPublic: session.isPublic || session.is_public || false,
                    is_public: session.is_public || session.isPublic || false,
                  } as SessionEntry;
                }
                knownActiveSessionIdsRef.current.delete(checkId);
                return null;
              })
              .catch(() => { knownActiveSessionIdsRef.current.delete(checkId); return null; })
          );
          const batchResults = await Promise.all(batchPromises);
          batchResults.forEach(r => { if (r) sessions.push(r); });
          if (maxSessionIdSeenRef.current !== 0 && i + batchSize < idsArray.length) {
            await new Promise(resolve => setTimeout(resolve, 50));
          }
        }
      }

      // ── Persist state to localStorage for next page load ──────────────────
      if (sessions.length > 0 || usedBackendEndpoint) {
        // Only overwrite cache when we got a real response (not a timeout/error)
        try {
          localStorage.setItem(LS_MAX_KEY, String(maxSessionIdSeenRef.current));
          localStorage.setItem(LS_IDS_KEY, JSON.stringify(sessions.map(s => s.sessionId)));
          localStorage.setItem(LS_SESSIONS_KEY, JSON.stringify(sessions));
        } catch { /* ignore quota errors */ }
      }

      setActiveSessions(sessions);
      // console.log(`[App] Active sessions: ${sessions.length} (checked ${sessionIdsArray.length} IDs)`);
      
      // If user's current session is no longer in active list, check if it's ended
      if (userCurrentSession) {
        const currentSessionInList = sessions.find(s => s.sessionId === userCurrentSession);
        if (!currentSessionInList) {
          // Session is no longer in active list (ended or doesn't exist)
          // Double-check by querying contract
          try {
            if (readOnlyClient) {
              const contractSession = await readOnlyClient.getSession(userCurrentSession);
              if (!contractSession || contractSession.state === 'Ended') {
                // console.log('[App] User session', userCurrentSession, 'is ended, clearing');
                setUserCurrentSession(null);
                setSessionLink('');
                // Mark as ended so we don't re-add it
                endedSessionsRef.current.add(userCurrentSession);
              }
            }
          } catch (error) {
            // Session doesn't exist, clear it
// log('[App] User session', userCurrentSession, 'not found, clearing');
            setUserCurrentSession(null);
            setSessionLink('');
            // Mark as ended so we don't re-add it
            endedSessionsRef.current.add(userCurrentSession);
          }
        } else if (endedSessionsRef.current.has(userCurrentSession)) {
          // User explicitly ended this session, don't keep it as current
          // console.log('[App] User session', userCurrentSession, 'was explicitly ended, clearing');
          setUserCurrentSession(null);
          setSessionLink('');
        }
      }
      
      // Update markers on map from active sessions (even without wallet).
      // Use ref so this call doesn't force fetchActiveSessions to re-create.
      if (map.current && map.current.loaded()) {
        updateSessionMarkersRef.current(sessions);
      }
    } catch (error) {
      // console.error('[App] Failed to fetch active sessions:', error);
    } finally {
      isFetchingSessionsRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnlyClient, userCurrentSession]); // updateSessionMarkers intentionally omitted — accessed via ref to prevent infinite loop

  // Initialize read-only client on mount (for fetching public data without wallet)
  useEffect(() => {
    const client = new ReadOnlyContractClient();
    setReadOnlyClient(client);
    
    // Fetch country data from chain on mount (no wallet required — read-only)
    const loadData = async () => {
      try {
        // Load allowed countries from V2 list_allowed_countries(page, pageSize)
        // Fetch up to 200 countries in one call; all registered countries are expected to fit
        const listResult = await client.listAllowedCountries(0, 200);
        if (Array.isArray(listResult) && listResult.length > 0) {
          setAllowedCountries(new Set(listResult));
        }
      } catch {
        // Silently ignore — map will start with no countries highlighted
      }

      countryDataLoadedRef.current = true;
      setCountryDataLoaded(true);
    };
    
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount. updateCountryOverlay will be called by the useEffect that watches state changes

  // Fetch active sessions when readOnlyClient is ready
  useEffect(() => {
    if (readOnlyClient) {
      fetchActiveSessions();
      
      // Poll every 60 seconds (reduced from 30 to minimize backend load)
      const sessionInterval = setInterval(() => {
        fetchActiveSessions();
      }, 60000);
      
      return () => clearInterval(sessionInterval);
    }
  }, [readOnlyClient, fetchActiveSessions]);

  // Note: GeoLink doesn't have a sessions endpoint, so we handle sessions entirely on-chain

  // Periodic location update to GeoLink when location is available
  // NEW: Only calls GeoLink API, no direct contract calls
  // Contract anchoring happens via anchor_events returned by GeoLink
  useEffect(() => {
    if (!playerLocation || !walletAddress || !contractClient) return;
    
    // In-memory cache for anchored event IDs (client-side deduplication)
    const anchoredEventsCache = new Map<string, number>(); // event_id -> timestamp
    const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour
    
    const updateLocationToGeoLink = async () => {
      try {
        const response = await geolinkApi.updateLocation({
          public_key: walletAddress,
          blockchain: 'stellar',
          latitude: playerLocation[1],
          longitude: playerLocation[0],
          description: 'GeoTrust Player',
        });
        
        // console.log('[App] Periodic location update to GeoLink');
        
        // Process anchor_events from GeoLink response (if any)
        if (response && response.anchor_events && Array.isArray(response.anchor_events) && response.anchor_events.length > 0) {
          // console.log(`[App] Processing ${response.anchor_events.length} anchor event(s) from GeoLink`);
          
          // Clean old cache entries (older than TTL)
          const now = Date.now();
          // Use Array.from to convert iterator to array for compatibility
          Array.from(anchoredEventsCache.entries()).forEach(([eventId, timestamp]) => {
            if (now - timestamp > CACHE_TTL_MS) {
              anchoredEventsCache.delete(eventId);
            }
          });
          
          // Process each anchor event
          for (const anchorEvent of response.anchor_events) {
            const { event_id, event_type, occurred_at, cell_id, prev_cell_id, rule_id, commitment } = anchorEvent;
            
            // Deduplication check
            if (anchoredEventsCache.has(event_id)) {
              // console.log(`[App] Event ${event_id} already anchored (cached), skipping`);
              continue;
            }
            
            try {
              // Convert parameters for contract call
              const eventIdBytes = Buffer.from(event_id, 'utf8');
              
              // Convert GeoLink's cell_id (string format like "34.230000_-118.232000") to numeric
              const numericCellId = parseGeoLinkCellId(cell_id || '0');
              const cellIdBigInt = BigInt(numericCellId);
              
              // Convert prev_cell_id if present
              const prevCellIdBigInt = prev_cell_id ? BigInt(parseGeoLinkCellId(prev_cell_id)) : null;
              
              const occurredAtBigInt = BigInt(Math.floor(new Date(occurred_at).getTime() / 1000));
              
              // Convert commitment to 32-byte buffer
              let commitmentBytes = Buffer.alloc(32, 0); // Default to zero hash
              if (commitment) {
                try {
                  if (commitment.startsWith('0x') || /^[0-9a-fA-F]+$/.test(commitment)) {
                    const hex = commitment.startsWith('0x') ? commitment.slice(2) : commitment;
                    commitmentBytes = Buffer.from(hex, 'hex');
                  } else {
                    commitmentBytes = Buffer.from(commitment, 'base64');
                  }
                  if (commitmentBytes.length !== 32) {
                    const padded = Buffer.alloc(32, 0);
                    commitmentBytes.copy(padded, 0, 0, Math.min(32, commitmentBytes.length));
                    commitmentBytes = padded;
                  }
                } catch (error) {
                  // console.warn('[App] Invalid commitment format, using zero hash:', error);
                }
              }
              
              // Call contract anchor_event
              const result = await contractClient.anchorEvent(
                walletAddress,
                eventIdBytes,
                event_type,
                cellIdBigInt,
                occurredAtBigInt,
                prevCellIdBigInt,
                rule_id || null,
                commitmentBytes,
              );
              
              // Mark as anchored in cache
              anchoredEventsCache.set(event_id, Date.now());
              
              // Update UI state with last anchor info
              setLastAnchorEvent({
                eventId: event_id,
                eventType: event_type,
                occurredAt: occurred_at,
                txHash: result.txHash || result.hash,
                cellId: cell_id,
              });
              
              // console.log(`[App] ✅ Anchored event ${event_id} (${event_type}), tx: ${result.txHash || result.hash}`);
            } catch (error: any) {
              // console.error(`[App] ❌ Failed to anchor event ${event_id}:`, error);
              // Don't cache failed events (allow retry)
            }
          }
        } else {
          // No anchor events - this is normal for most location updates
          // console.log('[App] No anchor events in GeoLink response (normal)');
        }
        
        // Update cell_id display if provided
        if (response && response.cell_id) {
          // Could update UI with current cell_id if needed
        }
      } catch (error) {
        // console.warn('[App] Failed periodic location update:', error);
      }
    };
    
    // Update immediately, then every 10 seconds
    // NOTE: This only calls GeoLink API, no direct contract calls
    updateLocationToGeoLink();
    const interval = setInterval(updateLocationToGeoLink, 10000);
    
    return () => clearInterval(interval);
  }, [playerLocation, walletAddress, contractClient]);

  // Import passkey service
  const passkeyService = useMemo(() => {
    try {
      const { passkeyService: service } = require('./services/passkeyService');
      return service;
    } catch (error) {
      // console.warn('[App] Passkey service not available:', error);
      return null;
    }
  }, []);

  // Check if passkey is available
  const checkPasskeyAvailable = useCallback(async (): Promise<boolean> => {
    if (!passkeyService) return false;
    
    try {
      // Check if passkey is enabled
      if (passkeyService.isPasskeyEnabled(walletAddress || undefined)) {
        const passkeyData = await passkeyService.getStoredPasskeyData(walletAddress || undefined);
        return passkeyData !== null;
      }
      return false;
    } catch (error) {
      // console.warn('[App] Passkey availability check failed:', error);
      return false;
    }
  }, [passkeyService, walletAddress]);

  // Handle deposit approval - supports both wallet signing (default) and passkey (optional)
  const handleApproveDeposit = useCallback(async (deposit: PendingDepositAction) => {
    if (!wallet || !walletAddress || !contractClient) {
      setNotificationState({
        isOpen: true,
        title: 'Error',
        message: 'Please connect your wallet first',
        type: 'error',
        autoClose: 5000,
      });
      return;
    }

    try {
      // Check if passkey is available - if so, use WebAuthn method
      // Otherwise, use wallet signing (simpler, no setup required)
      const hasPasskey = await checkPasskeyAvailable();
      
      if (hasPasskey && passkeyService) {
        // Use passkey-based deposit execution (WebAuthn method)
        try {
          setNotificationState({
            isOpen: true,
            title: 'Executing Deposit',
            message: 'Preparing deposit with passkey...',
            type: 'info',
            autoClose: 0,
          });

          // Get passkey credential for THIS wallet address
          if (!walletAddress) {
            throw new Error('Wallet address required for passkey authentication');
          }
          const passkeyData = await passkeyService.getStoredPasskeyData(walletAddress);
          if (!passkeyData) {
            throw new Error(`No passkey found for wallet ${walletAddress.substring(0, 8)}... Please set up a passkey for this wallet first.`);
          }
          
          // Verify the passkey is registered on contract for this wallet
          const readOnlyClient = new ReadOnlyContractClient();
          const isPasskeyRegistered = await readOnlyClient.isPasskeyRegistered(walletAddress);
          if (!isPasskeyRegistered) {
            throw new Error('Passkey is not registered on the contract for this wallet. Please register it first.');
          }

          // Create GeoLink deposit service
          const { GeoLinkDepositService } = await import('./services/geoLinkDepositService');
          const depositService = new GeoLinkDepositService();

          // Create ContractCallIntent
          const network = process.env.REACT_APP_STELLAR_NETWORK === 'mainnet' ? 'mainnet' : 'testnet';
          const intent = depositService.createContractCallIntent(deposit, network);

          // Encode intent to bytes
          const intentBytes = depositService.encodeIntentToBytes(intent);
          const signaturePayload = btoa(Array.from(intentBytes, byte => String.fromCharCode(byte)).join(''));

          // Generate WebAuthn challenge
          const challenge = await depositService.generateChallenge(intentBytes);

          // Authenticate with passkey
          setNotificationState({
            isOpen: true,
            title: 'Authenticate',
            message: 'Please authenticate with your passkey...',
            type: 'info',
            autoClose: 0,
          });

          const authResult = await passkeyService.authenticatePasskey(passkeyData.id, challenge);

          // Get passkey public key SPKI
          const passkeyPublicKeySPKI = await passkeyService.getPasskeyPublicKeySPKI(passkeyData.id, walletAddress);

          // Note: For passkey method, we need the user's secret key
          // In xyz-wallet, this is decrypted from encrypted storage using passkey
          // For GeoTrust, we'll get it from the wallet if available
          // This is a simplified version - full implementation would decrypt from encrypted storage
          let userSecretKey = '';
          try {
            // Try to get secret key from wallet (if available)
            // Note: This may not work with all wallet types
            if (wallet && typeof (wallet as any).getSecretKey === 'function') {
              userSecretKey = await (wallet as any).getSecretKey();
            }
          } catch (error) {
            // console.warn('[App] Could not get secret key from wallet:', error);
          }

          if (!userSecretKey) {
            // Fall back to wallet signing if we can't get secret key
            // console.log('[App] Secret key not available, falling back to wallet signing');
            throw new Error('Secret key required for passkey deposit execution');
          }

          // Execute deposit via GeoLink
          setNotificationState({
            isOpen: true,
            title: 'Executing Deposit',
            message: 'Submitting deposit transaction...',
            type: 'info',
            autoClose: 0,
          });

          const result = await depositService.executeDeposit(
            deposit.id,
            walletAddress,
            userSecretKey,
            passkeyPublicKeySPKI,
            authResult.signature,
            authResult.authenticatorData,
            authResult.clientDataJSON,
            signaturePayload
          );

          if (result.success) {
            setNotificationState({
              isOpen: true,
              title: 'Success',
              message: `Deposit executed successfully!${result.result?.txHash || result.result?.transaction_hash ? ` Transaction: ${(result.result?.txHash || result.result?.transaction_hash || '').slice(0, 8)}...` : ''}`,
              type: 'success',
              autoClose: 5000,
            });

            // Refresh pending deposits
            const { pending_deposits: updatedDeposits } = await geolinkApi.getPendingDeposits(walletAddress);
            setPendingDeposits(updatedDeposits);
            return;
          } else {
            throw new Error(result.error || 'Deposit execution failed');
          }
        } catch (error: any) {
          // console.error('[App] Passkey deposit execution failed:', error);
          // Fall through to wallet signing as fallback
          // console.log('[App] Falling back to wallet signing');
        }
      }

      // Use wallet signing method (default)
      setNotificationState({
        isOpen: true,
        title: 'Executing Deposit',
        message: 'Preparing deposit transaction...',
        type: 'info',
        autoClose: 0, // Don't auto-close
      });

      // Get deposit parameters
      const amount = deposit.parameters.amount || '0';
      const asset = deposit.parameters.asset || 'XLM';
      const userAddress = deposit.parameters.user_address || walletAddress;

      // Convert amount from stroops to XLM (if XLM)
      let amountInStroops: string;
      if (asset === 'XLM' || asset.includes('XLM')) {
        const amountNum = parseFloat(amount);
        amountInStroops = Math.floor(amountNum * 10000000).toString();
      } else {
        amountInStroops = amount;
      }

      // Call the deposit function on the contract directly
      // The contract address is in deposit.contract_address
      const depositContract = new (await import('@stellar/stellar-sdk')).Contract(deposit.contract_address);
      const publicKey = await wallet.getPublicKey();
      
      // Get account for sequence
      const rpcUrl = process.env.REACT_APP_SOROBAN_RPC_URL || 'https://soroban-testnet.stellar.org';
      const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const finalRpcUrl = isDevelopment 
        ? `${window.location.protocol}//${window.location.hostname}:8080/api/soroban-rpc`
        : rpcUrl;
      // Allow HTTP for localhost development
      const rpcOptions: any = {};
      if (isDevelopment && finalRpcUrl.startsWith('http://')) {
        rpcOptions.allowHttp = true;
      }
      const rpc = new (await import('@stellar/stellar-sdk')).rpc.Server(finalRpcUrl, rpcOptions);
      
      // Get account for sequence - handle account not found error
      let sourceAccount;
      try {
        sourceAccount = await rpc.getAccount(publicKey);
      } catch (error: any) {
        if (error.message && error.message.includes('Account not found')) {
          throw new Error('Account not found. Please ensure your account is funded on the Stellar network.');
        }
        throw error;
      }

      // Convert parameters to ScVal
      const { Address, nativeToScVal, TransactionBuilder, BASE_FEE, Networks, xdr } = await import('@stellar/stellar-sdk');
      const userAddressScVal = new Address(userAddress).toScVal();
      
      // Handle native XLM vs other assets
      let assetScVal;
      if (asset === 'XLM' || asset === 'native' || !asset || asset.trim() === '') {
        // Native XLM - use void ScVal or special handling
        assetScVal = xdr.ScVal.scvVoid();
      } else {
        // Other asset - treat as contract address
        try {
          assetScVal = new Address(asset).toScVal();
        } catch (error) {
          // If it's not a valid address, try as native XLM
          // console.warn('[App] Asset is not a valid address, treating as native XLM:', asset);
          assetScVal = xdr.ScVal.scvVoid();
        }
      }
      
      // Convert amount to i128 using nativeToScVal
      // nativeToScVal can handle numbers and will convert to appropriate ScVal type
      const amountNum = parseInt(amountInStroops, 10);
      const amountScVal = nativeToScVal(amountNum, { type: 'i128' });

      // Build transaction
      const transaction = new TransactionBuilder(sourceAccount, {
        fee: BASE_FEE,
        networkPassphrase: Networks.TESTNET,
      })
        .addOperation(depositContract.call('deposit', userAddressScVal, assetScVal, amountScVal))
        .setTimeout(300)
        .build();

      // Simulate first
      const simResponse = await rpc.simulateTransaction(transaction);
      if ('error' in simResponse) {
        throw new Error(`Simulation failed: ${JSON.stringify(simResponse.error)}`);
      }

      // Prepare transaction
      const prepared = await rpc.prepareTransaction(transaction);
      const xdrString = prepared.toXDR();

      // Sign with wallet
      setNotificationState({
        isOpen: true,
        title: 'Sign Transaction',
        message: 'Please approve the transaction in your wallet...',
        type: 'info',
        autoClose: 0,
      });

      const signedXdr = await wallet.signTransaction(xdrString, Networks.TESTNET);
      const signedTx = TransactionBuilder.fromXDR(signedXdr, Networks.TESTNET);

      // Send transaction
      setNotificationState({
        isOpen: true,
        title: 'Sending Transaction',
        message: 'Submitting deposit transaction...',
        type: 'info',
        autoClose: 0,
      });

      const sendResponse = await rpc.sendTransaction(signedTx);
      
      if (sendResponse.status === 'ERROR') {
        throw new Error(`Transaction failed: ${JSON.stringify(sendResponse.errorResult)}`);
      }

      // Wait for transaction to be included and get hash/ledger
      let transactionHash: string | undefined;
      let ledger: number | undefined;
      
      if (sendResponse.hash) {
        transactionHash = sendResponse.hash;
        // Wait a bit for transaction to be included, then get ledger
        await new Promise(resolve => setTimeout(resolve, 2000));
        try {
          const txResult = await rpc.getTransaction(sendResponse.hash);
          if (txResult && 'ledger' in txResult) {
            ledger = txResult.ledger;
          } else if (txResult && typeof txResult === 'object' && 'ledgerSeq' in txResult) {
            ledger = (txResult as any).ledgerSeq;
          }
        } catch (error) {
          // console.warn('[App] Could not get transaction ledger, using latest ledger');
          const latestLedger = await rpc.getLatestLedger();
          ledger = latestLedger.sequence;
        }
      }

      // Report completion to GeoLink
      if (transactionHash && ledger) {
        try {
          const { GeoLinkDepositService } = await import('./services/geoLinkDepositService');
          const depositService = new GeoLinkDepositService();
          await depositService.reportDepositCompletion(deposit.id, walletAddress, transactionHash, ledger);
        } catch (reportError) {
          // console.warn('[App] Failed to report deposit completion to GeoLink:', reportError);
          // Don't fail the whole operation if reporting fails
        }
      }

      setNotificationState({
        isOpen: true,
        title: 'Success',
        message: `Deposit executed successfully!${transactionHash ? ` Transaction: ${transactionHash.slice(0, 8)}...` : ''}`,
        type: 'success',
        autoClose: 5000,
      });

      // Refresh pending deposits
      const { pending_deposits: updatedDeposits } = await geolinkApi.getPendingDeposits(walletAddress);
      setPendingDeposits(updatedDeposits);
    } catch (error: any) {
      // console.error('[App] Failed to approve deposit:', error);
      let errorMessage = error.message || 'Unknown error';
      
      // Provide more helpful error messages
      if (errorMessage.includes('Account not found')) {
        errorMessage = 'Account not found. Please ensure your account is funded on the Stellar network.';
      } else if (errorMessage.includes('Proxy request failed') || errorMessage.includes('500')) {
        errorMessage = 'Backend server error. Please check if the backend is running on port 8080.';
      } else if (errorMessage.includes('Secret key required')) {
        errorMessage = 'Passkey authentication requires secret key. Falling back to wallet signing.';
      }
      
      setNotificationState({
        isOpen: true,
        title: 'Error',
        message: `Failed to execute deposit: ${errorMessage}`,
        type: 'error',
        autoClose: 5000,
      });
    }
  }, [wallet, walletAddress, contractClient, checkPasskeyAvailable, passkeyService]);

  // Handle deposit decline
  const handleDeclineDeposit = useCallback(async (deposit: PendingDepositAction) => {
    if (!walletAddress) return;

    try {
      await geolinkApi.cancelPendingDeposit(deposit.id, walletAddress);
      
      setNotificationState({
        isOpen: true,
        title: 'Deposit Cancelled',
        message: `Deposit for ${deposit.contract_name} has been cancelled`,
        type: 'info',
        autoClose: 3000,
      });

      // Refresh pending deposits
      const { pending_deposits: updatedDeposits } = await geolinkApi.getPendingDeposits(walletAddress);
      setPendingDeposits(updatedDeposits);
    } catch (error: any) {
      // console.error('[App] Failed to decline deposit:', error);
      setNotificationState({
        isOpen: true,
        title: 'Error',
        message: `Failed to cancel deposit: ${error.message || 'Unknown error'}`,
        type: 'error',
        autoClose: 5000,
      });
    }
  }, [walletAddress]);

  // Poll for pending deposits every 30 seconds
  useEffect(() => {
    if (!walletAddress) {
      setPendingDeposits([]);
      return;
    }

    const fetchPendingDeposits = async () => {
      try {
        const { pending_deposits: rawDeposits } = await geolinkApi.getPendingDeposits(walletAddress);

        // Dedup by (rule_id, matched_public_key) — GeoLink guarantees one entry per
        // pair after their server-side fix, but we defend against older behaviour.
        // When duplicates exist we keep the most recently received one.
        const byPair = new Map<string, typeof rawDeposits[0]>();
        for (const dep of rawDeposits) {
          const key = `${dep.rule_id}_${dep.matched_public_key}`;
          const prev = byPair.get(key);
          if (!prev || new Date(dep.received_at || 0) >= new Date(prev.received_at || 0)) {
            byPair.set(key, dep);
          }
        }
        const deposits = Array.from(byPair.values());

        setPendingDeposits(deposits);
        
        // Auto-execute deposits if auto_execute is enabled AND user has passkey
        // Disable auto-execution for now - user must manually approve deposits
        // This prevents unwanted transaction prompts
        /*
        for (const deposit of deposits) {
          if (deposit.status === 'pending') {
            // Check if contract has auto_execute enabled
            const contract = nearbyContracts.find(c => c.id === deposit.contract_id);
            if (contract?.auto_execute) {
              const hasPasskey = await checkPasskeyAvailable();
              // Only auto-execute if passkey is available (for security)
              if (hasPasskey) {
                // console.log('[App] Auto-executing deposit with passkey:', deposit.id);
                try {
                  await handleApproveDeposit(deposit);
                } catch (error) {
                  // console.error('[App] Failed to auto-execute deposit:', error);
                }
              }
            }
          }
        }
        */
      } catch (error) {
        // console.warn('[App] Failed to fetch pending deposits:', error);
      }
    };

    // Fetch immediately, then every 30 seconds
    fetchPendingDeposits();
    const interval = setInterval(fetchPendingDeposits, 30000);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletAddress, nearbyContracts, handleApproveDeposit, checkPasskeyAvailable]);

  // Background poll for GeoLink pending count — keeps the hamburger badge accurate
  // even when the dashboard is closed.
  useEffect(() => {
    if (!walletAddress) {
      setPendingGeoLinkCount(0);
      return;
    }

    let cancelled = false;
    let staleCleaned = false;

    const fetchCount = async () => {
      try {
        // On the first run, purge duplicate pending records accumulated by the
        // old action-ID-based dedup bug (keeps only the newest per rule+wallet pair).
        if (!staleCleaned) {
          staleCleaned = true;
          try {
            await fetch('/api/v2/geolink/pending-deposits/stale', { method: 'DELETE' });
          } catch { /* non-fatal */ }
        }

        const items = await listPendingDeposits({ walletPublicKey: walletAddress });
        if (!cancelled) {
          // Deduplicate by (ruleId, walletPublicKey) — only count the most recent
          // pending entry per logical rule+wallet pair (defensive against server restarts
          // before the stale-cleanup endpoint runs).
          const seen = new Set<string>();
          let count = 0;
          for (const item of items) {
            if (item.geoTrustDecision !== 'pending') continue;
            const key = `${item.ruleId}::${item.walletPublicKey}`;
            if (!seen.has(key)) { seen.add(key); count++; }
          }
          setPendingGeoLinkCount(count);
        }
      } catch {
        // non-fatal; keep last known count
      }
    };

    fetchCount();
    const id = setInterval(fetchCount, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [walletAddress]);

  // Calculate distance between two coordinates (Haversine formula)
  const calculateDistance = useCallback((lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;
    
    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    
    return R * c; // Distance in meters
  }, []);

  const updateOtherUserMarkers = useCallback((users: Array<{ id: string; location: [number, number]; distance: number }>) => {
    if (!map.current) return;

    let usersForMarkers = users;
    if (applyTrustGroupMapFilter) {
      if (trustGroupMapMemberSet.size === 0) usersForMarkers = [];
      else {
        usersForMarkers = users.filter((u) => trustGroupMapMemberSet.has(stellarAddrKey(u.id)));
      }
    }

    const trustFilterKey = applyTrustGroupMapFilter
      ? `1:${Array.from(trustGroupMapMemberSet).sort().join(',')}`
      : '0';

    // Create a hash of user data to detect actual changes
    const usersHash = JSON.stringify({
      tf: trustFilterKey,
      users: usersForMarkers.map(u => ({ id: u.id, location: u.location })).sort((a, b) => a.id.localeCompare(b.id)),
    });
    
    // Skip if data hasn't changed
    if (previousUserMarkersRef.current === usersHash && showOtherUsers) {
      return;
    }
    previousUserMarkersRef.current = usersHash;

    if (!showOtherUsers) {
      // Only clear if we're hiding users
      document.querySelectorAll('.other-user-marker').forEach(m => m.remove());
      if (map.current && map.current.isStyleLoaded()) {
        userCirclesRef.current.forEach((sourceId) => {
          const layerId = `${sourceId}-layer`;
          if (map.current!.getLayer(layerId)) {
            map.current!.removeLayer(layerId);
          }
          if (map.current!.getSource(sourceId)) {
            map.current!.removeSource(sourceId);
          }
        });
      }
      userCirclesRef.current = [];
      return;
    }

    // Batch marker updates to prevent flickering - clear and re-add in one operation
    // Remove existing markers
    document.querySelectorAll('.other-user-marker').forEach(m => m.remove());
    
    // Clear existing user radius circles
    if (map.current && map.current.isStyleLoaded()) {
      userCirclesRef.current.forEach((sourceId) => {
        const layerId = `${sourceId}-layer`;
        if (map.current!.getLayer(layerId)) {
          map.current!.removeLayer(layerId);
        }
        if (map.current!.getSource(sourceId)) {
          map.current!.removeSource(sourceId);
        }
      });
    }
    userCirclesRef.current = [];

    // Ensure map is loaded and container is ready before adding markers
    const addMarkers = () => {
      if (!map.current) return;
      
      // Check if map container exists and is in DOM
      const container = map.current.getContainer();
      if (!container || !container.parentElement) {
        // console.warn('Map container not ready, retrying...');
        setTimeout(addMarkers, 100);
        return;
      }

      if (!map.current.loaded()) {
        map.current.once('load', addMarkers);
        return;
      }

      const compactMarkers = applyTrustGroupMapFilter && trustGroupMapMemberSet.size > 0;
      usersForMarkers.forEach(user => {
        if (!mapFilters.showUsers) return; // Skip if users filter is off

        // A user counts as "live" when their wallet is in the active
        // broadcasters feed.  Read the ref (not state) so we don't
        // invalidate the `updateOtherUserMarkers` useCallback — the
        // `liveMarkerFeed` sync effect below also patches the class
        // on already-rendered markers whenever the set changes, so
        // this is only the initial paint.
        const pk = (user as any).publicKey as string | undefined;
        const isLive = !!(pk && liveBroadcasterWalletsRef.current.has(pk));

        const el = document.createElement('div');
        el.className = 'other-user-marker' + (isLive ? ' live' : '');
        const dot = compactMarkers ? 16 : 24;
        const border = compactMarkers ? '2px' : '3px';
        el.innerHTML = compactMarkers
          ? `<div class="marker-distance" style="position:absolute;top:-14px;left:50%;transform:translateX(-50%);opacity:0.95;font-size:8px;padding:1px 4px;line-height:1.1;background:rgba(0,0,0,0.78);color:#fff;border-radius:4px;white-space:nowrap;pointer-events:none">${user.distance.toFixed(1)}km</div><span class="live-label">LIVE</span>`
          : `<div class="marker-distance">${user.distance.toFixed(1)} km</div><span class="live-label">LIVE</span>`;
        // `data-public-key` lets the liveMarkerFeed sync effect flip
        // `.live` on/off without rebuilding markers when a broadcast
        // starts or ends while the viewer is on the map.
        if (pk) el.setAttribute('data-public-key', pk);
        // Inline dimensions are required so Mapbox can compute the -50%,-50% centering
        // offset correctly. Relying solely on a CSS class causes the browser to report
        // 0x0 at mount time, which makes the marker appear at the wrong coordinate.
        el.style.cssText = `
          width: ${dot}px;
          height: ${dot}px;
          border-radius: 50%;
          border: ${border} solid white;
          background: ${compactMarkers ? '#6366f1' : '#7C3AED'};
          box-shadow: 0 2px 8px rgba(0,0,0,0.35);
          cursor: pointer;
          pointer-events: auto;
          position: relative;
        `;
        attachMarkerTap(el, {
          onTap: () => {
            const pk = (user as any).publicKey as string | undefined;
            if (pk) {
              // Wallet marker → open the profile directly and skip
              // the legacy popup so the broadcaster's minimised pill
              // doesn't get clobbered for a brief frame.
              openWalletProfileFromMarker(pk);
              return;
            }
            setSelectedMarker({
              type: 'opponent',
              location: user.location,
              userId: user.id,
              distance: user.distance,
              publicKey: pk,
            });
          },
        });
        
        try {
          const marker = new mapboxgl.Marker({ element: el })
            .setLngLat(user.location)
            .addTo(map.current!);
          
          marker.getElement().setAttribute('data-user-id', user.id);
          
          // Add hover event listeners to show radius circle
          el.addEventListener('mouseenter', () => {
            const radiusMeters = maxDistance * 1000; // Convert km to meters
            showRadiusCircle(user.location, radiusMeters);
          });
          
          el.addEventListener('mouseleave', () => {
            hideRadiusCircle();
          });
        } catch (error) {
          // console.error('[App] Failed to add other-user marker:', error);
        }
      });
    };

    addMarkers();
    // `openWalletProfileFromMarker` is declared later in the file with
    // `useCallback([])` and only reads refs, so it's referentially stable
    // across renders — excluding it from the dep list avoids a forward-
    // reference TS error without risking a stale closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOtherUsers, mapFilters.showUsers, maxDistance, showRadiusCircle, hideRadiusCircle, applyTrustGroupMapFilter, trustGroupMapMemberSet]);

  // Render NFT markers on the map (matching xyz-wallet pattern)
  const renderNFTMarkers = useCallback(() => {
    if (!map.current) return;
    
    // console.log('[App] renderNFTMarkers called with', nearbyNFTs.length, 'NFTs');
    
    // Ensure map is fully loaded and style is loaded before adding markers
    if (!map.current.loaded() || !map.current.isStyleLoaded()) {
      // console.log('[App] renderNFTMarkers: Map not fully ready, waiting...', {
      //   loaded: map.current.loaded(),
      //   styleLoaded: map.current.isStyleLoaded()
      // });
      if (!map.current.loaded()) {
        map.current.once('load', renderNFTMarkers);
      } else {
        map.current.once('style.load', renderNFTMarkers);
      }
      return;
    }
    
    // Clear existing NFT markers
    nftMarkersRef.current.forEach(marker => marker.remove());
    nftMarkersRef.current = [];
    
    // Clear existing NFT radius circles
    if (map.current.isStyleLoaded()) {
      nftCirclesRef.current.forEach((sourceId) => {
        const layerId = `${sourceId}-layer`;
        if (map.current!.getLayer(layerId)) {
          map.current!.removeLayer(layerId);
        }
        if (map.current!.getSource(sourceId)) {
          map.current!.removeSource(sourceId);
        }
      });
    }
    nftCirclesRef.current = [];
    
    // console.log('[App] Adding', nearbyNFTs.length, 'NFT markers to map');
    
    // Add markers for nearby NFTs
    nearbyNFTs.forEach((nft) => {
      if (!mapFilters.showNFTs) return; // Skip if NFTs filter is off
      if (nft.latitude && nft.longitude) {
        // Construct image URL using the helper function (matching xyz-wallet)
        const imageUrl = constructImageUrl(nft.server_url, nft.ipfs_hash) || nft.image_url || 'https://via.placeholder.com/64x64?text=NFT';
        
        const el = document.createElement('div');
        el.className = 'nft-marker';
        // Start at 32px (same as contract markers), grows with zoom via zoomend handler
        const initNftSize = map.current ? (map.current.getZoom() >= 14 ? 64 : map.current.getZoom() >= 10 ? 40 : 32) : 32;
        el.style.cssText = `
          width: ${initNftSize}px;
          height: ${initNftSize}px;
          background-image: url('${imageUrl}');
          background-size: cover;
          background-repeat: no-repeat;
          background-position: center;
          border-radius: 8px;
          border: 2px solid #FFD700;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(255, 215, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          position: absolute;
          transform: translate(-50%, -50%);
          pointer-events: auto;
          transition: width 0.2s, height 0.2s;
        `;
        // Add fallback if image fails to load
        const img = new Image();
        img.onerror = () => {
          el.style.backgroundImage = 'url(https://via.placeholder.com/64x64?text=NFT)';
          el.style.backgroundSize = 'cover';
        };
        img.src = imageUrl;
        el.title = nft.collection_name || nft.name || 'NFT';
        
        try {
          const nftMarker = new mapboxgl.Marker({ element: el })
            .setLngLat([nft.longitude, nft.latitude])
            .addTo(map.current!);
          
          // Click handling: single click opens profile (after 300ms delay), double click zooms
          let nftLastClick = 0;
          let nftSingleTimer: ReturnType<typeof setTimeout> | null = null;
          el.addEventListener('click', (e) => {
            e.stopPropagation();
            const now = Date.now();
            const delta = now - nftLastClick;
            nftLastClick = now;
            if (delta < 300) {
              // Double-click — cancel pending single-click, zoom instead
              if (nftSingleTimer) { clearTimeout(nftSingleTimer); nftSingleTimer = null; }
              nftLastClick = 0;
              const currentZoom = map.current?.getZoom() ?? 0;
              if (map.current) flyToWithMode(map.current, [nft.longitude, nft.latitude], currentZoom < 15);
            } else {
              // Possible single click — wait to confirm no second click is coming
              if (nftSingleTimer) clearTimeout(nftSingleTimer);
              nftSingleTimer = setTimeout(() => {
                nftSingleTimer = null;
                setSelectedMarker({ type: 'nft', location: [nft.longitude, nft.latitude], nft });
              }, 300);
            }
          });
          el.addEventListener('dblclick', (e) => { e.stopPropagation(); e.preventDefault(); });
          
          nftMarkersRef.current.push(nftMarker);
          // console.log('[App] Added NFT marker at', [nft.longitude, nft.latitude]);
          
          // Add hover event listeners to show radius circle (after marker is created)
          // Use the marker's element to ensure events work properly
          const markerElement = nftMarker.getElement();
          markerElement.addEventListener('mouseenter', () => {
            // console.log('[App] NFT marker hovered');
            // NFTs don't have a radius field, so use a reasonable default (2km = 2000m)
            // This is a typical interaction radius for NFTs
            const radiusMeters = 2000; // 2km default radius for NFTs
            // Ensure coordinates are numbers, not strings
            const lng = typeof nft.longitude === 'number' ? nft.longitude : parseFloat(String(nft.longitude || '0'));
            const lat = typeof nft.latitude === 'number' ? nft.latitude : parseFloat(String(nft.latitude || '0'));
            if (!isNaN(lng) && !isNaN(lat)) {
              showRadiusCircle([lng, lat], radiusMeters);
            } else {
              // console.warn('[App] Invalid NFT coordinates for radius:', { lng, lat, nft });
            }
          });
          
          markerElement.addEventListener('mouseleave', () => {
            // console.log('[App] NFT marker unhovered');
            hideRadiusCircle();
          });
        } catch (error) {
          // console.error('[App] Failed to add NFT marker:', error, nft);
        }
      }
    });
  }, [nearbyNFTs, mapFilters.showNFTs, showRadiusCircle, hideRadiusCircle]);

  // Update NFT markers when nearbyNFTs changes
  useEffect(() => {
    // Use a small timeout to batch updates and prevent flickering
    const timeoutId = setTimeout(() => {
      if (!map.current) {
        // console.log('[App] Map not ready for NFT markers yet');
        return;
      }
      
      if (nearbyNFTs.length > 0) {
        // console.log('[App] Rendering', nearbyNFTs.length, 'NFT markers');
        renderNFTMarkers();
      } else {
        // Clear markers when no NFTs
        nftMarkersRef.current.forEach(marker => marker.remove());
        nftMarkersRef.current = [];
      }
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [nearbyNFTs, renderNFTMarkers]);

  // Render contract markers on the map (matching xyz-wallet pattern)
  const renderContractMarkers = useCallback(() => {
    if (!map.current) {
      // console.log('[App] renderContractMarkers: map.current is null');
      return;
    }
    
    // console.log('[App] renderContractMarkers called with', nearbyContracts.length, 'contracts', {
    //   mapLoaded: map.current.loaded(),
    //   mapStyleLoaded: map.current.isStyleLoaded()
    // });
    
    // Clear any legacy DOM contract markers (pins are now a GeoJSON layer).
    contractMarkersRef.current.forEach(marker => marker.remove());
    contractMarkersRef.current = [];
    
    // Clear existing contract circles AND polygon layers
    if (map.current.isStyleLoaded()) {
      contractCirclesRef.current.forEach((sourceId) => {
        // Handle all layer naming patterns: -layer (hover circle), -fill and -border (geofence polygon)
        [`${sourceId}-layer`, `${sourceId}-fill`, `${sourceId}-border`].forEach(layerId => {
          if (map.current!.getLayer(layerId)) map.current!.removeLayer(layerId);
        });
        if (map.current!.getSource(sourceId)) map.current!.removeSource(sourceId);
      });
    }
    contractCirclesRef.current = [];
    
    // Ensure map is fully loaded and style is loaded before adding markers
    if (!map.current.loaded() || !map.current.isStyleLoaded()) {
      // console.log('[App] renderContractMarkers: Map not fully ready, waiting...', {
      //   loaded: map.current.loaded(),
      //   styleLoaded: map.current.isStyleLoaded()
      // });
      if (!map.current.loaded()) {
        map.current.once('load', renderContractMarkers);
      } else {
        map.current.once('style.load', renderContractMarkers);
      }
      return;
    }
    
    // Add markers for nearby contracts
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let _markersAdded = 0;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let _invalidCoords = 0;
    // console.log('[App] Processing', nearbyContracts.length, 'contracts for markers');
    
    // Visual style per rule_type — same abacus icon as dashboard, colour-coded background
    const RULE_COLOURS: Record<string, string> = {
      location:  '#7B68EE',
      proximity: '#4A90D9',
      geofence:  '#FF6B35',
    };
    const RULE_BORDERS: Record<string, string> = {
      location:  '#b8adff',
      proximity: '#7bb9f5',
      geofence:  '#ffaa87',
    };
    // Same square-pin gradient + shadow palette the dashboard map uses, so
    // the two maps render contract pins identically.
    const RULE_GRADIENTS: Record<string, string> = {
      location:  'linear-gradient(135deg,#7B68EE 0%,#9d8fff 100%)',
      proximity: 'linear-gradient(135deg,#4A90D9 0%,#6aaef0 100%)',
      geofence:  'linear-gradient(135deg,#FF6B35 0%,#ff8c60 100%)',
    };
    const RULE_SHADOWS: Record<string, string> = {
      location:  'rgba(123,104,238,0.5)',
      proximity: 'rgba(74,144,217,0.5)',
      geofence:  'rgba(255,107,53,0.5)',
    };

    nearbyContracts.forEach((contract, index) => {
      if (!mapFilters.showContracts) return;
      
      const lat = typeof contract.latitude === 'number' ? contract.latitude : parseFloat(String(contract.latitude || ''));
      const lng = typeof contract.longitude === 'number' ? contract.longitude : parseFloat(String(contract.longitude || ''));
      
      if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) return;

      try {
        const ruleType = (contract as any).rule_type || 'location';
        const colour = RULE_COLOURS[ruleType] || '#7B68EE';
        const boundaryGeo = (contract as any).boundary_geojson as { type: string; coordinates: any[] } | null | undefined;

        // ── Geofence polygon — always-visible filled polygon layer ──────────────
        if (ruleType === 'geofence' && boundaryGeo?.coordinates) {
          const sourceId = `contract-poly-${contract.id}-${index}`;
          try {
            if (!map.current!.getSource(sourceId)) {
              map.current!.addSource(sourceId, {
                type: 'geojson',
                data: { type: 'Feature', geometry: boundaryGeo as any, properties: { rule_id: contract.id } },
              });
              map.current!.addLayer({
                id: `${sourceId}-fill`,
                type: 'fill',
                source: sourceId,
                paint: { 'fill-color': colour, 'fill-opacity': 0.12 },
              });
              map.current!.addLayer({
                id: `${sourceId}-border`,
                type: 'line',
                source: sourceId,
                paint: { 'line-color': colour, 'line-width': 2, 'line-opacity': 0.85 },
              });
              contractCirclesRef.current.push(sourceId);
            }
          } catch { /* map style not ready yet — skip polygon, pin still added below */ }
        }

        // ── Square pin marker at lat/lng (centroid for geofences, centre for circles) ──
        // Rounded-square DOM marker matching the dashboard map: colour-coded
        // gradient background, abacus glyph, zoom-driven sizing. Auto-execute
        // rules get a gold border override.
        const el = document.createElement('div');
        el.className = 'contract-marker';
        const z0     = map.current ? map.current.getZoom() : 0;
        const initSz = z0 >= 14 ? 64 : z0 >= 10 ? 40 : 32;
        const initFs = initSz >= 64 ? 32 : initSz >= 40 ? 22 : 16;

        const baseBorder   = RULE_BORDERS[ruleType] || '#a78bfa';
        const borderColour = (contract as any).auto_execute ? '#FFD700' : baseBorder;
        const shadowColour = (contract as any).auto_execute ? 'rgba(255,215,0,0.7)' : (RULE_SHADOWS[ruleType] || `${colour}80`);
        const bgGradient   = RULE_GRADIENTS[ruleType] || RULE_GRADIENTS.location;
        const ruleName     = (contract as any).rule_name || contract.name || 'Smart Contract';

        el.style.cssText = `
          width:${initSz}px;height:${initSz}px;
          background:${bgGradient};
          border-radius:6px;border:2px solid ${borderColour};
          cursor:pointer;box-shadow:0 2px 8px ${shadowColour};
          display:flex;align-items:center;justify-content:center;
          font-size:${initFs}px;color:white;
          position:absolute;transform:translate(-50%,-50%);
          pointer-events:auto;transition:width .2s,height .2s,font-size .2s;
        `;
        el.innerHTML = '🧮';
        el.title = `${ruleName}${(contract as any).auto_execute ? ' (Auto-execute)' : ''}`;
        el.style.display = mapFilters.showContracts ? 'flex' : 'none';

        const contractMarker = new mapboxgl.Marker({ element: el })
          .setLngLat([lng, lat])
          .addTo(map.current!);
        contractMarkersRef.current.push(contractMarker);

        // Click: single → rule-detail overlay, double → zoom toggle.
        let contractLastClick = 0;
        let contractSingleTimer: ReturnType<typeof setTimeout> | null = null;
        el.addEventListener('click', (e) => {
          e.stopPropagation();
          const now = Date.now();
          const delta = now - contractLastClick;
          contractLastClick = now;
          if (delta < 300) {
            if (contractSingleTimer) { clearTimeout(contractSingleTimer); contractSingleTimer = null; }
            contractLastClick = 0;
            // Double-click: when zoomed out fly in (3D), when already zoomed
            // in fly back out — the same toggle the map's other pins use.
            const currentZoom = map.current?.getZoom() ?? 0;
            if (map.current) flyToWithMode(map.current, [lng, lat], currentZoom < 15);
          } else {
            if (contractSingleTimer) clearTimeout(contractSingleTimer);
            contractSingleTimer = setTimeout(() => {
              contractSingleTimer = null;
              setContractRuleOverlay({
                ruleId:          contract.id,
                ruleName:        contract.rule_name || (contract as any).name || contract.contract_name,
                contractAddress: contract.contract_address,
                functionName:    contract.function_name,
                network:         contract.network || 'testnet',
                ruleLocation:    { latitude: lat, longitude: lng },
                ruleType:        (contract as any).rule_type || null,
                radiusMeters:    (contract as any).radius_meters != null ? Number((contract as any).radius_meters) : null,
                boundaryGeoJson: (contract as any).boundary_geojson || null,
              });
            }, 300);
          }
        });
        el.addEventListener('dblclick', (e) => { e.stopPropagation(); e.preventDefault(); });

        // Hover: location/proximity show the radius circle (geofence already
        // renders its polygon, so skip).
        if (ruleType !== 'geofence') {
          el.addEventListener('mouseenter', () => {
            const r = (contract as any).radius_meters != null ? Number((contract as any).radius_meters) : (maxDistance * 1000);
            // Match the dashboard's rule hover circle: rule-type colour + dashed.
            showRadiusCircle([lng, lat], r, {
              color: colour,
              dashed: true,
              fillOpacity: 0.18,
              lineOpacity: 0.85,
            });
          });
          el.addEventListener('mouseleave', hideRadiusCircle);
        }
      } catch (error) {
        // console.error(`[App] Error adding contract marker ${index}:`, error, contract);
      }
    });
    
    // console.log(`[App] Contract markers summary:`, {
    //   total: nearbyContracts.length,
    //   added: markersAdded,
    //   invalidCoords: invalidCoords,
    //   mapLoaded: map.current ? map.current.loaded() : false,
    //   mapStyleLoaded: map.current ? map.current.isStyleLoaded() : false
    // });
  }, [nearbyContracts, mapFilters.showContracts, maxDistance, showRadiusCircle, hideRadiusCircle]);

  // Update contract markers when nearbyContracts changes - use same batching pattern as NFTs
  useEffect(() => {
    // Use a longer timeout to batch updates and prevent flickering during rapid updates
    const timeoutId = setTimeout(() => {
      if (!map.current) {
        // console.log('[App] Map not ready for contract markers yet');
        return;
      }
      
      // Create a hash of contract data to detect actual changes (include id for better uniqueness)
      const contractsHash = JSON.stringify(nearbyContracts.map(c => ({ 
        id: c.id,
        name: c.name, 
        latitude: c.latitude, 
        longitude: c.longitude 
      })).sort((a, b) => (a.id || 0) - (b.id || 0)));
      
      // Skip if data hasn't changed - this prevents unnecessary clearing/re-rendering
      if (previousContractMarkersRef.current === contractsHash) {
        // console.log('[App] Contract markers data unchanged, skipping re-render');
        return;
      }
      
      // Only update the hash and re-render if data actually changed
      const previousHash = previousContractMarkersRef.current;
      previousContractMarkersRef.current = contractsHash;
      
      if (nearbyContracts.length > 0) {
        // console.log('[App] Rendering', nearbyContracts.length, 'contract markers (data changed)');
        // Ensure map is ready before rendering
        if (map.current && map.current.loaded() && map.current.isStyleLoaded()) {
          renderContractMarkers();
        } else {
          // console.log('[App] Map not ready for contract markers, will retry when ready');
          // Wait for map to be ready
          const waitForMap = () => {
            if (map.current && map.current.loaded() && map.current.isStyleLoaded()) {
              renderContractMarkers();
            } else if (map.current) {
              setTimeout(waitForMap, 100);
            }
          };
          waitForMap();
        }
      } else {
        // Only clear markers if we previously had contracts (avoid clearing on initial load)
        if (previousHash && previousHash !== '') {
          // console.log('[App] Clearing contract markers (no contracts)');
          contractMarkersRef.current.forEach(marker => marker.remove());
          contractMarkersRef.current = [];
          // Clear circles
          if (map.current && map.current.isStyleLoaded()) {
            contractCirclesRef.current.forEach((sourceId) => {
              const layerId = `${sourceId}-layer`;
              if (map.current!.getLayer(layerId)) {
                map.current!.removeLayer(layerId);
              }
              if (map.current!.getSource(sourceId)) {
                map.current!.removeSource(sourceId);
              }
            });
          }
          contractCirclesRef.current = [];
        }
      }
    }, 100); // Increased timeout to 100ms to batch rapid updates
    return () => clearTimeout(timeoutId);
  }, [nearbyContracts, renderContractMarkers, mapFilters.showContracts]);

  const fetchOtherUsers = useCallback(async (playerLoc: [number, number] | null) => {
    // Allow fetching even without playerLocation - use map center as fallback
    let searchLocation: [number, number];
    if (playerLoc) {
      searchLocation = playerLoc;
    } else if (map.current) {
      // Use map center as fallback when wallet is not connected
      const center = map.current.getCenter();
      searchLocation = [center.lng, center.lat];
      // console.log('[App] No player location, using map center for NFT/contract search:', searchLocation);
    } else {
      // Can't fetch without location
      return;
    }
    
    if (!showOtherUsers && !playerLoc) {
      // If showOtherUsers is false and no playerLoc, only fetch NFTs/contracts
      // (user profiles are disabled but we still want to show NFTs/contracts)
    } else if (!showOtherUsers) {
      // showOtherUsers is false but we have playerLoc - skip user fetching
    }
    
    try {
      // Fetch all nearby wallets directly from GeoLink's documented endpoint.
      // We do NOT filter by activeSessions — that data is unreliable (sessions
      // are frequently missed because the backend only scans a 30-ID window and
      // only accepts Waiting/Active states).  Instead, show every wallet that
      // GeoLink reports nearby, minus the current user.
      const sessionUsers: Array<{
        id: string;
        location: [number, number];
        distance: number;
        publicKey: string;
        sessionId: number;
        sessionState: string;
      }> = [];

      // Also build a session-player lookup for session metadata (optional enrichment)
      const sessionMetaMap = new Map<string, { sessionId: number; sessionState: string; cellId?: number }>();
      for (const session of activeSessions) {
        const addMeta = (pk: string | null, cellId?: number) => {
          if (pk && pk !== walletAddress && !sessionMetaMap.has(pk)) {
            sessionMetaMap.set(pk, { sessionId: session.sessionId, sessionState: session.state, cellId });
          }
        };
        addMeta(session.player1, session.p1CellId);
        addMeta(session.player2, session.p2CellId);
      }

      // Primary source: GeoLink /api/location/nearby (documented, working endpoint).
      const nearbyWallets = await geolinkApi.getNearbyUsers(
        searchLocation[1], // lat
        searchLocation[0], // lon
        20000000           // 20 000 km — server caps internally if needed
      ).catch(() => [] as NearbyUser[]);

      // Only treat a wallet as "live" if it updated its location within the last 10 minutes.
      const LIVE_THRESHOLD_MS = 10 * 60 * 1000;
      const now = Date.now();

      for (const u of nearbyWallets) {
        if (!u.public_key) continue;
        if (u.public_key === walletAddress) continue; // skip self

        // Staleness check — GeoLink returns every wallet that ever registered a
        // location; we only want wallets that are currently active.
        if (u.last_updated) {
          const updatedAt = new Date(u.last_updated).getTime();
          if (!isNaN(updatedAt) && now - updatedAt > LIVE_THRESHOLD_MS) continue;
        }

        const lat = typeof u.latitude === 'number' ? u.latitude : parseFloat(String(u.latitude));
        const lng = typeof u.longitude === 'number' ? u.longitude : parseFloat(String(u.longitude));
        if (isNaN(lat) || isNaN(lng)) continue;

        const location: [number, number] = [lng, lat];
        const distance = playerLoc ? calculateDistance(playerLoc[1], playerLoc[0], lat, lng) : 0;
        const meta = sessionMetaMap.get(u.public_key);

        sessionUsers.push({
          id: u.public_key, location, distance,
          publicKey: u.public_key,
          sessionId: meta?.sessionId ?? 0,
          sessionState: meta?.sessionState ?? 'Active',
        });
      }

      // Use nearby GeoLink users as the player list
      if (showOtherUsers) {
        setOtherUsers(sessionUsers);
        
        // Update markers on map - debounced to prevent flickering
        // Use setTimeout to batch the update (similar to NFT markers)
        setTimeout(() => {
          updateOtherUserMarkers(sessionUsers);
        }, 0);
      }
      
      // Always fetch nearby NFTs and contracts (even without wallet)
      const [nfts, contracts] = await Promise.all([
        geolinkApi.getNearbyNFTs(searchLocation[1], searchLocation[0], maxDistance * 1000).catch((err) => {
          // console.warn('[App] Failed to fetch nearby NFTs:', err);
          return [];
        }),
        geolinkApi.getNearbyContracts(searchLocation[1], searchLocation[0], maxDistance * 1000).catch((err) => {
          // console.warn('[App] Failed to fetch nearby contracts:', err);
          return [];
        }),
      ]);
      
      // console.log('[App] Fetched nearby entities:', { nfts: nfts.length, contracts: contracts.length });
      if (contracts.length > 0) {
        // console.log('[App] Nearby contracts:', contracts);
        // Log first contract structure for debugging
        // console.log('[App] First contract structure:', {
        //   name: contracts[0].name,
        //   latitude: contracts[0].latitude,
        //   longitude: contracts[0].longitude,
        //   latType: typeof contracts[0].latitude,
        //   lngType: typeof contracts[0].longitude,
        //   fullContract: contracts[0]
        // });
      } else {
        // console.log('[App] No contracts fetched from GeoLink API');
      }
      
      setNearbyNFTs(nfts);
      setNearbyContracts(contracts);
      
      // console.log('[App] Fetched nearby users:', { 
      //   fromSessions: sessionUsers.length,
      //   total: sessionUsers.length,
      //   nfts: nfts.length, 
      //   contracts: contracts.length 
      // });
      
      // Markers are already updated above with setTimeout debouncing
    } catch (error) {
      // console.error('[App] Failed to fetch nearby users:', error);
      // Fallback to empty array on error
      setOtherUsers([]);
    }
  }, [showOtherUsers, maxDistance, updateOtherUserMarkers, activeSessions, walletAddress, calculateDistance]);

  // Fetch nearby users, NFTs, and contracts when location changes or active sessions update
  // Also fetch NFTs/contracts when map center changes (for users without wallet)
  // Debounce to prevent flickering
  useEffect(() => {
    // Always fetch NFTs/contracts (even without wallet), but only fetch users if showOtherUsers is true
    // Use playerLocation if available, otherwise use map center
    const shouldFetch = playerLocation || map.current;
    
    if (shouldFetch) {
      // Clear existing timeout
      if (markerUpdateTimeoutRef.current) {
        clearTimeout(markerUpdateTimeoutRef.current);
      }
      
      // Debounce marker updates (500ms delay)
      const now = Date.now();
      const timeSinceLastUpdate = now - lastMarkerUpdateRef.current;
      const delay = timeSinceLastUpdate < 500 ? 500 - timeSinceLastUpdate : 0;
      
      markerUpdateTimeoutRef.current = setTimeout(() => {
        lastMarkerUpdateRef.current = Date.now();
        fetchOtherUsers(playerLocation);
      }, delay);
      
      return () => {
        if (markerUpdateTimeoutRef.current) {
          clearTimeout(markerUpdateTimeoutRef.current);
        }
      };
    }
  }, [playerLocation, showOtherUsers, fetchOtherUsers, activeSessions]);
  
  // Also fetch NFTs/contracts when map moves (for users without wallet)
  useEffect(() => {
    if (!map.current || playerLocation) return; // Skip if we have playerLocation (already handled above)
    
    const handleMapMove = () => {
      // Debounce map move events
      if (markerUpdateTimeoutRef.current) {
        clearTimeout(markerUpdateTimeoutRef.current);
      }
      
      markerUpdateTimeoutRef.current = setTimeout(() => {
        fetchOtherUsers(null); // Pass null to use map center
      }, 1000); // Longer delay for map moves to avoid too many API calls
    };
    
    const currentMap = map.current;
    currentMap.on('moveend', handleMapMove);
    
    // Initial fetch when map is ready
    if (currentMap.loaded()) {
      handleMapMove();
    } else {
      currentMap.once('load', handleMapMove);
    }
    
    return () => {
      if (currentMap) {
        currentMap.off('moveend', handleMapMove);
      }
      if (markerUpdateTimeoutRef.current) {
        clearTimeout(markerUpdateTimeoutRef.current);
      }
    };
  }, [playerLocation, fetchOtherUsers]);
  
  // Monitor session state changes to detect when a session ends
  // (end_session on the internal geo-session contract).
  useEffect(() => {
    if (!readOnlyClient || !userCurrentSession) return;
    
    const checkSessionState = async () => {
      try {
        const session = await readOnlyClient.getSession(userCurrentSession);
        if (!session || session.state === 'Ended') {
          // console.log('[App] Session ended or not found:', userCurrentSession);
          // Session has ended, clear user session state
          setUserCurrentSession(null);
          setSessionLink('');
          // Refresh sessions to update UI
          await fetchActiveSessions();
        }
      } catch (error) {
        // Session might not exist anymore
        // console.log('[App] Could not check session state:', error);
      }
    };
    
    // Check session state periodically
    const interval = setInterval(checkSessionState, 10000); // Check every 10 seconds
    
    return () => clearInterval(interval);
  }, [readOnlyClient, userCurrentSession, fetchActiveSessions]);
  
  // Fetch account balance when wallet is connected
  const fetchAccountBalance = useCallback(async () => {
    if (!wallet || !walletAddress) { setAccountBalance(null); return; }
    try {
      const server = new Horizon.Server('https://horizon-testnet.stellar.org');
      const account = await server.loadAccount(walletAddress);
      const xlmBalance = account.balances.find((b: any) => b.asset_type === 'native');
      setAccountBalance(xlmBalance ? parseFloat(xlmBalance.balance).toFixed(2) : '0.00');
    } catch {
      setAccountBalance(null);
    }
  }, [wallet, walletAddress]);

  // Expose a manual trigger so child components can request an immediate refresh.
  // Also bumps passkeyRefreshTrigger so PasskeyKitPanel re-fetches its smart account balance.
  const refreshAccountBalance = useCallback(() => {
    fetchAccountBalance();
    setPasskeyRefreshTrigger(n => n + 1);
  }, [fetchAccountBalance]);

  useEffect(() => {
    if (wallet && walletAddress) {
      fetchAccountBalance();
      const balanceInterval = setInterval(fetchAccountBalance, 30000);
      return () => clearInterval(balanceInterval);
    }
  }, [wallet, walletAddress, fetchAccountBalance]);

  // Check user's current session when wallet is connected
  useEffect(() => {
    const checkUserSession = async () => {
      if (!wallet || !contractClient) {
        setUserCurrentSession(null);
        return;
      }
      
      try {
        const publicKey = await wallet.getPublicKey(true);
        
        // If there's a recently joined session, prioritize it and don't overwrite it
        if (recentlyJoinedSessionRef.current !== null) {
          const recentSession = activeSessions.find(s => s.sessionId === recentlyJoinedSessionRef.current);
          if (recentSession && 
              (recentSession.player1 === publicKey || recentSession.player2 === publicKey) &&
              (recentSession.state === 'Waiting' || recentSession.state === 'Active')) {
            // Most recently joined session is still valid, keep it
            if (userCurrentSession !== recentlyJoinedSessionRef.current) {
              // console.log('[App] Setting to recently joined session:', recentlyJoinedSessionRef.current);
              setUserCurrentSession(recentlyJoinedSessionRef.current);
              setSessionLink(`${window.location.origin}?session=${recentlyJoinedSessionRef.current}`);
            } else {
              // console.log('[App] Keeping recently joined session:', recentlyJoinedSessionRef.current);
            }
            return;
          } else {
            // Recently joined session is no longer valid, clear the flag
            recentlyJoinedSessionRef.current = null;
          }
        }
        
        // If userCurrentSession is already set and the session still exists and is valid, keep it
        // This prevents overwriting a newly joined session
        if (userCurrentSession !== null) {
          const currentSession = activeSessions.find(s => s.sessionId === userCurrentSession);
          if (currentSession && 
              (currentSession.player1 === publicKey || currentSession.player2 === publicKey) &&
              (currentSession.state === 'Waiting' || currentSession.state === 'Active') &&
              !endedSessionsRef.current.has(userCurrentSession)) {
            // Current session is still valid, don't change it
            // console.log('[App] Keeping current session:', userCurrentSession);
            return;
          }
        }
        
        // First, if userCurrentSession is set, verify it directly from the contract
        // This handles cases where activeSessions hasn't been updated yet after joining
        if (userCurrentSession !== null) {
          try {
            const contractSession = await readOnlyClient?.getSession(userCurrentSession);
            if (contractSession) {
              // Check if user is in this session
              const isInPrivateSession = contractSession.player1 === publicKey || contractSession.player2 === publicKey;
              const isInPublicSession = contractSession.players && contractSession.players.some((p: any) => p.address === publicKey);
              const isInSession = isInPrivateSession || isInPublicSession;
              
              if (isInSession && (contractSession.state === 'Waiting' || contractSession.state === 'Active')) {
                // User is still in this session, keep it
                if (!endedSessionsRef.current.has(userCurrentSession)) {
                  // console.log('[App] Verified user is still in session:', userCurrentSession);
                  setSessionLink(`${window.location.origin}?session=${userCurrentSession}`);
                  return;
                }
              } else if (contractSession.state === 'Ended') {
                // console.log('[App] User session', userCurrentSession, 'is ended, clearing');
                setUserCurrentSession(null);
                endedSessionsRef.current.add(userCurrentSession);
              } else {
                // console.log('[App] User is not in session', userCurrentSession, 'anymore, clearing');
                setUserCurrentSession(null);
              }
            } else {
              // console.log('[App] User session', userCurrentSession, 'not found, clearing');
              setUserCurrentSession(null);
            }
          } catch (error) {
            // console.warn('[App] Could not verify user session from contract:', error);
            // Don't clear on error - might be temporary network issue
          }
        }
        
        // Check if user is in any active session (exclude Ended sessions and sessions user explicitly ended)
        for (const session of activeSessions) {
          // Check if user is in session via player1/player2 (private sessions) or players array (public sessions)
          const isInPrivateSession = session.player1 === publicKey || session.player2 === publicKey;
          const isInPublicSession = session.players && session.players.some((p: any) => p.address === publicKey);
          const isInSession = isInPrivateSession || isInPublicSession;
          
          if (isInSession && (session.state === 'Waiting' || session.state === 'Active')) {
            // Skip if user explicitly ended this session
            if (endedSessionsRef.current.has(session.sessionId)) {
              // console.log('[App] Skipping session', session.sessionId, '- user explicitly ended it');
              continue;
            }
            
            // Skip if this is already the user's current session
            if (userCurrentSession === session.sessionId) {
              // console.log('[App] User is already in session:', session.sessionId);
              return;
            }
            
            // Double-check session state from contract to ensure it's not ended
            try {
              const contractSession = await readOnlyClient?.getSession(session.sessionId);
              if (contractSession && contractSession.state === 'Ended') {
                // console.log('[App] Session', session.sessionId, 'is ended, clearing user session');
                setUserCurrentSession(null);
                endedSessionsRef.current.add(session.sessionId);
                continue;
              }
            } catch (error) {
              // console.warn('[App] Could not verify session state from contract:', error);
            }
            
            setUserCurrentSession(session.sessionId);
            // Set session link if found
            if (session.sessionId) {
              setSessionLink(`${window.location.origin}?session=${session.sessionId}`);
            }
// log('[App] User is already in session:', session.sessionId);
            return;
          }
        }
        // Only clear if we didn't find any valid session and userCurrentSession wasn't already set
        if (userCurrentSession === null) {
          // Already cleared above
        } else {
          // userCurrentSession was set but not found in activeSessions - this is OK, might be timing issue
          // console.log('[App] User session', userCurrentSession, 'not in activeSessions list (may be timing issue)');
        }
      } catch (error) {
        // console.error('[App] Failed to check user session:', error);
        setUserCurrentSession(null);
      }
    };
    
    if (wallet && activeSessions.length >= 0) { // Check even if no active sessions (to clear state)
      checkUserSession();
    } else if (!wallet) {
      setUserCurrentSession(null);
    }
    // Note: userCurrentSession is intentionally excluded to prevent infinite loops
    // The function checks userCurrentSession internally but we don't want to re-run when it changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallet, contractClient, activeSessions, readOnlyClient]);

  // Restore wallet connection on page load and handle session URL parameter
  useEffect(() => {
    const restoreWallet = async () => {
      if (Wallet.wasConnected() && !wallet) {
        try {
          const w = new Wallet();
          // Try to get public key - this will reconnect if needed
          // First try silently, if that fails, the wallet will need to reconnect
          try {
            const address = await w.getPublicKey(true); // Try silently first
            if (address) {
              setWallet(w);
              setWalletAddress(address);
              const client = new ContractClient(w);
              setContractClient(client);
              // console.log('[App] Wallet restored successfully:', address);
            }
          } catch (error: any) {
            // If silent restore fails, user will need to reconnect manually
            // console.log('[App] Silent wallet restore failed, user will need to reconnect:', error);
            // Clear invalid connection state
            if (typeof window !== 'undefined') {
              localStorage.removeItem('geotrust_wallet_connected');
              localStorage.removeItem('geotrust_wallet_address');
            }
          }
          // Admin check will happen automatically via useEffect when wallet and client are set
        } catch (error: any) {
          // console.log('Failed to restore wallet connection:', error);
          // Clear invalid connection state - restoration failed, so connection is invalid
          if (typeof window !== 'undefined') {
            localStorage.removeItem('geotrust_wallet_connected');
            localStorage.removeItem('geotrust_wallet_address');
          }
        }
      }
    };
    
    // Check for session ID in URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const sessionIdParam = urlParams.get('session');
    if (sessionIdParam) {
      const sessionId = parseInt(sessionIdParam, 10);
      if (!isNaN(sessionId) && sessionId > 0) {
        // console.log('[App] Session ID found in URL:', sessionId);
        // Store session ID to join after wallet is connected
        setPendingSessionJoin(sessionId);
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
    
    restoreWallet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check admin status when wallet and contract client are ready
  useEffect(() => {
    if (wallet && contractClient) {
      // console.log('[App] Wallet and contractClient ready, checking admin status...');
      checkAdminStatus();
      fetchCountryPolicy();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallet, contractClient]);

  // Auto-checkin when wallet and contract client are ready
  // But only if user is not already in a session and there's no pending session join
  const hasCheckedIn = useRef(false);
  // Track if we've already attempted to set verifier in this session
  const hasSetVerifier = useRef(false);
  // Track if we've already attempted to set WebAuthn verifier in this session
  const hasSetWebauthnVerifier = useRef(false);
  // Track if we're currently creating the live session to prevent concurrent calls
  const isCreatingLiveSessionRef = useRef(false);
  // Auto-go-live if user is already in a session on load
  useEffect(() => {
    if (wallet && contractClient && walletAddress && userCurrentSession !== null && !playerLocation && !isCheckingIn) {
      // User is already in a session but location is not shared - auto go live
      const autoGoLive = async () => {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              const { latitude, longitude } = position.coords;
              setPlayerLocation([longitude, latitude]);
              // Update location in session
              if (handleUpdateLocation) {
                handleUpdateLocation();
              }
            },
            (error) => {
              // console.warn('[App] Auto-go-live: Location access denied or failed:', error);
            }
          );
        }
      };
      autoGoLive();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallet, contractClient, walletAddress, userCurrentSession]);

  // Auto-join session or allow starting new session when wallet is connected and location is shared
  useEffect(() => {
    if (wallet && contractClient && walletAddress && playerLocation && !isCheckingIn && 
        !hasCheckedIn.current && userCurrentSession === null && pendingSessionJoin === null) {
      // Wait a bit for active sessions to load before attempting to join
      const timer = setTimeout(async () => {
        // Double-check conditions haven't changed
        if (userCurrentSession !== null || pendingSessionJoin !== null || !wallet || !contractClient || !playerLocation) {
          return;
        }
        
        hasCheckedIn.current = true;
        
        // Try to join an existing "Waiting" session first
        const waitingSessions = activeSessions.filter(s => 
          s.state === 'Waiting' && 
          s.player1 !== walletAddress && 
          s.player2 !== walletAddress &&
          (s.player1 === null || s.player2 === null) // Session has space
        );
        
        if (waitingSessions.length > 0) {
          // Join the first available waiting session
          const sessionToJoin = waitingSessions[0];
          // console.log('[App] Auto-joining waiting session:', sessionToJoin.sessionId);
          try {
            await handleJoinSession(sessionToJoin.sessionId);
            // Successfully joined - hasCheckedIn stays true
          } catch (error: any) {
            // console.warn('[App] Failed to auto-join session:', error);
            // If join fails, reset flag so user can try again or start new session
            hasCheckedIn.current = false;
          }
        } else {
          // No waiting sessions available - automatically start a new session
          // console.log('[App] No waiting sessions available - auto-starting new session');
          try {
            // Use autoCheckIn to create and join a new session
            await autoCheckIn();
          } catch (error: any) {
            // console.warn('[App] Failed to auto-start session:', error);
            // Reset flag so user can try again manually
            hasCheckedIn.current = false;
          }
        }
      }, 2000); // Wait 2 seconds for sessions to load
      
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallet, contractClient, walletAddress, playerLocation, userCurrentSession, pendingSessionJoin, activeSessions]);

  // Handle pending session join from URL parameter
  // Wait for wallet to be fully connected before attempting to join
  useEffect(() => {
    if (pendingSessionJoin && wallet && contractClient && walletAddress && !isCheckingIn) {
      const joinPendingSession = async () => {
        try {
          // console.log('[App] Auto-joining session from URL:', pendingSessionJoin);
          // Small delay to ensure everything is ready
          await new Promise(resolve => setTimeout(resolve, 500));
          await handleJoinSession(pendingSessionJoin);
          setPendingSessionJoin(null);
        } catch (error: any) {
          // console.error('[App] Failed to auto-join session:', error);
          // Don't show alert if user rejected - that's expected
          if (!error.message?.includes('rejected') && !error.message?.includes('denied')) {
            alert(`Failed to join session ${pendingSessionJoin}: ${error.message || error}`);
          }
          setPendingSessionJoin(null);
        }
      };
      joinPendingSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSessionJoin, wallet, contractClient, walletAddress]);

  const connectWallet = async () => {
    if (isWalletConnecting) return;
    setIsWalletConnecting(true);
    try {
      setWalletError(null);
      setIsCheckingIn(false);
      hasCheckedInRef.current = false; // Reset check-in flag
      const w = new Wallet();
      await w.connect();
      const address = await w.getPublicKey();
      setWallet(w);
      setWalletAddress(address);
      const client = new ContractClient(w);
      setContractClient(client);
      
      // Check if passkey is registered for this wallet address
      // This is important for deposits to work properly
      try {
        const readOnlyClient = new ReadOnlyContractClient();
        const isRegistered = await readOnlyClient.isPasskeyRegistered(address);
        
        if (!isRegistered && passkeyService) {
          // Check if passkey is available and user has stored passkey data locally for THIS wallet
          const hasPasskeyAvailable = await checkPasskeyAvailable();
          const storedPasskeyData = await passkeyService.getStoredPasskeyData(address);
          
          if (hasPasskeyAvailable && storedPasskeyData) {
            // User has passkey locally for this wallet but not registered on contract
            // Prompt to register
            const shouldRegister = window.confirm(
              'Your passkey is not registered on the contract for this wallet. ' +
              'Register it now to enable deposits? (This is a one-time setup)'
            );
            
            if (shouldRegister) {
              try {
                // Get the public key from stored passkey data (SPKI format)
                const spkiPublicKey = storedPasskeyData.publicKey;
                
                // Convert SPKI format to raw 65-byte format for contract
                let rawPublicKey: string;
                try {
                  rawPublicKey = await passkeyService.convertSPKIToRawPublicKey(spkiPublicKey);
                } catch (convertError: any) {
                  throw new Error(`Failed to convert passkey format: ${convertError.message || 'Unknown error'}`);
                }
                
                // Register on contract for this wallet address
                await client.registerPasskey(address, rawPublicKey);
                
                setNotificationState({
                  isOpen: true,
                  title: 'Success',
                  message: 'Passkey registered successfully! You can now make deposits.',
                  type: 'success',
                  autoClose: 5000,
                });
              } catch (regError: any) {
                setNotificationState({
                  isOpen: true,
                  title: 'Warning',
                  message: 'Passkey registration failed. Deposits may not work. Error: ' + (regError.message || 'Unknown error'),
                  type: 'warning',
                  autoClose: 8000,
                });
              }
            }
          } else if (hasPasskeyAvailable) {
            // Passkey is available but not set up locally
            // User can set it up later if they want
            setNotificationState({
              isOpen: true,
              title: 'Info',
              message: 'Passkey is available but not set up. You can set it up later to enable deposits.',
              type: 'info',
              autoClose: 5000,
            });
          }
        }
      } catch (passkeyCheckError) {
        // Non-critical error - just log it
      }
      
      // Explicitly call checkAdminStatus after connection
      // Small delay to ensure client is fully initialized
      setTimeout(() => {
        checkAdminStatus();
        fetchCountryPolicy();
      }, 500);
    } catch (error: any) {
      const msg: string = error?.message || 'Failed to connect wallet. Please try again or select a different wallet.';
      // Don't show a red error banner for intentional cancellations — user just
      // closed the WC modal or clicked away from the SWK modal.
      const isCancellation = /cancelled|canceled|rejected|closed|user closed|user denied/i.test(msg);
      if (!isCancellation) {
        setWalletError(msg);
      }
    } finally {
      setIsWalletConnecting(false);
    }
  };
  
  // Store connectWallet in ref so it can be accessed in updateSessionMarkers
  connectWalletRef.current = connectWallet;

  // Keep the ref in sync so the stable MutationObserver closure can read it
  isWalletConnectingRef.current = isWalletConnecting;

  // Bridges the spatial-object interaction panel uses to satisfy
  // contract-execution preconditions and to actually sign + broadcast the
  // Soroban tx GeoLink authorises. Without these the panel reports
  // "No wallet bridge available" when a linked execution rule fires.
  const spatialInteractionBridges = useMemo(() => ({
    ensureWallet: async () => {
      if (walletAddressRef.current) return { publicKey: walletAddressRef.current };
      try { await connectWalletRef.current?.(); } catch { return null; }
      return walletAddressRef.current ? { publicKey: walletAddressRef.current } : null;
    },
    signAndSubmit: async (envelope: {
      contractAddress: string;
      functionName: string;
      functionParameters: Record<string, unknown>;
      actorWallet: string;
    }) => {
      if (!wallet) throw new Error('Connect a wallet to sign this transaction.');
      return executeSpatialContractCall(wallet, envelope);
    },
    // Signature only: the treasury routes build and submit, so this is
    // the whole of the host's contribution. The fork is here because
    // this is where the app knows which kind of wallet is connected —
    // a smart account cannot sign a raw envelope, and a browser wallet
    // has no auth entry to attest to.
    signXdr: async (xdr: string, latestLedger?: number) => {
      const address = walletAddressRef.current;
      const account = address?.startsWith('C')
        ? passkeyKitService.getAccountByWallet(address)
          ?? passkeyKitService.getAccountByCredentialId(address)
        : null;

      if (account) return passkeyKitService.signUnsignedXdr(xdr, account, latestLedger);
      if (!wallet) throw new Error('Connect a wallet to sign this transaction.');
      const { Networks } = await import('@stellar/stellar-sdk');
      return wallet.signTransaction(
        xdr,
        process.env.REACT_APP_STELLAR_NETWORK_PASSPHRASE || Networks.TESTNET,
      );
    },
  }), [wallet]);

  // Expose contractClient to window for testing
  useEffect(() => {
    if (contractClient) {
      (window as any).contractClient = contractClient;
      // console.log('[App] ContractClient exposed to window.contractClient for testing');
    } else {
      delete (window as any).contractClient;
    }
  }, [contractClient]);

  // ── Compliance post-login evaluation ────────────────────────────────────────
  // Runs every time a wallet (re)connects.  We start with a global-scope
  // check (no country) so the banner appears immediately, then re-evaluate
  // once we know the user's country so the country-scoped watchlist /
  // deny-list can kick in.  On disconnect we clear the evaluation back to
  // null inside `disconnectWallet` above so the banner doesn't linger.
  useEffect(() => {
    if (!walletAddress) return;
    let cancelled = false;
    evaluateWalletAtLogin(walletAddress).then((evaluation) => {
      if (cancelled) return;
      setComplianceEvaluation(evaluation);
      setComplianceDismissed(false);
    });
    return () => { cancelled = true; };
  }, [walletAddress]);

  // Re-evaluate with the country scope once playerLocation resolves so
  // country-level watch/deny takes effect.  We use the resolver ref the
  // map install wires up — if the ref isn't populated yet, the first
  // effect already gave us a global-only answer.
  useEffect(() => {
    if (!walletAddress || !playerLocation) return;
    const resolver = resolveCountryRef.current;
    if (!resolver) return;
    const resolved = resolver(playerLocation[0], playerLocation[1], true);
    if (!resolved) return;
    let cancelled = false;
    evaluateWalletAtLogin(walletAddress, resolved.code).then((evaluation) => {
      if (cancelled) return;
      setComplianceEvaluation(evaluation);
    });
    return () => { cancelled = true; };
  }, [walletAddress, playerLocation]);

  const disconnectWallet = async () => {
    try {
      if (wallet) {
        await wallet.disconnect();
      }
      setWallet(null);
      setWalletAddress(null);
      setTrustGroupMapAllowlist([]);
      setApplyTrustGroupMapFilter(false);
      setContractClient(null);
      setIsAdmin(false);
      setPlayerLocation(null);
      setSessionLink('');
      setWalletError(null);
      setComplianceEvaluation(null);
      setComplianceDismissed(false);
      setAdminCountryScope(new Set());
      // Reset verifier flags on disconnect so they can be set again on reconnect
      hasSetVerifier.current = false;
      hasSetWebauthnVerifier.current = false;
      hasCheckedInRef.current = false;
      // Ensure overlay is expanded on mobile after disconnect
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        setOverlayMinimized(false);
      }
    } catch (error) {
      // console.error('Failed to disconnect wallet:', error);
    }
  };

  // Helper function to get or create the live session
  const getOrCreateLiveSession = async (): Promise<number | null> => {
    if (!readOnlyClient || !contractClient) {
      // console.warn('[App] Cannot get/create live session - clients not ready');
      return null;
    }

    // Prevent concurrent calls
    if (isCreatingLiveSessionRef.current) {
      // console.log('[App] Live session creation already in progress, waiting...');
      // Wait a bit and retry
      await new Promise(resolve => setTimeout(resolve, 1000));
      const existingId = await readOnlyClient.getLiveSessionId();
      if (existingId) {
        return existingId;
      }
      // If still no session, allow this call to proceed
    }

    isCreatingLiveSessionRef.current = true;

    try {
      // First, check if a live session already exists
      let liveSessionId = await readOnlyClient.getLiveSessionId();
      
      if (liveSessionId) {
        // Verify the session exists and is public
        const session = await readOnlyClient.getSession(liveSessionId);
        if (session && (session.is_public || session.isPublic)) {
          // console.log('[App] Live session found:', liveSessionId);
          return liveSessionId;
        } else {
          // console.warn('[App] Live session ID exists but session is not public or not found, creating new one');
          liveSessionId = null;
        }
      }

      // No live session exists - create one if user is admin
      if (isAdmin && contractClient) {
        // console.log('[App] No live session found, creating new public session as admin...');
        try {
          const newSessionId = await contractClient.createSession(true); // isPublic = true
          // console.log('[App] Created public session:', newSessionId);
          
          // Set it as the live session
          await contractClient.setLiveSessionId(newSessionId);
          // console.log('[App] ✅ Set session as live session:', newSessionId);
          
          isCreatingLiveSessionRef.current = false;
          return newSessionId;
        } catch (error: any) {
          // console.error('[App] Failed to create live session:', error);
          // If it's a txBadSeq error, wait and retry once
          if (error.message?.includes('txBadSeq') || error.message?.includes('badSeq')) {
            // console.log('[App] Sequence number issue, waiting and retrying...');
            await new Promise(resolve => setTimeout(resolve, 2000));
            try {
              const retrySessionId = await contractClient.createSession(true);
              await contractClient.setLiveSessionId(retrySessionId);
              // console.log('[App] ✅ Retry successful, set session as live session:', retrySessionId);
              isCreatingLiveSessionRef.current = false;
              return retrySessionId;
            } catch (retryError: any) {
              // console.error('[App] Retry also failed:', retryError);
              if (!retryError.message?.includes('rejected')) {
                alert(`Failed to create live session after retry: ${retryError.message || retryError}`);
              }
              isCreatingLiveSessionRef.current = false;
              return null;
            }
          } else if (!error.message?.includes('rejected')) {
            alert(`Failed to create live session: ${error.message || error}`);
          }
          isCreatingLiveSessionRef.current = false;
          return null;
        }
      } else {
        // console.warn('[App] No live session exists and user is not admin - cannot create live session');
        isCreatingLiveSessionRef.current = false;
        return null;
      }
    } catch (error) {
      // console.error('[App] Error getting/creating live session:', error);
      isCreatingLiveSessionRef.current = false;
      return null;
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const autoCheckIn = async () => {
    if (!navigator.geolocation || !contractClient) return;
    
    // Check if user is already in a session - if so, don't create a new one
    if (userCurrentSession !== null) {
// log('[App] User is already in session:', userCurrentSession, '- skipping auto-checkin');
      return;
    }
    
    setIsCheckingIn(true);
    try {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          // Double-check session state before creating
          if (userCurrentSession !== null) {
// log('[App] User joined a session while getting location - skipping session creation');
            setIsCheckingIn(false);
            return;
          }

          const { latitude, longitude } = position.coords;
          setPlayerLocation([longitude, latitude]);

          // Update location to GeoLink API
          if (walletAddress) {
            try {
              await geolinkApi.updateLocation({
                public_key: walletAddress,
                blockchain: 'stellar',
                latitude,
                longitude,
                description: 'GeoTrust Player',
              });
// log('[App] Location updated to GeoLink API');
            } catch (error) {
// warn('[App] Failed to update location to GeoLink:', error);
            }
          }

          // Add player marker - ensure map container is ready
          if (map.current) {
            const addPlayerMarker = () => {
              if (!map.current) return;
              
              // Check if map container exists and is in DOM
              const container = map.current.getContainer();
              if (!container || !container.parentElement) {
                // console.warn('Map container not ready, retrying...');
                setTimeout(addPlayerMarker, 100);
                return;
              }

              if (!map.current.loaded()) {
                map.current.once('load', addPlayerMarker);
                return;
              }

              const existingMarker = document.getElementById('player-marker');
              if (existingMarker) existingMarker.remove();

              try {
                const marker = new mapboxgl.Marker({ 
                  element: createMarkerElement('player', () => {
                    if (walletAddressRef.current) {
                      // Own wallet marker → open ProfileOverlay directly.
                      openWalletProfileFromMarker(walletAddressRef.current);
                    } else {
                      setSelectedMarker({
                        type: 'player',
                        location: [longitude, latitude],
                      });
                    }
                  }, [longitude, latitude])
                })
                  .setLngLat([longitude, latitude])
                  .addTo(map.current);
                marker.getElement().id = 'player-marker';
              } catch (error) {
// error('Failed to add player marker:', error);
              }
            };

            addPlayerMarker();
          }

          // Get country code and join the live session
          const countryCode = await getCountryCode(longitude, latitude);
          if (countryCode && contractClient) {
            try {
              // Final check before joining session
              if (userCurrentSession !== null) {
// log('[App] User already in session - location updates now handled via GeoLink anchor_events');
                // DEPRECATED: updateLocation calls removed - location updates now handled via GeoLink anchor_events
                // The 10s location update loop will process anchor_events from GeoLink responses
                // No direct contract calls needed here
                setIsCheckingIn(false);
                return;
              }

              // Get or create the live session
              const liveSessionId = await getOrCreateLiveSession();
              if (!liveSessionId) {
                throw new Error('Failed to get or create live session');
              }

              const cellId = calculateCellId(latitude, longitude);
              const assetTag = await getAssetTag();
              
              // Check if user is already in the live session
              const liveSession = await readOnlyClient?.getSession(liveSessionId);
              const isAlreadyInSession = liveSession?.players?.some((p: any) => p.address === walletAddress) ||
                                        liveSession?.player1 === walletAddress ||
                                        liveSession?.player2 === walletAddress;
              
              if (isAlreadyInSession) {
                // User is already in session - location updates now handled via GeoLink anchor_events
// log('[App] Already in live session - location updates handled via GeoLink anchor_events');
                // DEPRECATED: updateLocation calls removed - location updates now handled via GeoLink anchor_events
                // The 10s location update loop will process anchor_events from GeoLink responses
                // No direct contract calls needed here
                // (Removed updateLocation call - will be handled by anchor_events)
                Promise.resolve().catch((error: any) => {
                  // Silently ignore rejections - location updates are non-critical
                  if (!error.message?.includes('rejected') && 
                      !error.message?.includes('denied') &&
                      !error.message?.includes('Transaction was rejected')) {
// debug('[App] Location update failed (non-critical):', error.message || error);
                  }
                });
                setUserCurrentSession(liveSessionId);
                setSessionLink(`${window.location.origin}?session=${liveSessionId}`);
              } else {
                // Join the live session
// log('[App] Joining live session:', liveSessionId);
                await contractClient.joinSession(
                  liveSessionId,
                  cellId,
                  assetTag,
                  countryCode
                );
// log('[App] ✅ Joined live session:', liveSessionId);
                
                // Set user's current session
                setUserCurrentSession(liveSessionId);
                setSessionLink(`${window.location.origin}?session=${liveSessionId}`);
                
                // Mark this as the most recently joined session
                recentlyJoinedSessionRef.current = liveSessionId;
                
                // Track this session ID for future polling
                knownActiveSessionIdsRef.current.add(liveSessionId);
                maxSessionIdSeenRef.current = Math.max(maxSessionIdSeenRef.current, liveSessionId);
                
                // Refresh active sessions to update UI
                await fetchActiveSessions();
                
                // Clear the recently joined flag after a delay
                setTimeout(() => {
                  if (recentlyJoinedSessionRef.current === liveSessionId) {
                    recentlyJoinedSessionRef.current = null;
                  }
                }, 5000);
              }
              
              // Fetch other users after checkin
              fetchOtherUsers([longitude, latitude]);
            } catch (error: any) {
              if (error.message?.includes('rejected') || error.message?.includes('denied')) {
                // User rejected transaction - this is expected, don't show error
// log('Transaction rejected by user');
              } else if (error.message?.includes('XDR_PARSING_ERROR') || error.message?.includes('XDR format error')) {
                // XDR parsing errors indicate SDK compatibility issues - log but don't show error to user
// warn('[App] Auto-checkin failed due to XDR parsing error - SDK may be incompatible:', error.message);
                // Don't show error to user - they've already been notified about XDR issues during admin check
              } else if (!error.message?.includes('Country not allowed')) {
// error('Auto-checkin failed:', error);
              }
            }
          }
          setIsCheckingIn(false);
        },
        (error) => {
// error('Geolocation error:', error);
          setIsCheckingIn(false);
        }
      );
    } catch (error) {
      setIsCheckingIn(false);
    }
  };

  // Track if checkAdminStatus is currently running to prevent duplicate calls
  const isCheckingAdminStatus = useRef(false);
  
  const checkAdminStatus = async () => {
    if (!contractClient || !wallet) {
// log('[App] checkAdminStatus skipped - no contractClient or wallet');
      return;
    }

    // Prevent duplicate concurrent calls
    if (isCheckingAdminStatus.current) {
// log('[App] checkAdminStatus already running, skipping duplicate call');
      return;
    }

    isCheckingAdminStatus.current = true;

    try {
// log('[App] Checking admin status...');
      let admin: string | null = null;
      let xdrError = false;
      
      try {
        // Use read-only client to avoid Freighter prompts
        const client = readOnlyClient || new ReadOnlyContractClient();
        admin = await client.getAdmin();
      } catch (error: any) {
        // Check if this is an XDR parsing error
        if (error.message?.includes('XDR_PARSING_ERROR')) {
// warn('[App] XDR parsing error when checking admin - SDK may be incompatible with contract format');
          xdrError = true;
          alert(
            'Warning: Unable to read contract data due to XDR format mismatch.\n\n' +
            'This usually means:\n' +
            '1. The Stellar SDK version is incompatible with the contract\n' +
            '2. The contract format has changed\n\n' +
            'Please update your dependencies or contact support.\n\n' +
            'The app will continue but some features may not work correctly.'
          );
        } else {
          throw error; // Re-throw other errors
        }
      }
      
      const publicKey = await wallet.getPublicKey();
// log('[App] Admin from contract:', admin, typeof admin);
// log('[App] Current wallet public key:', publicKey);
      
      // The contract could not be read, so admin is unknown — and unknown
      // must deny.
      //
      // This branch used to restore admin from `localStorage` and, failing
      // that, from a hardcoded list of two addresses (one of them labelled
      // "previous admin"). Both granted the ability to assign country admins
      // and intervene anywhere, on the strength of a cache or a constant,
      // precisely when the authoritative source was unavailable. The
      // hardcoded branch also *wrote* the localStorage key, so a single XDR
      // error marked the browser admin for good.
      if (xdrError) {
// warn('[App] Cannot determine admin status - denying until the contract can be read');
        setIsAdmin(false);
        setIsAdminOrCountryAdmin(false);
        return;
      }
      
      // Normalize admin to string for comparison
      const adminStr = admin ? String(admin) : null;
      
      // Store main admin address for country admin management
      setMainAdminAddress(adminStr);
      
      // If contract is not initialized (admin is null), prompt to initialize
      if (adminStr === null) {
// warn('[App] Contract not initialized. Please initialize the contract first.');
        const shouldInit = window.confirm(
          'Contract is not initialized. Would you like to initialize it now?\n\n' +
          'This will set you as the admin and configure the default country policy.'
        );
        if (shouldInit) {
          try {
            await contractClient.init(publicKey, false); // default_allow_all = false
// log('[App] ✅ Contract initialized successfully');
            // Retry admin check after initialization (wait longer for transaction to settle)
            setTimeout(() => {
// log('[App] Re-checking admin status after initialization...');
              checkAdminStatus();
            }, 3000);
          } catch (error: any) {
// error('[App] Failed to initialize contract:', error);
            // Check if initialization also failed with XDR error
            if (error.message?.includes('XDR_PARSING_ERROR') || error.message?.includes('XDR format error')) {
              alert(
                'Failed to initialize contract due to XDR format mismatch.\n\n' +
                'This indicates an SDK compatibility issue. Please update your dependencies.'
              );
            } else {
              alert(`Failed to initialize contract: ${error.message || error}`);
            }
          }
        }
        return;
      }
      
      if (adminStr === publicKey) {
        setIsAdmin(true);
        setIsAdminOrCountryAdmin(true);
        // Not persisted. A cached "yes" would be honoured on the next load
        // before anything had been verified, and the failure mode of that
        // cache is admin without a contract read.
// log('[App] ✅ Admin status confirmed, setting up contracts...');
        
        // Automatically set ZK verifier contract ID if available in env
        // V2: use REACT_APP_V2_ZK_VERIFIER_ID (setVerifier routes to set_zk_verifier on session contract)
        const verifierId = process.env.REACT_APP_V2_ZK_VERIFIER_ID;
        
        if (verifierId && !hasSetVerifier.current) {
          // First, check if verifier is already set on the contract
          let currentVerifier: string | null = null;
          try {
            if (readOnlyClient) {
              currentVerifier = await readOnlyClient.getVerifier();
            }
            if (currentVerifier) {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const _currentPreview = currentVerifier.substring(0, 8) + '...' + currentVerifier.substring(currentVerifier.length - 4);
// log('[App] Verifier already set in contract:', currentPreview);
              if (currentVerifier === verifierId) {
// log('[App] Verifier matches env, skipping set');
                hasSetVerifier.current = true; // Mark as set to prevent retries
              } else {
                // Verifier is set to a different value - don't override
// warn('[App] Verifier is set to different value, not overriding');
                hasSetVerifier.current = true; // Mark as set to prevent retries
              }
            }
          } catch (error: any) {
            // If we can't read the verifier, continue with setting it
            // console.warn('[App] Could not read current verifier, will attempt to set:', error);
          }
          
          // Only set verifier if it's not already set or doesn't match
          if (!hasSetVerifier.current && verifierId) {
            hasSetVerifier.current = true; // Mark as attempted
            try {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const _verifierPreview = verifierId.substring(0, 8) + '...' + verifierId.substring(verifierId.length - 4);
// log('[App] Setting verifier contract:', verifierPreview);
              await contractClient.setVerifier(verifierId);
// log('[App] Verifier contract ID set successfully:', verifierPreview);
              // Wait a bit to ensure transaction is processed before next call
              await new Promise(resolve => setTimeout(resolve, 2000));
            } catch (error: any) {
              const errorMsg = error?.message || error?.toString() || 'Unknown error';
              // Don't log as error for user rejections - this is expected behavior
              if (errorMsg.includes('rejected by user') || errorMsg.includes('Transaction was rejected')) {
// log('[App] Verifier setup was cancelled by user');
                hasSetVerifier.current = false; // Allow retry later
              } else if (errorMsg.includes('txBadSeq') || errorMsg.includes('already') || errorMsg.includes('set')) {
// warn('[App] Verifier may already be set or sequence issue - marking as set');
                hasSetVerifier.current = true; // Mark as set to prevent retries
              } else {
// error('[App] Failed to set verifier:', error);
// warn('[App] Failed to set verifier (non-critical):', errorMsg);
                hasSetVerifier.current = false; // Allow retry on next check
              }
            }
          }
        } else if (verifierId && hasSetVerifier.current) {
// log('[App] Verifier already set in this session, skipping duplicate call');
        } else if (!verifierId) {
// warn('[App] REACT_APP_V2_ZK_VERIFIER_ID not set in environment');
        }
        
        // Automatically set WebAuthn verifier contract ID if available in env
        // Only set once per session to avoid duplicate calls
        const webauthnVerifierId = process.env.REACT_APP_WEBAUTHN_VERIFIER_CONTRACT_ID;
        
        // First, check if WebAuthn verifier is already set
        let currentWebauthnVerifier: string | null = null;
        try {
          currentWebauthnVerifier = await (readOnlyClient || new ReadOnlyContractClient()).getWebauthnVerifier();
          if (currentWebauthnVerifier) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const _currentPreview = currentWebauthnVerifier.substring(0, 8) + '...' + currentWebauthnVerifier.substring(currentWebauthnVerifier.length - 4);
// log('[App] WebAuthn verifier already set in contract:', currentPreview);
            if (currentWebauthnVerifier === webauthnVerifierId) {
// log('[App] WebAuthn verifier matches env, skipping set');
              hasSetWebauthnVerifier.current = true; // Mark as set to prevent retries
            } else {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const _envPreview = webauthnVerifierId ? webauthnVerifierId.substring(0, 8) + '...' + webauthnVerifierId.substring(webauthnVerifierId.length - 4) : 'NOT SET';
// warn('[App] WebAuthn verifier mismatch - contract has:', currentPreview, 'env has:', envPreview);
            }
          }
        } catch (error) {
// warn('[App] Could not check current WebAuthn verifier:', error);
        }
        
        if (webauthnVerifierId && !hasSetWebauthnVerifier.current && currentWebauthnVerifier !== webauthnVerifierId) {
          hasSetWebauthnVerifier.current = true; // Mark as attempted
          try {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const _webauthnPreview = webauthnVerifierId.substring(0, 8) + '...' + webauthnVerifierId.substring(webauthnVerifierId.length - 4);
// log('[App] Setting WebAuthn verifier contract:', webauthnPreview);
            await contractClient.setWebauthnVerifier(webauthnVerifierId);
// log('[App] WebAuthn verifier contract ID set successfully:', webauthnPreview);
            // Wait a bit to ensure transaction is processed before next call
            await new Promise(resolve => setTimeout(resolve, 2000));
          } catch (error: any) {
            const errorMsg = error?.message || error?.toString() || 'Unknown error';
            // Don't log as error for user rejections - this is expected behavior
            if (errorMsg.includes('rejected by user') || errorMsg.includes('Transaction was rejected')) {
// log('[App] WebAuthn verifier setup was cancelled by user');
              hasSetWebauthnVerifier.current = false; // Allow retry later
            } else if (errorMsg.includes('txBadSeq') || errorMsg.includes('already') || errorMsg.includes('set')) {
// warn('[App] WebAuthn verifier may already be set or sequence issue - marking as set');
              hasSetWebauthnVerifier.current = true; // Mark as set to prevent retries
            } else {
// error('[App] Failed to set WebAuthn verifier:', error);
// warn('[App] Failed to set WebAuthn verifier (non-critical):', errorMsg);
              hasSetWebauthnVerifier.current = false; // Allow retry on next check
            }
          }
        } else if (webauthnVerifierId && hasSetWebauthnVerifier.current) {
// log('[App] WebAuthn verifier already set in this session, skipping duplicate call');
        } else if (!webauthnVerifierId) {
// warn('[App] REACT_APP_WEBAUTHN_VERIFIER_CONTRACT_ID not set in environment');
        }
        
        // Game Hub retired: session start/end is now internal to the
        // geo-session contract (start_session / end_session). No external
        // hub wiring is performed here anymore. See "Internalize sessions"
        // migration — in memory of James Bachini.
      } else {
        setIsAdmin(false);
        // Clear the keys this build no longer writes, so a browser carrying
        // them from an earlier version stops presenting them to anything
        // that still looks.
        localStorage.removeItem('geotrust_isAdmin');
        localStorage.removeItem('geotrust_adminAddress');
// log('[App] ❌ User is not admin. Admin:', admin, 'User:', publicKey);
      }
    } catch (error) {
// error('Failed to check admin status:', error);
      // An unreadable contract is not a reason to keep an earlier yes.
      setIsAdmin(false);
    } finally {
      isCheckingAdminStatus.current = false;
    }
  };

  const fetchCountryPolicy = async () => {
    // V2 has no global "list all allowed countries" endpoint.
    // allowedCountries is managed via localStorage + optimistic updates in the admin panel.
    // This function now only reads the per-country policy to update defaultAllowAll —
    // it NEVER clears allowedCountries, which would destroy the localStorage-persisted state.
    const client = readOnlyClient || contractClient;
    if (!client) return;

    try {
      const policyResult = await (client as any).getCountryPolicy();
      // null → no policy set — keep all existing state untouched
      if (policyResult === null) return;
      if (!Array.isArray(policyResult) || policyResult.length < 1) return;

      // policyResult[0] in V2 = allow_public_sessions (used as the "default allow all" proxy)
      // Do NOT interpret policyResult[1] as an "allowed country count" — in V2 it is daily_limit.
      const [defaultAllow] = policyResult;
      if (typeof defaultAllow === 'boolean') {
        setDefaultAllowAll(defaultAllow);
      }
      // Never touch allowedCountries here — it lives in localStorage.
    } catch {
      // Silently ignore — keep all existing state untouched.
    }
  };

  // Handle joining a session
  const handleJoinSession = async (sessionId: number) => {
    // Note: Sessions are managed entirely on-chain, not in GeoLink
    
    if (!wallet || !contractClient) {
      alert('Please connect your wallet first to join a session');
      await connectWallet();
      return;
    }

    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    try {
      setIsCheckingIn(true);
      
      // Get user's location
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          setPlayerLocation([longitude, latitude]);

          // Get country code
          const countryCode = await getCountryCode(longitude, latitude);
          if (!countryCode) {
            alert('Failed to determine country');
            setIsCheckingIn(false);
            return;
          }

          try {
            const cellId = calculateCellId(latitude, longitude);
            const assetTag = await getAssetTag();
            
            // Join the session
            await contractClient.joinSession(
              sessionId,
              cellId,
              assetTag,
              countryCode,
              undefined // No proof for now
            );

            setSessionLink(`${window.location.origin}?session=${sessionId}`);
            
            // Update user's current session state immediately
            setUserCurrentSession(sessionId);
            
            // Mark this as the most recently joined session to prevent checkUserSession from overwriting it
            recentlyJoinedSessionRef.current = sessionId;
            
            // Track this session ID for future polling
            if (sessionId) {
              knownActiveSessionIdsRef.current.add(sessionId);
              maxSessionIdSeenRef.current = Math.max(maxSessionIdSeenRef.current, sessionId);
            }
            
            // Clear the recently joined flag after a delay to allow checkUserSession to run normally
            const capturedSessionId = sessionId; // Capture for setTimeout
            setTimeout(() => {
              if (recentlyJoinedSessionRef.current === capturedSessionId) {
                recentlyJoinedSessionRef.current = null;
              }
            }, 5000); // 5 second grace period
            
            // Refresh active sessions to update UI
            await fetchActiveSessions();
            
            setNotificationState({
              isOpen: true,
              title: 'Success',
              message: `Successfully joined session #${sessionId}!`,
              type: 'success',
              autoClose: 3000,
            });
            
// log('[App] ✅ Session joined successfully');
          } catch (error: any) {
// error('Failed to join session:', error);
            alert(`Failed to join session: ${error.message || error}`);
          } finally {
            setIsCheckingIn(false);
          }
        },
        (error) => {
// error('Geolocation error:', error);
          alert('Failed to get your location. Please allow location access.');
          setIsCheckingIn(false);
        }
      );
    } catch (error: any) {
// error('Error joining session:', error);
      alert(`Error: ${error.message || error}`);
      setIsCheckingIn(false);
    }
  };

  // Update location only (without creating/joining sessions) - used for periodic updates
  const handleUpdateLocation = async () => {
    if (!navigator.geolocation || !contractClient || !userCurrentSession) {
// warn('[App] Cannot update location - missing requirements');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setPlayerLocation([longitude, latitude]);

        // Update player marker position
        if (map.current) {
          const addPlayerMarker = () => {
            if (!map.current) return;
            
            const container = map.current.getContainer();
            if (!container || !container.parentElement) {
              return;
            }

            if (!map.current.loaded()) {
              map.current.once('load', addPlayerMarker);
              return;
            }

            const existingMarker = document.getElementById('player-marker');
            if (existingMarker) existingMarker.remove();

            try {
              const marker = new mapboxgl.Marker({ 
                element: createMarkerElement('player', () => {
                  if (walletAddressRef.current) {
                    openWalletProfileFromMarker(walletAddressRef.current);
                  } else {
                    setSelectedMarker({ type: 'player', location: [longitude, latitude] });
                  }
                }, [longitude, latitude])
              })
                .setLngLat([longitude, latitude])
                .addTo(map.current);
              marker.getElement().id = 'player-marker';
            } catch (error) {
// error('Failed to add player marker:', error);
            }
          };

          addPlayerMarker();
        }

        // Update location in the live session (non-blocking - failures are silently ignored)
        // DEPRECATED: Direct contract.updateLocation calls removed
        // Location updates are now handled via GeoLink anchor_events in the 10s loop
        // The 10s location update loop will process anchor_events from GeoLink responses
        // No direct contract calls needed here - anchoring happens automatically when GeoLink returns anchor_events
        const countryCode = await getCountryCode(longitude, latitude).catch(() => null);
        if (countryCode && userCurrentSession) {
          // Location updates now handled via GeoLink anchor_events - no direct contract call needed
// log('[App] Location change detected - will be anchored via GeoLink anchor_events');
        }

        // Also update location to GeoLink API
        if (walletAddress) {
          try {
            await geolinkApi.updateLocation({
              public_key: walletAddress,
              blockchain: 'stellar',
              latitude,
              longitude,
              description: 'GeoTrust Player',
            });
          } catch (error) {
// warn('[App] Failed to update location to GeoLink:', error);
          }
        }
      },
      (error) => {
// warn('Failed to get location for update:', error);
      }
    );
  };

  // ── Live tracking helpers ─────────────────────────────────────────────────

  /**
   * Moves the yellow player marker to the given coordinates.
   * When live tracking is active the marker is reused and only repositioned
   * (smooth glide) to avoid constant DOM churn.
   */
  const placeOrMovePlayerMarker = (lng: number, lat: number) => {
    if (!map.current || !map.current.loaded()) return;
    setPlayerLocation([lng, lat]);

    if (livePlayerMarkerRef.current) {
      // Smooth reposition — no DOM create/destroy
      livePlayerMarkerRef.current.setLngLat([lng, lat]);
      return;
    }

    // First placement — remove any stale DOM marker then add a Mapbox one
    document.getElementById('player-marker')?.remove();
    try {
      const m = new mapboxgl.Marker({
        element: createMarkerElement('player', () => {
          if (walletAddressRef.current) {
            openWalletProfileFromMarker(walletAddressRef.current);
          } else {
            setSelectedMarker({ type: 'player', location: [lng, lat] });
          }
        }, [lng, lat]),
      })
        .setLngLat([lng, lat])
        .addTo(map.current);
      m.getElement().id = 'player-marker';
      livePlayerMarkerRef.current = m;
    } catch { /* map not ready */ }
  };

  /** Start watchPosition-based continuous tracking. */
  const startLiveTracking = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser.');
      return;
    }
    // Clear any existing watch first
    if (watchPositionIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchPositionIdRef.current);
    }

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude, longitude, speed, accuracy } = pos.coords;
        // Ignore wildly inaccurate fixes (can happen briefly indoors)
        if (accuracy > 150) return;

        // Update yellow marker immediately (visual, no cost)
        placeOrMovePlayerMarker(longitude, latitude);

        // ── Smooth GPS speed with a rolling average (reduces noise) ──────────
        if (speed !== null && speed >= 0) {
          speedHistoryRef.current.push(speed);
          if (speedHistoryRef.current.length > 5) speedHistoryRef.current.shift();
        }
        const smoothedSpeed = speedHistoryRef.current.length > 0
          ? speedHistoryRef.current.reduce((a, b) => a + b, 0) / speedHistoryRef.current.length
          : speed;

        // ── Hysteresis: require N consecutive readings before switching mode ─
        const rawMode = detectMode(smoothedSpeed);
        const pending = pendingModeRef.current;
        if (rawMode === pending.mode) {
          pending.count++;
        } else {
          pending.mode  = rawMode;
          pending.count = 1;
        }
        // walking needs more confirmation (GPS speed is noisiest at low speeds)
        const needed = rawMode === 'walking' ? 3 : 2;
        if (pending.count >= needed) {
          setMovementMode(rawMode);
        }

        // ── Map-follow: zoom / pitch / bearing driven by effective mode ──────
        const mode = effectiveModeRef.current;
        if (mode !== 'stationary' && map.current?.loaded() && !teleportActiveRef.current) {
          // teleportActiveRef gate: while AI-agent teleport mode is on,
          // GPS-driven follow MUST stand down — otherwise our flyTo
          // gets clobbered by the next GPS tick within ~600 ms and the
          // user sees the camera pop back to their own location.
          const followCfg: Record<string, { zoom: number; pitch: number; duration: number } | null> = {
            walking:    { zoom: 17, pitch: 15,  duration: 600  },
            driving:    { zoom: 15, pitch: 45,  duration: 700  },
            flying:     { zoom: 12, pitch: 55,  duration: 900  },
            stationary: null,
          };
          const cfg = followCfg[mode];
          if (cfg) {
            let bearing = map.current.getBearing();
            const prev = prevPositionRef.current;
            if (prev) {
              const dist = Math.hypot(longitude - prev[0], latitude - prev[1]);
              // Only rotate bearing if moved meaningfully (avoids jitter while stopped)
              if (dist > 0.00004) bearing = calcBearing(prev[1], prev[0], latitude, longitude);
            }
            prevPositionRef.current = [longitude, latitude];
            map.current.easeTo({
              center:   [longitude, latitude],
              zoom:     cfg.zoom,
              pitch:    cfg.pitch,
              bearing,
              duration: cfg.duration,
            });
          }
        } else {
          prevPositionRef.current = [longitude, latitude];
        }

        // Push to GeoLink API — throttled so we don't spam the server
        const now = Date.now();
        if (walletAddress && now - liveTrackingLastApiRef.current >= LIVE_API_INTERVAL) {
          liveTrackingLastApiRef.current = now;
          geolinkApi.updateLocation({
            public_key: walletAddress,
            blockchain: 'stellar',
            latitude,
            longitude,
            description: 'GeoTrust Player',
          }).catch(() => { /* ignore transient errors */ });
        }
      },
      (err) => {
        // GeolocationPositionError codes:
        //   1 = PERMISSION_DENIED      — user revoked permission, stop.
        //   2 = POSITION_UNAVAILABLE   — transient: weak/no GPS, keep watching.
        //   3 = TIMEOUT                — transient: fix didn't arrive within
        //                                 `timeout` ms; the browser will keep
        //                                 retrying automatically, so do NOT
        //                                 tear down the watch here.  Tearing
        //                                 it down used to flip isLiveTracking
        //                                 off and the Game Panel would then
        //                                 nag the user with a "stay live?"
        //                                 prompt even though the user never
        //                                 actually went offline.
        if (err.code === 1 /* PERMISSION_DENIED */) {
          setIsLiveTracking(false);
          setMovementMode('stationary');
          if (watchPositionIdRef.current !== null) {
            try { navigator.geolocation.clearWatch(watchPositionIdRef.current); } catch { /* ignore */ }
            watchPositionIdRef.current = null;
          }
        }
        // For code 2 (POSITION_UNAVAILABLE) and code 3 (TIMEOUT) we leave the
        // watch in place — the browser retries on its own and a fresh success
        // callback will arrive once the device reacquires a fix.
      },
      // A longer timeout + a small maximumAge makes high-accuracy fixes much
      // more reliable indoors / on weak GPS.  maximumAge: 10_000 lets the
      // browser reuse a recent fix if a brand-new one isn't ready yet.
      { enableHighAccuracy: true, maximumAge: 10_000, timeout: 45_000 },
    );

    watchPositionIdRef.current = watchId;
    setIsLiveTracking(true);
    setMovementMode('stationary');
  };

  /** Stop live tracking, keep the marker where it is. */
  const stopLiveTracking = () => {
    if (watchPositionIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchPositionIdRef.current);
      watchPositionIdRef.current = null;
    }
    setIsLiveTracking(false);
    setMovementMode('stationary');
    setManualMode(null);
    prevPositionRef.current   = null;
    speedHistoryRef.current   = [];
    pendingModeRef.current    = { mode: 'stationary', count: 0 };
  };

  /** Go offline — stop tracking, remove yellow marker, return to "Go LIVE" state. */
  /**
   * Open the logged-in user's ProfileOverlay at a specific tab.
   * Used by the wallet-panel + GamePanel quick-peek icon chips
   * (👤 Profile / 🎥 Live / 💎 Trustlines) so users can jump straight
   * to the data they want without first landing on the Profile tab
   * and re-clicking.  No-op when no wallet is connected.
   */
  const openMyProfileTab = useCallback((t: 'profile' | 'live' | 'trustlines') => {
    if (!walletAddress) return;
    setProfileOverlayTab(t);
    setProfileOverlayFor(walletAddress);
  }, [walletAddress]);

  /**
   * Open another wallet's ProfileOverlay from a map marker tap.
   *
   * This used to route through `setSelectedMarker` → effect →
   * `setProfileOverlayFor`, which briefly rendered the marker popup
   * overlay (`{selectedMarker && <div className="marker-popup-overlay">}`)
   * on the first commit and then cleared it on the next — a visible
   * one-frame flash that looked like the profile was "opening and
   * closing" whenever the broadcaster clicked a different cyan
   * marker while their live session was minimised.
   *
   * Routing wallet taps directly through a single helper skips the
   * popup path entirely and keeps the minimised-session guard in
   * one place: if a paid broadcast or a watch session is in flight
   * we refuse to swap the overlay target (because doing so tears
   * down the WebRTC session) and instead surface a clear toast.
   */
  // ── TikTok-style swipe-between-broadcasts ──────────────────────
  //
  // The algorithm registry lives in `services/liveFeedAlgorithms.ts`
  // so adding new sort strategies is a pure-module exercise.  We
  // persist the viewer's choice to localStorage so it survives
  // reloads, mirroring LiveDirectoryOverlay which reads from the
  // same key.  Shipped algorithms: recency / proximity / random.
  const [liveFeedAlgorithm, _setLiveFeedAlgorithm] = useState<string>(() => {
    try {
      return window.localStorage.getItem('geotrust:liveFeedAlgo') || DEFAULT_ALGORITHM_ID;
    } catch { return DEFAULT_ALGORITHM_ID; }
  });
  // Storage-event listener so the two instances (App.tsx + the
  // overlay) stay in lockstep even if the overlay is mounted and
  // mutates the preference.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'geotrust:liveFeedAlgo' && e.newValue) {
        _setLiveFeedAlgorithm(e.newValue);
      }
    };
    window.addEventListener('storage', onStorage);
    const id = setInterval(() => {
      try {
        const v = window.localStorage.getItem('geotrust:liveFeedAlgo');
        if (v && v !== liveFeedAlgorithm) _setLiveFeedAlgorithm(v);
      } catch { /* ignore */ }
    }, 2000);
    return () => { window.removeEventListener('storage', onStorage); clearInterval(id); };
  }, [liveFeedAlgorithm]);

  const viewerLocationForFeed = useMemo(
    () => (playerLocation ? { lat: playerLocation[1], lng: playerLocation[0] } : null),
    [playerLocation],
  );
  // Only poll while the user is actually looking at a profile.
  // Closes the door on the hook firing in the background when the
  // user is just exploring the map with the directory closed.
  const liveFeed = useLiveFeed({
    algorithmId: liveFeedAlgorithm,
    viewerLocation: viewerLocationForFeed,
    enabled: !!profileOverlayFor,
    viewerWallet: walletAddress,
  });

  /*
   * Second, always-on live feed used purely to drive the map-marker
   * highlight (pulsating ring + zoom-gated "LIVE" badge).  The
   * primary `liveFeed` above is deliberately paused while the profile
   * overlay is closed to cut down on polling, but map-level
   * broadcaster indication has to work even when the user hasn't
   * opened the directory yet — otherwise the new "is anybody live
   * right now?" affordance never lights up.  The hook is documented
   * as safe to mount multiple times (see useLiveFeed.ts).
   */
  const liveMarkerFeed = useLiveFeed({
    enabled: true,
    intervalMs: 15_000,
    eagerRefresh: true,
    viewerWallet: walletAddress,
  });

  // Set of agent `broadcasterId`s currently live-broadcasting. Used
  // by `AgentMarkerLayer` to flip the pulsing red ring on the agent's
  // marker, and by `AgentTeleportHUD` to mount the inline mini player
  // when the followed agent is on air. Stored in React state (not
  // just a ref) so AgentMarkerLayer's `liveAgentIds` prop changes
  // identity on every poll → its dedicated sync effect re-runs.
  const [liveAgentBroadcasterIds, setLiveAgentBroadcasterIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const wallets   = new Set<string>();
    const agentIds  = new Set<string>();
    for (const s of liveMarkerFeed.sessions) {
      // Only sessions that have actually started streaming count as
      // "live" on the map — `requested`/`paid_ready` broadcasters
      // don't have media yet, so pulsating them would be misleading.
      if (s.status !== 'live') continue;
      // Pulse both the operator/payer wallet and the on-air broadcaster
      // identity. For human broadcasts those are the same string so the
      // Set dedupes; for agent broadcasts they differ, and we want both
      // markers (the human operator AND the agent itself) to indicate
      // live status on the map.
      if (s.wallet) wallets.add(s.wallet);
      if (s.broadcasterId && s.broadcasterId !== s.wallet) wallets.add(s.broadcasterId);
      // The agent-only Set powers the AgentMarkerLayer pulse + the
      // teleport HUD's inline live player. Agent broadcasts are
      // those where broadcasterId differs from the operator wallet
      // (humans broadcast as themselves, so wallet === broadcasterId
      // — those are NOT agents and shouldn't be in this Set).
      if (s.broadcasterId && (!s.wallet || s.broadcasterId !== s.wallet)) {
        agentIds.add(s.broadcasterId);
      }
    }
    liveBroadcasterWalletsRef.current = wallets;
    setLiveAgentBroadcasterIds((prev) => {
      // Bail out of state update when the contents are unchanged so
      // we don't trigger needless re-renders of AgentMarkerLayer +
      // its sync effects on every 15s poll when nothing changed.
      if (prev.size === agentIds.size) {
        let same = true;
        for (const id of Array.from(agentIds)) { if (!prev.has(id)) { same = false; break; } }
        if (same) return prev;
      }
      return agentIds;
    });

    // Patch already-rendered markers without forcing a full rebuild.
    // New markers created after this tick pick the same `.live` state
    // up via the ref at creation time inside `updateOtherUserMarkers`.
    document.querySelectorAll<HTMLElement>('.other-user-marker[data-public-key]').forEach((el) => {
      const pk = el.getAttribute('data-public-key') || '';
      el.classList.toggle('live', wallets.has(pk));
    });
  }, [liveMarkerFeed.sessions]);

  /*
   * Toggle `.show-live-labels` on the map container when the zoom
   * crosses a threshold.  The CSS in App.css gates the tiny "LIVE"
   * badge on this class so pulsating is visible at every zoom but
   * the label only appears when the viewer is close enough to care
   * about individual broadcasters.  Threshold 12 ≈ city-block scale
   * (Mapbox zoom ~12 shows street networks), which matches the "I'm
   * zoomed in" intuition without waiting until street-level 16+.
   */
  useEffect(() => {
    const LABEL_ZOOM_THRESHOLD = 12;
    let mapInstance: mapboxgl.Map | null = null;
    let cancelled = false;

    const updateLabelVisibility = () => {
      if (!mapInstance) return;
      const container = mapInstance.getContainer();
      if (!container) return;
      container.classList.toggle('show-live-labels', mapInstance.getZoom() >= LABEL_ZOOM_THRESHOLD);
    };

    const attach = () => {
      if (cancelled) return;
      if (!map.current) {
        // Map isn't ready yet — retry on the next frame.  Mirrors the
        // pattern used elsewhere (e.g. addMarkers).
        setTimeout(attach, 100);
        return;
      }
      mapInstance = map.current;
      updateLabelVisibility();
      mapInstance.on('zoom', updateLabelVisibility);
      mapInstance.on('zoomend', updateLabelVisibility);
    };
    attach();

    return () => {
      cancelled = true;
      if (mapInstance) {
        mapInstance.off('zoom', updateLabelVisibility);
        mapInstance.off('zoomend', updateLabelVisibility);
      }
    };
  }, []);

  const openWalletProfileFromMarker = useCallback((pk: string) => {
    if (!pk) return;
    const currentTarget = profileOverlayForRef.current;
    // Same wallet as the one already open → just (re)open it (no-op
    // against minimised pill since the wallet matches).
    if (currentTarget === pk) {
      setProfileOverlayFor(pk);
      return;
    }
    // A live session is active on the overlay for a different wallet
    // → don't swap, warn the user.  Covers both "I'm broadcasting and
    // clicked someone else" and "I'm watching someone and clicked a
    // third person".
    if (liveSessionActiveRef.current && currentTarget && currentTarget !== pk) {
      setNotificationState({
        isOpen: true,
        title: 'Live session in progress',
        message:
          'Finish or stop your current live session before opening a different profile — otherwise your broadcast / viewer stream would be torn down.',
        type: 'info',
        autoClose: 4500,
      });
      return;
    }
    setProfileOverlayFor(pk);
  }, []);

  const handleGoOffline = () => {
    // Stop any active watchPosition
    if (watchPositionIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchPositionIdRef.current);
      watchPositionIdRef.current = null;
    }
    setIsLiveTracking(false);
    setMovementMode('stationary');
    setManualMode(null);
    prevPositionRef.current   = null;
    speedHistoryRef.current   = [];
    pendingModeRef.current    = { mode: 'stationary', count: 0 };

    // Remove the yellow marker from the map
    if (livePlayerMarkerRef.current) {
      livePlayerMarkerRef.current.remove();
      livePlayerMarkerRef.current = null;
    }
    document.getElementById('player-marker')?.remove();

    // Clear location state — returns GamePanel to "Go LIVE"
    setPlayerLocation(null);
    setSessionLink('');
  };

  const handleShareLocation = async () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setPlayerLocation([longitude, latitude]);

        // Add player marker - ensure map container is ready
        if (map.current) {
          const addPlayerMarker = () => {
            if (!map.current) return;
            
            // Check if map container exists and is in DOM
            const container = map.current.getContainer();
            if (!container || !container.parentElement) {
              // console.warn('Map container not ready, retrying...');
              setTimeout(addPlayerMarker, 100);
              return;
            }

            if (!map.current.loaded()) {
              map.current.once('load', addPlayerMarker);
              return;
            }

            const existingMarker = document.getElementById('player-marker');
            if (existingMarker) existingMarker.remove();

            try {
              const marker = new mapboxgl.Marker({ 
                element: createMarkerElement('player', () => {
                  if (walletAddressRef.current) {
                    openWalletProfileFromMarker(walletAddressRef.current);
                  } else {
                    setSelectedMarker({ type: 'player', location: [longitude, latitude] });
                  }
                }, [longitude, latitude])
              })
                .setLngLat([longitude, latitude])
                .addTo(map.current);
              marker.getElement().id = 'player-marker';
            } catch (error) {
// error('Failed to add player marker:', error);
            }
          };

          addPlayerMarker();
        }

          // Get country code and join the live session
        const countryCode = await getCountryCode(longitude, latitude);
        if (!countryCode) {
          alert('Failed to determine country');
          return;
        }

        if (contractClient) {
          try {
            // Get or create the live session
            const liveSessionId = await getOrCreateLiveSession();
            if (!liveSessionId) {
              throw new Error('Failed to get or create live session. Only admins can create the live session.');
            }

            const cellId = calculateCellId(latitude, longitude);
            const assetTag = await getAssetTag();
            
            // Check if user is already in the live session
            const liveSession = await readOnlyClient?.getSession(liveSessionId);
            const isAlreadyInSession = liveSession?.players?.some((p: any) => p.address === walletAddress) ||
                                      liveSession?.player1 === walletAddress ||
                                      liveSession?.player2 === walletAddress;
            
            if (isAlreadyInSession) {
              // User is already in session - location updates now handled via GeoLink anchor_events
// log('[App] Already in live session - location updates handled via GeoLink anchor_events');
              // DEPRECATED: updateLocation calls removed - location updates now handled via GeoLink anchor_events
              // The 10s location update loop will process anchor_events from GeoLink responses
              setUserCurrentSession(liveSessionId);
              setSessionLink(`${window.location.origin}?session=${liveSessionId}`);
            } else {
              // Join the live session
// log('[App] Joining live session:', liveSessionId);
              await contractClient.joinSession(
                liveSessionId,
                cellId,
                assetTag,
                countryCode
              );
// log('[App] ✅ Joined live session:', liveSessionId);
              
              // Set user's current session
              setUserCurrentSession(liveSessionId);
              setSessionLink(`${window.location.origin}?session=${liveSessionId}`);
              
              // Mark this as the most recently joined session
              recentlyJoinedSessionRef.current = liveSessionId;
              
              // Track this session ID for future polling
              knownActiveSessionIdsRef.current.add(liveSessionId);
              maxSessionIdSeenRef.current = Math.max(maxSessionIdSeenRef.current, liveSessionId);
              
              // Refresh active sessions to update UI
              await fetchActiveSessions();
              
              // Clear the recently joined flag after a delay
              setTimeout(() => {
                if (recentlyJoinedSessionRef.current === liveSessionId) {
                  recentlyJoinedSessionRef.current = null;
                }
              }, 5000);
            }
            
            // Fetch other users after joining
            fetchOtherUsers([longitude, latitude]);
          } catch (error: any) {
            if (error.message?.includes('rejected') || error.message?.includes('denied')) {
              // User rejected transaction - this is expected, don't show error
// log('Transaction rejected by user');
            } else if (error.message?.includes('Country not allowed')) {
              alert('Your country is not allowed to join sessions');
            } else if (error.message?.includes('txTooLate')) {
              alert('Transaction expired. Please try again.');
            } else {
              alert(`Failed to join live session: ${error.message}`);
            }
          }
        }
      },
      (error) => {
        alert(`Geolocation error: ${error.message}`);
      }
    );
  };

  const getCountryCode = async (lng: number, lat: number): Promise<number | null> => {
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${mapboxgl.accessToken}&types=country`
      );
      const data = await response.json();
      const country = data.features[0]?.properties?.short_code?.toUpperCase();
      if (!country) return null;

      // Cache alpha-2 in state so AgentMarkerLayer (and any future
      // country-scoped components) can subscribe to the right feed
      // without re-doing the geocode.
      try { setCurrentCountryAlpha2(country); } catch { /* swallow */ }

      // Convert ISO2 to ISO numeric
      const isoNumeric = iso2ToNumeric(country);
      return isoNumeric;
    } catch (error) {
// error('Failed to geocode:', error);
      return null;
    }
  };

  const getAssetTag = async (): Promise<Uint8Array> => {
    // Get trustlines and hash asset
    // Simplified for MVP
    if (!wallet) throw new Error('Wallet not connected');
    // In production, fetch trustlines and hash selected asset
    return new Uint8Array(32); // Placeholder
  };

  // Contract-marker → full RuleMatchDetailOverlay
  const [contractRuleOverlay, setContractRuleOverlay] = useState<RuleMatchInfo | null>(null);

  const [selectedMarker, setSelectedMarker] = useState<{
    type: 'player' | 'opponent' | 'nft' | 'contract';
    location: [number, number];
    userId?: string;
    publicKey?: string;
    distance?: number;
    sessionId?: number;
    cellId?: number;
    country?: number;
    nft?: NearbyNFT;
    contract?: NearbyContract;
  } | null>(null);

  // One-click shortcut:  if a wallet-bearing marker (own player or an
  // opponent with a Stellar address and no session-specific action) is
  // clicked, skip the intermediate "Open my profile / View profile"
  // popup and jump straight into ProfileOverlay.  NFT, contract, and
  // session-join markers still show the popup — they expose actions
  // the profile overlay doesn't cover.  While a live session is
  // running (own broadcast or watching someone) we deliberately do
  // NOT auto-swap the overlay target — the minimised pill must keep
  // its grip on the existing WebRTC session while the user explores.
  useEffect(() => {
    if (!selectedMarker) return;
    if (selectedMarker.type === 'nft' || selectedMarker.type === 'contract') return;
    if (selectedMarker.sessionId) return;
    const pk =
      selectedMarker.publicKey ||
      (selectedMarker.type === 'player' ? (walletAddress || undefined) : undefined);
    if (!pk) return;
    // Defence-in-depth: most wallet-backed marker taps now route
    // through `openWalletProfileFromMarker` directly and never hit
    // selectedMarker, which eliminates the one-frame popup flash.
    // This effect still runs for any legacy / overlay-triggered
    // flows (e.g. peek-into-marker from the notification center)
    // so we preserve the minimised-session guard here as a backup.
    if (liveSessionActiveRef.current && profileOverlayForRef.current && pk !== profileOverlayForRef.current) {
      setSelectedMarker(null);
      return;
    }
    setProfileOverlayFor(pk);
    setSelectedMarker(null);
  }, [selectedMarker, walletAddress]);

  const createMarkerElement = (type: 'player' | 'opponent', onClick?: () => void, location?: [number, number]): HTMLElement => {
    const el = document.createElement('div');
    // Keep the class only for the pulse animation on .marker-player;
    // all positioning-critical dimensions MUST be inline so Mapbox can read
    // them at mount time (before CSSOM resolves stylesheet rules) when it
    // calculates the -50%,-50% centering offset for setLngLat.
    el.className = `marker-${type}`;
    const bg = type === 'player' ? '#FFD700' : '#7C3AED';
    el.style.cssText = `
      width: 24px;
      height: 24px;
      border-radius: 50%;
      border: 3px solid white;
      background: ${bg};
      box-shadow: 0 4px 12px rgba(0,0,0,0.4);
      cursor: pointer;
      position: relative;
    `;
    // Tap / double-tap handling via pointer events.  See
    // `src/utils/markerTap.ts` for why plain `click` is unreliable
    // on iOS Safari for dynamically-created marker elements.
    attachMarkerTap(el, {
      onTap: () => { if (onClick) onClick(); },
      onDoubleTap: () => {
        const center = location ?? (map.current?.getCenter()
          ? [map.current.getCenter().lng, map.current.getCenter().lat] as [number, number]
          : undefined);
        const currentZoom = map.current?.getZoom() ?? 0;
        if (center && map.current) {
          flyToWithMode(map.current, center, currentZoom < 15);
        }
      },
    });
    return el;
  };

  useEffect(() => {
    if (!showOtherUsers) {
      document.querySelectorAll('.other-user-marker').forEach(m => m.remove());
      return;
    }
    if (otherUsers.length > 0) {
      updateOtherUserMarkers(otherUsers.filter(u => u.distance <= maxDistance));
    }
  }, [maxDistance, showOtherUsers, otherUsers, applyTrustGroupMapFilter, trustGroupMapMemberSet, updateOtherUserMarkers]);

  // Ensure overlay is expanded on mobile when no wallet is connected
  useEffect(() => {
    if (!wallet) {
      // On mobile, always expand overlay when no wallet is connected
      const isMobile = window.innerWidth <= 768;
      if (isMobile && overlayMinimized) {
        setOverlayMinimized(false);
      }
    }
  }, [wallet, overlayMinimized]);

  // Ensure overlay stays visible on mobile - check on window resize
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 768;
      if (isMobile && !wallet && overlayMinimized) {
        setOverlayMinimized(false);
      }
    };
    window.addEventListener('resize', handleResize);
    // Also check immediately
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [wallet, overlayMinimized]);

  // WalletConnect modal body-class manager.
  // Toggles "wc-modal-open" on <body> only when the w3m-modal is actually
  // visible (has the "open" attribute AND display is not none).
  //
  // Also detects when the WC modal closes while a wallet connection is in
  // progress and the WalletConnect SDK has not rejected its promise (a common
  // WC v2 behaviour). In that case we forcibly reset isWalletConnecting so the
  // "Connect Wallet" button does not stay stuck in "Connecting..." state.
  // setIsWalletConnecting is a stable React setter — safe to capture in the
  // [] closure. isWalletConnectingRef gives us the latest value without
  // needing to re-register observers on every render.
  useEffect(() => {
    let attrObserver: MutationObserver | null = null;

    const isVisible = (el: Element) => {
      if (!el.hasAttribute('open')) return false;
      return getComputedStyle(el).display !== 'none';
    };

    const applyOpen = (open: boolean) => {
      document.body.classList.toggle('wc-modal-open', open);
      // WC modal just closed — if we're still showing "Connecting...", unblock the UI
      if (!open && isWalletConnectingRef.current) {
        setIsWalletConnecting(false);
      }
    };

    const watchModal = (modal: Element) => {
      applyOpen(isVisible(modal));
      attrObserver = new MutationObserver(() => applyOpen(isVisible(modal)));
      attrObserver.observe(modal, { attributes: true, attributeFilter: ['open', 'style', 'class'] });
    };

    const bodyObserver = new MutationObserver(() => {
      const modal = document.querySelector('w3m-modal');
      if (modal) { bodyObserver.disconnect(); watchModal(modal); }
    });

    const existing = document.querySelector('w3m-modal');
    if (existing) { watchModal(existing); }
    else { bodyObserver.observe(document.body, { childList: true }); }

    return () => {
      bodyObserver.disconnect();
      attrObserver?.disconnect();
      document.body.classList.remove('wc-modal-open');
    };
  }, [setIsWalletConnecting]);

  const isDarkMap = mapStyle.includes('dark');

  return (
    <div className={`App${isDarkMap ? ' map-dark' : ''}`}>
      {/* Compliance banner — persistent top banner when the logged-in
          wallet hits a server-side deny or watch list.  BLOCKED forces
          disconnect; WARN is advisory and can be acknowledged. */}
      <ComplianceBanner
        evaluation={complianceDismissed ? null : complianceEvaluation}
        wallet={walletAddress || undefined}
        onLogout={() => { void disconnectWallet(); }}
        onDismiss={() => setComplianceDismissed(true)}
      />
      <div ref={mapContainer} className="map-container" />

      {/* AI-agent markers for the country the user is currently in,
          plus an opt-in `?agentCountries=US,CA,MX,BR` URL override
          for fleet operators who need to watch multiple countries
          from one map. The current camera country is always included
          so a US-based viewer never loses their US agents by adding
          extras. Default visitors (no query param) see exactly what
          they did before — single country, derived from geolocation.
          Lightweight component — null-renders until both the map and
          at least one country are known. Clicking a marker now opens
          the teleport selection panel first; "Open profile" inside
          the panel falls back to the original wallet-profile flow so
          AgentBadge inside the ProfileOverlay still handles the
          "this wallet is an agent" UX. */}
      {watchedAgentCountries.length > 0 && map.current ? (
        <AgentMarkerLayer
          map={map.current}
          country={watchedAgentCountries}
          onAgentSelected={(pk) => {
            // While teleport mode is already active, clicking a
            // different agent should re-aim the camera at them
            // instead of popping a confirm panel — that matches the
            // intent ("I want to follow that one now") and keeps the
            // saved pre-teleport camera intact for a single Exit.
            if (teleport.enabled) {
              teleport.start(pk);
              return;
            }
            teleport.requestPanel(pk);
          }}
          onPositionsChange={setAgentMarkerSnapshot}
          selectedAgentId={teleport.selectedAgentId || teleport.pendingAgentId}
          // While teleport mode is active for a given agent, pin
          // their marker to the cinematic's currently-visited stop
          // (lags raw WS so marker + camera + every viewer's camera
          // animate through the same sequence). Cleared on exit so
          // the marker resumes raw-WS tracking automatically.
          positionOverrides={
            teleport.enabled
              && teleport.selectedAgentId
              && teleport.displayedAgentPosition
              ? new Map([[teleport.selectedAgentId, teleport.displayedAgentPosition]])
              : null
          }
          // Pulsing red ring on agent markers whose `broadcasterId`
          // appears in an active LiveSession with status='live'.
          // Mirrors the wallet-marker `.live` pulse so the "this
          // agent is on air right now" affordance is visible at a
          // glance even when the user is panned far away. Toggles in
          // place via a `data-live` attribute — no marker DOM is
          // recreated on broadcast start/stop so the smooth WS
          // position interpolation keeps playing across transitions.
          liveAgentIds={liveAgentBroadcasterIds}
        />
      ) : null}

      {/* Teleport-mode UI surfaces. Renders nothing until the user
          either taps an AI-agent marker (selection panel) or starts
          a teleport (active HUD), so it's free to mount permanently
          alongside the marker layer. */}
      <AgentTeleportHUD
        api={teleport}
        onOpenProfile={openWalletProfileFromMarker}
        viewerWallet={walletAddress || null}
        // Active live broadcast for the agent the user is currently
        // following (if any). The HUD uses this to mount an inline
        // mini player in the control panel — picture-in-picture
        // style — so the user can watch the agent's broadcast
        // without leaving teleport mode. Recomputed per render
        // because liveMarkerFeed.sessions is stable across polls
        // when nothing changed (the Set dedupe above keeps state
        // identity steady), so this is cheap.
        followedAgentLiveSession={
          teleport.enabled && teleport.selectedAgentId
            ? (liveMarkerFeed.sessions.find(
                (s) => s.broadcasterId === teleport.selectedAgentId
                  && s.status === 'live',
              ) || null)
            : null
        }
        // Full live-broadcast feed so the teleport HUD can flag OTHER
        // broadcasters that are streaming within range of the agent's
        // current stop (distinct from the followed agent's own
        // broadcast above). The HUD filters this down to in-range
        // live sessions itself.
        liveSessions={teleport.enabled ? liveMarkerFeed.sessions : null}
        // The mini player's split-pane PIP renders a second tiny
        // Mapbox canvas mirroring this map's camera so viewers see
        // both the agent broadcast AND what the teleport host's
        // camera is looking at side-by-side. teleportHub already
        // syncs the host camera into every viewer's local map, so
        // the mini canvas just reflects whatever the local map is
        // currently showing — no second WebRTC broadcast needed.
        hostMap={map.current}
        hostMapStyle={mapStyle}
        // Same consented wallet payment handler the AgentLiveBroadcastModal
        // uses — lets the opt-in auto-broadcast pay for a paid live
        // session from the operator's wallet (and abort cleanly if a
        // session needs payment but no wallet is connected).
        onExecuteLivePayment={createLiveSessionPaymentHandler({
          gWalletSigner: wallet as any,
        })}
        // Viewer's resolved policy country — the auto-broadcast prefers
        // this (it has a configured WebRTC policy) over the agent's own
        // country to avoid 409 webrtc_policy_not_set. Resolved the same
        // way the ProfileOverlay does (player location → map center).
        viewerBroadcastCountry={(() => {
          try {
            const resolve = resolveCountryRef.current;
            if (!resolve) return null;
            const pick = (loc?: [number, number] | null) =>
              loc ? resolve(loc[0], loc[1], true) : null;
            let info = pick(playerLocation);
            if (!info && map.current) {
              const c = map.current.getCenter();
              info = pick([c.lng, c.lat]);
            }
            return info?.code ?? null;
          } catch { return null; }
        })()}
      />
      
      {/* Stellar Logo - Top Left Corner, visible only when zoomed out */}
      {mapZoom <= 4 && (
        <div style={{
          position: 'absolute',
          top: '16px',
          left: '16px',
          zIndex: 1000,
          pointerEvents: 'none',
          transition: 'opacity 0.3s ease-in-out'
        }}>
          <img 
            src="/images/Stellar_Logo.png" 
            alt="Stellar" 
            style={{ 
              height: '48px', 
              width: 'auto',
              maxWidth: '200px',
              objectFit: 'contain',
              filter: isDarkMap
                ? 'brightness(0) invert(1) drop-shadow(0 2px 6px rgba(0,0,0,0.6))'
                : 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
              transition: 'filter 0.3s ease',
            }} 
          />
        </div>
      )}
      
      {/* Old search and settings buttons removed - now in NavigationMenu */}
      
      <div className={`overlay ${overlayMinimized && wallet ? 'minimized' : ''} ${!wallet ? 'no-wallet' : ''}`}>
        {/* Wallet panel — only shown when connected; contains wallet info + smart account */}
        {wallet && walletAddress && (
        <div className="overlay-header" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 0 }}>
          {/* ── Top row: wallet address / balance / controls ── */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div className="wallet-status" style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <img
                  src="/images/stellar-xlm.png"
                  alt="Stellar"
                  style={{ height: '20px', width: '20px', objectFit: 'contain', flexShrink: 0 }}
                />
                <span className="wallet-address" style={{ fontFamily: 'Courier New', fontSize: '12px' }}>
                  {walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}
                </span>
                {isAdmin && <span className="admin-badge">ADMIN</span>}
              </div>
              {accountBalance !== null && (
                <div style={{ fontSize: '11px', color: '#666' }}>
                  Balance: {accountBalance} XLM
                </div>
              )}
              <div style={{ display: 'flex', gap: '6px', marginTop: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  className="icon-button"
                  onClick={() => { navigator.clipboard.writeText(walletAddress); }}
                  title="Copy Address"
                  style={{ fontSize: '10px', padding: '2px 6px' }}
                >
                  📋
                </button>
                <a
                  href={`https://stellar.expert/explorer/testnet/account/${walletAddress}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="icon-button"
                  title="View on Stellar Explorer"
                  style={{ fontSize: '10px', padding: '2px 6px', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  🔗
                </a>
                {/* ── Quick-peek icon chips into ProfileOverlay tabs ──
                    Replaces the old "My Profile" text button.  Each chip
                    is 22×22 so the whole row takes less horizontal space
                    than the single button did while exposing three
                    actions (👤 bio / handles, 🎥 live broadcast, 💎
                    trustlines & payments). */}
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    border: '1px solid rgba(0,0,0,0.10)',
                    borderRadius: 8,
                    background: 'rgba(0,0,0,0.03)',
                    padding: 2,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => openMyProfileTab('profile')}
                    title="My Profile — bio, handles, avatar"
                    aria-label="My Profile"
                    style={profileChipStyle}
                  >👤</button>
                  <button
                    type="button"
                    onClick={() => openMyProfileTab('live')}
                    title="Live broadcast — start / manage your paid live session"
                    aria-label="Live broadcast"
                    style={{ ...profileChipStyle, position: 'relative' }}
                  >
                    🎥
                    {liveSessionActive && (
                      <span
                        aria-hidden
                        style={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          background: '#ef4444',
                          boxShadow: '0 0 0 2px #fff',
                          animation: 'pulse 1.4s ease-in-out infinite',
                        }}
                      />
                    )}
                  </button>
                  {!walletAddress.startsWith('C') && (
                    <button
                      type="button"
                      onClick={() => openMyProfileTab('trustlines')}
                      title="Trustlines & payments"
                      aria-label="Trustlines"
                      style={profileChipStyle}
                    >💎</button>
                  )}
                </div>
              </div>
            </div>
            <div className="overlay-controls">
              {/* Disconnect moved into the expanded wallet body
                  (see the button below PasskeyKitPanel).  Keeping
                  only the minimise/restore control in the corner
                  stops the floating Live FAB from covering the
                  disconnect button when the wallet panel is
                  collapsed on mobile. */}
              <button
                className="icon-button"
                onClick={() => setOverlayMinimized(!overlayMinimized)}
                title={overlayMinimized ? 'Restore' : 'Minimize'}
              >
                {overlayMinimized ? '□' : '−'}
              </button>
            </div>
          </div>

          {/* ── Smart Account (Passkey) — lives inside the same white card ── */}
          {!overlayMinimized && process.env.REACT_APP_ACCOUNT_FACTORY_ID && (
            <PasskeyKitPanel
              key={walletAddress}
              walletAddress={walletAddress}
              wallet={wallet}
              refreshTrigger={passkeyRefreshTrigger}
              onWalletBalanceChange={refreshAccountBalance}
            />
          )}

          {/* Disconnect — only rendered when the panel is expanded.
              Full-width button at the bottom of the wallet card so
              users can't miss it, and so it can never be covered by
              the floating Live FAB when the panel is minimised. */}
          {!overlayMinimized && (
            <div style={{ padding: '8px 12px 10px' }}>
              <button
                onClick={disconnectWallet}
                title="Disconnect Wallet"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid rgba(239,68,68,0.35)',
                  background: 'rgba(239,68,68,0.08)',
                  color: '#b91c1c',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                <span aria-hidden>⎋</span>
                Disconnect wallet
              </button>
            </div>
          )}
        </div>
        )}
        {!overlayMinimized && (
          <>
            {walletError ? (
              <div className="wallet-error">
                <h3>Connection Failed</h3>
                <p>{walletError}</p>
                {walletError.includes('No wallets available') && (
                  <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#fff3cd', borderRadius: '6px', fontSize: '12px', lineHeight: '1.45' }}>
                    {isMobileBrowser() ? (
                      <>
                        <strong>📱 Try these:</strong>
                        <ul style={{ margin: '5px 0 0 16px', padding: 0 }}>
                          {hasWalletConnect() && <li><strong>WalletConnect</strong> ✅ — scan QR with xBull or Freighter</li>}
                          <li><strong>Albedo</strong> — works in browser, no install</li>
                          <li><strong>xBull</strong> — install as PWA at <a href="https://xbull.app" target="_blank" rel="noopener noreferrer">xbull.app</a></li>
                        </ul>
                      </>
                    ) : (
                      <>
                        <strong>Install a wallet extension:</strong>
                        <ul style={{ margin: '5px 0 0 16px', padding: 0 }}>
                          <li><a href="https://freighter.app" target="_blank" rel="noopener noreferrer">Freighter</a> (recommended)</li>
                          <li><a href="https://xbull.app" target="_blank" rel="noopener noreferrer">xBull</a></li>
                        </ul>
                      </>
                    )}
                  </div>
                )}
                <button
                  className="primary-button"
                  onClick={() => { setWalletError(null); connectWallet(); }}
                  disabled={isWalletConnecting}
                  style={{ marginTop: '10px', width: '100%' }}
                >
                  {isWalletConnecting ? (
                    <><span className="loading-spinner"></span> Connecting...</>
                  ) : 'Try Again'}
                </button>
              </div>
            ) : !wallet ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <button
                  className="primary-button"
                  onClick={connectWallet}
                  disabled={isWalletConnecting}
                  style={{ width: '100%' }}
                >
                  {isWalletConnecting ? (
                    <>
                      <span className="loading-spinner"></span>
                      Connecting...
                    </>
                  ) : (
                    'Connect Wallet'
                  )}
                </button>
                {isMobileBrowser() && (
                  <p style={{ margin: 0, fontSize: '11px', color: '#888', textAlign: 'center', lineHeight: '1.4' }}>
                    {hasWalletConnect() ? 'Use WalletConnect to connect your mobile wallet' : 'Choose Albedo or WalletConnect in the list'}
                  </p>
                )}
                
                {/* Show user's current session if connected */}
                {wallet && userCurrentSession !== null && (
                  <div className="game-panel" style={{ marginTop: '8px', backgroundColor: '#FFD700', color: '#000', padding: '12px' }}>
                    <h3 style={{ margin: '0 0 12px 0', fontSize: '16px' }}>Your Session</h3>
                    <div style={{ fontSize: '12px', marginBottom: '8px' }}>
                      <div><strong>Session #{userCurrentSession}</strong></div>
                      <div>Status: {activeSessions.find(s => s.sessionId === userCurrentSession)?.state || 'Active'}</div>
                    </div>
                    <button 
                      className="primary-button" 
                      onClick={() => {
                        setShowSessionDetailsOverlay(true);
                      }}
                      style={{ padding: '6px 12px', fontSize: '11px', backgroundColor: '#000', color: '#FFD700' }}
                    >
                      View Session Details
                    </button>
                  </div>
                )}
                
              </div>
            ) : (
              <>
                {/* Legacy Passkey Setup Prompt — only shown when Passkey Kit is NOT deployed */}
                {walletAddress && passkeyService && !process.env.REACT_APP_ACCOUNT_FACTORY_ID && !passkeyService.isPasskeyEnabled(walletAddress) && (
                  <div style={{ marginTop: '15px', padding: '15px', backgroundColor: 'rgba(255, 215, 0, 0.1)', borderRadius: '8px', border: '1px solid rgba(255, 215, 0, 0.3)' }}>
                    <p style={{ margin: '0 0 10px 0', color: '#FFD700', fontSize: '0.9em' }}>
                      Enable passkey authentication for enhanced security and auto-execute deposits
                    </p>
                    <button 
                      className="primary-button" 
                      onClick={() => setShowPasskeySetupModal(true)}
                      style={{ width: '100%' }}
                    >
                      Set Up Passkey
                    </button>
                  </div>
                )}

                <GamePanel
                  onShareLocation={handleShareLocation}
                  onUpdateLocation={handleUpdateLocation}
                  sessionLink={sessionLink}
                  playerLocation={playerLocation}
                  showOtherUsers={showOtherUsers}
                  onToggleShowUsers={setShowOtherUsers}
                  maxDistance={maxDistance}
                  onDistanceChange={setMaxDistance}
                  otherUsersCount={gamePanelNearbyOthersCount}
                  aiAgentsCount={agentMarkerSnapshot.length}
                  trustGroupMemberAllowlistCount={trustGroupMapAllowlist.length}
                  trustGroupMapFilterActive={applyTrustGroupMapFilter}
                  onTrustGroupMapFilterToggle={setApplyTrustGroupMapFilter}
                  minimized={gamePanelMinimized}
                  onToggleMinimize={() => setGamePanelMinimized(!gamePanelMinimized)}
                  isLiveTracking={isLiveTracking}
                  movementMode={movementMode}
                  effectiveMode={effectiveMode}
                  isAutoMode={manualMode === null}
                  onSetMode={handleSetMode}
                  onClearMode={handleClearMode}
                  onStartLiveTracking={startLiveTracking}
                  onStopLiveTracking={stopLiveTracking}
                  onGoOffline={handleGoOffline}
                />
              </>
            )}
          </>
        )}
      </div>
      
      {/* Contract Rule Detail Overlay — opens when a contract marker on the map is clicked */}
      {contractRuleOverlay && (
        <RuleMatchDetailOverlay
          isOpen={!!contractRuleOverlay}
          onClose={() => setContractRuleOverlay(null)}
          info={contractRuleOverlay}
          network={(contractRuleOverlay.network as 'testnet' | 'mainnet') || 'testnet'}
          allowedCountries={allowedCountries}
          borderRevision={borderRevision}
        />
      )}

      {/* Marker Profile Popup */}
      {selectedMarker && (
        <div className="marker-popup-overlay" onClick={() => setSelectedMarker(null)}>
          <div className="marker-popup" onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                zIndex: 2,
              }}
            >
              {selectedMarker.location && (
                <button
                  onClick={() => {
                    if (map.current) flyToWithMode(map.current, selectedMarker.location as [number, number], true);
                    setSelectedMarker(null);
                  }}
                  style={{
                    height: 32,
                    background: 'rgba(99,102,241,0.12)',
                    border: '1px solid rgba(99,102,241,0.4)',
                    borderRadius: 8,
                    color: '#4f46e5',
                    fontSize: 12,
                    fontWeight: 600,
                    padding: '0 10px',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    lineHeight: 1,
                  }}
                  title="Zoom into this location"
                >
                  🔍 Zoom
                </button>
              )}
              <button
                className="marker-popup-close"
                onClick={() => setSelectedMarker(null)}
                style={{ position: 'static' }}
              >
                ×
              </button>
            </div>
            <h3>
              {selectedMarker.type === 'nft' ? 'NFT Details' :
               selectedMarker.type === 'contract' ? 'Smart Contract' :
               selectedMarker.publicKey ? (
                 <>
                   {shortenStellarAddress(selectedMarker.publicKey)}
                   {selectedMarker.type === 'player' && (
                     <span style={{ fontSize: '14px', fontWeight: 500, color: '#666' }}> (you)</span>
                   )}
                 </>
               ) :
               selectedMarker.type === 'player' ? 'You' :
               'Wallet on map'}
              {selectedMarker.type === 'contract' && selectedMarker.contract && (() => {
                const c = selectedMarker.contract as any;
                const name = c.contract_name || c.name;
                return name ? <span style={{ display: 'block', fontSize: '12px', fontWeight: 400, color: '#888', marginTop: '2px' }}>{name}</span> : null;
              })()}
            </h3>
            <div className="marker-popup-content">
              {selectedMarker.type === 'nft' && selectedMarker.nft && (
                <>
                  <div className="marker-popup-field">
                    <label>Collection:</label>
                    <span>{selectedMarker.nft.collection_name || selectedMarker.nft.name || 'Unknown NFT'}</span>
                  </div>
                  {selectedMarker.nft.description && (
                    <div className="marker-popup-field">
                      <label>Description:</label>
                      <span>{selectedMarker.nft.description}</span>
                    </div>
                  )}
                  {selectedMarker.nft.distance && (
                    <div className="marker-popup-field">
                      <label>Distance:</label>
                      <span>{selectedMarker.nft.distance.toFixed(1)}m away</span>
                    </div>
                  )}
                  {(selectedMarker.nft.contract_address || selectedMarker.nft.token_id) && (
                    <div className="marker-popup-field">
                      <label>Contract:</label>
                      <span style={{ fontFamily: 'Courier New', fontSize: '11px', wordBreak: 'break-all' }}>
                        {selectedMarker.nft.contract_address || 'N/A'}
                        {selectedMarker.nft.token_id && ` #${selectedMarker.nft.token_id}`}
                      </span>
                    </div>
                  )}
                  <div className="marker-popup-field" style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #eee' }}>
                    <div style={{ 
                      width: '100%', 
                      height: '200px', 
                      backgroundImage: `url(${constructImageUrl(selectedMarker.nft.server_url, selectedMarker.nft.ipfs_hash) || selectedMarker.nft.image_url || 'https://via.placeholder.com/200x200?text=NFT'})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      borderRadius: '8px',
                      border: '2px solid #FFD700'
                    }} />
                  </div>
                </>
              )}
              {selectedMarker.type === 'contract' && selectedMarker.contract && (() => {
                const c = selectedMarker.contract as any;
                const name = c.contract_name || c.name || 'Unknown Contract';
                const pill = (text: string, color: string, bg: string) => (
                  <span style={{ display: 'inline-block', fontSize: '10px', fontWeight: 600, padding: '2px 7px', borderRadius: '10px', background: bg, color, marginRight: '4px', marginBottom: '4px' }}>
                    {text}
                  </span>
                );
                return (
                  <>
                    {/* Header — name + status pills */}
                    <div style={{ marginBottom: '10px' }}>
                      <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: '6px' }}>{name}</div>
                      <div>
                        {c.is_active !== false ? pill('● Active', '#166534', '#dcfce7') : pill('○ Inactive', '#991b1b', '#fee2e2')}
                        {c.auto_execute && pill('⚡ Auto-execute', '#854d0e', '#fef9c3')}
                        {c.requires_webauthn && pill('🔐 WebAuthn', '#1e3a8a', '#dbeafe')}
                        {c.use_smart_wallet && pill('💳 Smart Wallet', '#5b21b6', '#ede9fe')}
                        {c.network && pill(c.network === 'mainnet' ? '🌐 Mainnet' : '🧪 Testnet', '#374151', '#f3f4f6')}
                      </div>
                    </div>

                    {c.description && (
                      <div className="marker-popup-field">
                        <label>Description:</label>
                        <span>{c.description}</span>
                      </div>
                    )}

                    {c.rule_name && (
                      <div className="marker-popup-field">
                        <label>Execution Rule:</label>
                        <span>
                          {c.rule_name}
                          {c.rule_type && (
                            <span style={{
                              marginLeft: '6px', fontSize: '10px', fontWeight: 600, padding: '1px 6px',
                              borderRadius: '8px',
                              background: c.rule_type === 'geofence' ? 'rgba(255,107,53,0.15)' : c.rule_type === 'proximity' ? 'rgba(74,144,217,0.15)' : 'rgba(123,104,238,0.15)',
                              color:      c.rule_type === 'geofence' ? '#c04a15' : c.rule_type === 'proximity' ? '#2a6da8' : '#5a48cc',
                            }}>
                              {c.rule_type}
                            </span>
                          )}
                        </span>
                      </div>
                    )}

                    {c.function_name && (
                      <div className="marker-popup-field">
                        <label>Function:</label>
                        <span style={{ fontFamily: 'Courier New', fontSize: '12px' }}>{c.function_name}</span>
                      </div>
                    )}

                    {c.trigger_on && (
                      <div className="marker-popup-field">
                        <label>Trigger:</label>
                        <span>
                          {c.trigger_on === 'dwell'
                            ? `After ${c.min_location_duration_seconds != null ? `${c.min_location_duration_seconds}s` : '?'} dwell`
                            : c.trigger_on === 'enter' ? 'On Enter'
                            : c.trigger_on === 'exit'  ? 'On Exit'
                            : c.trigger_on}
                        </span>
                      </div>
                    )}

                    {(c.max_executions_per_public_key != null || c.execution_time_window_seconds != null) && (
                      <div className="marker-popup-field">
                        <label>Rate Limit:</label>
                        <span>
                          {c.max_executions_per_public_key != null
                            ? `${c.max_executions_per_public_key}× per ${
                                c.execution_time_window_seconds != null
                                  ? (c.execution_time_window_seconds >= 604800 ? `${Math.round(c.execution_time_window_seconds / 604800)}w`
                                    : c.execution_time_window_seconds >= 86400 ? `${Math.round(c.execution_time_window_seconds / 86400)}d`
                                    : c.execution_time_window_seconds >= 3600  ? `${Math.round(c.execution_time_window_seconds / 3600)}h`
                                    : `${c.execution_time_window_seconds}s`)
                                  : '?'}`
                            : 'Unlimited'}
                        </span>
                      </div>
                    )}

                    {c.requires_confirmation != null && (
                      <div className="marker-popup-field">
                        <label>Confirmation:</label>
                        <span>{c.requires_confirmation ? '✅ Required' : '⚡ Automatic'}</span>
                      </div>
                    )}

                    {c.rule_type !== 'geofence' && c.radius_meters && (
                      <div className="marker-popup-field">
                        <label>Radius:</label>
                        <span>{c.radius_meters.toLocaleString()} m</span>
                      </div>
                    )}

                    {c.rule_type === 'geofence' && c.boundary_geojson && (
                      <div className="marker-popup-field">
                        <label>Boundary:</label>
                        <span>{c.boundary_geojson.type} {c.geofence_id ? `(ID #${c.geofence_id})` : '(inline)'}</span>
                      </div>
                    )}

                    {c.target_wallet_public_key && (
                      <div className="marker-popup-field">
                        <label>Target Wallet:</label>
                        <span style={{ fontFamily: 'Courier New', fontSize: '11px' }}>
                          {c.target_wallet_public_key.slice(0, 8)}…{c.target_wallet_public_key.slice(-4)}
                        </span>
                      </div>
                    )}

                    {c.distance != null && (
                      <div className="marker-popup-field">
                        <label>Distance:</label>
                        <span>{c.distance.toFixed(0)} m away</span>
                      </div>
                    )}

                    {/* Contract address with copy */}
                    <div className="marker-popup-field">
                      <label>Contract Address:</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: 'Courier New', fontSize: '11px', wordBreak: 'break-all', color: '#6366f1' }}>
                          {c.contract_address}
                        </span>
                        <button
                          className="icon-button"
                          onClick={() => {
                            navigator.clipboard.writeText(c.contract_address);
                            alert('Contract address copied!');
                          }}
                          title="Copy"
                          style={{ fontSize: '12px', padding: '3px 7px', flexShrink: 0 }}
                        >
                          📋
                        </button>
                      </div>
                    </div>

                    {/* Discovered / legacy functions list */}
                    {(() => {
                      const fns: any[] = c.discovered_functions || c.functions || [];
                      return fns.length > 0 ? (
                        <div className="marker-popup-field">
                          <label>Discovered Functions:</label>
                          <div style={{ maxHeight: '90px', overflowY: 'auto', fontSize: '11px', fontFamily: 'Courier New' }}>
                            {fns.map((f: any, i: number) => (
                              <div key={i} style={{ padding: '3px 6px', background: '#f0f0f0', borderRadius: '4px', marginBottom: '2px' }}>
                                {f.name || f.function_name || String(f)}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null;
                    })()}
                  </>
                );
              })()}
              {selectedMarker.publicKey && selectedMarker.type !== 'nft' && selectedMarker.type !== 'contract' && (
                <div className="marker-popup-field">
                  <label>Stellar address:</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span className="marker-popup-id" style={{ fontFamily: 'Courier New', fontSize: '12px', wordBreak: 'break-all' }}>
                      {shortenStellarAddress(selectedMarker.publicKey)}
                    </span>
                    <button
                      className="icon-button"
                      onClick={() => {
                        navigator.clipboard.writeText(selectedMarker.publicKey!);
                        alert('Address copied to clipboard.');
                      }}
                      title="Copy full address"
                      style={{ fontSize: '12px', padding: '4px 8px', flexShrink: 0 }}
                    >
                      📋 Copy full
                    </button>
                  </div>
                </div>
              )}
              <div className="marker-popup-field">
                <label>Location:</label>
                <span>
                  {(() => {
                    // When not logged in, show approximate location only
                    if (!walletAddress && selectedMarker.type === 'opponent') {
                      const lat = typeof selectedMarker.location[1] === 'number' 
                        ? selectedMarker.location[1] 
                        : Number(selectedMarker.location[1]) || 0;
                      const lng = typeof selectedMarker.location[0] === 'number' 
                        ? selectedMarker.location[0] 
                        : Number(selectedMarker.location[0]) || 0;
                      // Round to 1 decimal place for approximate location
                      return `${lat.toFixed(1)}, ${lng.toFixed(1)} (approximate)`;
                    }
                    // When logged in, show exact coordinates
                    const lat = typeof selectedMarker.location[1] === 'number' 
                      ? selectedMarker.location[1] 
                      : Number(selectedMarker.location[1]) || 0;
                    const lng = typeof selectedMarker.location[0] === 'number' 
                      ? selectedMarker.location[0] 
                      : Number(selectedMarker.location[0]) || 0;
                    return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
                  })()}
                </span>
              </div>
              {selectedMarker.distance !== undefined && (
                <div className="marker-popup-field">
                  <label>Distance:</label>
                  <span>{selectedMarker.distance.toFixed(1)} km</span>
                </div>
              )}
              {selectedMarker.sessionId && (
                <div className="marker-popup-field">
                  <label>Session ID:</label>
                  <span>#{selectedMarker.sessionId}</span>
                </div>
              )}
              {selectedMarker.cellId && (
                <div className="marker-popup-field">
                  <label>Cell ID:</label>
                  <span>{selectedMarker.cellId}</span>
                </div>
              )}
              {selectedMarker.country && (
                <div className="marker-popup-field">
                  <label>Country Code:</label>
                  <span>{selectedMarker.country}</span>
                </div>
              )}
              {selectedMarker.userId && (
                <div className="marker-popup-field">
                  <label>User ID:</label>
                  <span className="marker-popup-id">{selectedMarker.userId}</span>
                </div>
              )}
              {selectedMarker.type === 'player' && (
                <div className="marker-popup-field">
                  <label>Status:</label>
                  <span className="marker-popup-status">Active</span>
                </div>
              )}
              {selectedMarker.publicKey && readOnlyClient && (
                <div className="marker-popup-field" style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #eee' }}>
                  <label style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}>Active Sessions:</label>
                  {(() => {
                    const userSessions = activeSessions.filter(s => 
                      (s.player1 === selectedMarker.publicKey || s.player2 === selectedMarker.publicKey) && 
                      s.state !== 'Ended'
                    );
                    if (userSessions.length === 0) {
                      return <span style={{ color: '#666', fontSize: '12px' }}>No active sessions</span>;
                    }
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {userSessions.map(session => (
                          <div key={session.sessionId} style={{ 
                            padding: '6px', 
                            backgroundColor: session.state === 'Active' ? '#e8f5e9' : '#fff3cd', 
                            borderRadius: '4px',
                            fontSize: '11px'
                          }}>
                            <div><strong>Session #{session.sessionId}</strong> - {session.state}</div>
                            {session.p1CellId && <div style={{ color: '#666', fontSize: '10px' }}>Cell: {session.p1CellId}</div>}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}
              {selectedMarker.publicKey && selectedMarker.type === 'opponent' && walletAddress && (
                <TrustlineComparison 
                  myPublicKey={walletAddress}
                  theirPublicKey={selectedMarker.publicKey}
                />
              )}
              {selectedMarker.sessionId && selectedMarker.type === 'opponent' && walletAddress && contractClient && (
                <div className="marker-popup-field" style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #eee' }}>
                  {userCurrentSession === selectedMarker.sessionId ? (
                    <div style={{ padding: '8px', backgroundColor: '#e8f5e9', borderRadius: '4px', textAlign: 'center' }}>
                      <span style={{ color: '#2e7d32', fontWeight: 600 }}>✓ You are in this session</span>
                    </div>
                  ) : (
                    <button
                      className="primary-button"
                      onClick={async () => {
                        // Check if already in this session
                        if (userCurrentSession === selectedMarker.sessionId) {
                          alert('You are already in this session!');
                          return;
                        }
                        
                        if (userCurrentSession !== null) {
                          if (!window.confirm(`You are currently in session #${userCurrentSession}. Switch to session #${selectedMarker.sessionId}?`)) {
                            return;
                          }
                        }
                        if (selectedMarker.sessionId) {
                          try {
                            await handleJoinSession(selectedMarker.sessionId);
                            setSelectedMarker(null); // Close overlay after joining
                          } catch (error: any) {
// error('[App] Failed to join session from marker:', error);
                            alert(`Failed to join session: ${error.message || 'Unknown error'}`);
                          }
                        }
                      }}
                      style={{ width: '100%', marginTop: '8px' }}
                    >
                      {userCurrentSession !== null ? `Switch to Session #${selectedMarker.sessionId}` : `Join Session #${selectedMarker.sessionId}`}
                    </button>
                  )}
                </div>
              )}
              {(() => {
                const profilePk =
                  selectedMarker.publicKey ||
                  (selectedMarker.type === 'player' ? walletAddress || undefined : undefined);
                if (!profilePk) return null;
                const isSelf = selectedMarker.type === 'player' || profilePk === walletAddress;
                return (
                  <div
                    className="marker-popup-field"
                    style={{
                      marginTop: 12,
                      paddingTop: 12,
                      borderTop: '1px solid #eee',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                    }}
                  >
                    <button
                      onClick={() => {
                        setProfileOverlayFor(profilePk);
                        setSelectedMarker(null);
                      }}
                      style={{
                        background: isSelf ? '#FFD700' : '#111827',
                        color: isSelf ? '#111827' : '#fff',
                        border: `1px solid ${isSelf ? '#FFD700' : '#111827'}`,
                        borderRadius: 8,
                        padding: '10px 14px',
                        fontSize: 14,
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      {isSelf ? 'Open my profile' : 'View profile'}
                    </button>
                    <a
                      href={`https://stellar.expert/explorer/testnet/account/${profilePk}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#b45309', textDecoration: 'none', fontWeight: 600, fontSize: 12 }}
                    >
                      View on Stellar Explorer →
                    </a>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
      
      {/* Confirmation Overlay */}
      {confirmationState && (
        <ConfirmationOverlay
          isOpen={confirmationState.isOpen}
          title={confirmationState.title}
          message={confirmationState.message}
          confirmText={confirmationState.confirmText}
          cancelText={confirmationState.cancelText}
          type={confirmationState.type}
          onConfirm={confirmationState.onConfirm}
          onCancel={() => setConfirmationState(null)}
        />
      )}
      
      {/* Notification Overlay */}
      {notificationState && (
        <NotificationOverlay
          isOpen={notificationState.isOpen}
          title={notificationState.title}
          message={notificationState.message}
          type={notificationState.type}
          autoClose={notificationState.autoClose}
          onClose={() => setNotificationState(null)}
        />
      )}
      
      {/* Session Details Overlay */}
      {showSessionDetailsOverlay && userCurrentSession && (
        <SessionDetailsOverlay
          isOpen={showSessionDetailsOverlay}
          onClose={() => {
            setShowSessionDetailsOverlay(false);
            setSessionDetailsData(null);
          }}
          currentSession={activeSessions.find(s => s.sessionId === userCurrentSession) || sessionDetailsData || null}
          allSessions={activeSessions}
          walletAddress={walletAddress}
          onJoinSession={async (sessionId: number) => {
            await handleJoinSession(sessionId);
            setNotificationState({
              isOpen: true,
              title: 'Success',
              message: `Successfully joined session #${sessionId}!`,
              type: 'success',
              autoClose: 3000,
            });
          }}
        />
      )}
      
      {/* Country Management Overlay */}
      {showCountryManagementOverlay && selectedCountry && (
        <CountryManagementOverlay
          isOpen={showCountryManagementOverlay}
          onClose={() => {
            setShowCountryManagementOverlay(false);
            setSelectedCountry(null);
          }}
          countryCode={selectedCountry.code}
          countryName={selectedCountry.name}
          contractClient={contractClient}
          walletAddress={walletAddress}
          mainAdminAddress={mainAdminAddress}
          isMainAdmin={!!(walletAddress && mainAdminAddress && walletAddress === mainAdminAddress)}
          map={map.current}
          onPolicyChanged={fetchCountryPolicy}
          onBordersChanged={() => {
            // Refresh the main map geometry then re-apply policy colors
            loadCountryOverlay({ force: true }).then(() => {
              // updateCountryOverlay is already scheduled inside loadCountryOverlay (200ms),
              // but call it again at 600ms and 1500ms as a safety net for large files.
              setTimeout(() => updateCountryOverlay(), 600);
              setTimeout(() => updateCountryOverlay(), 1500);
            });
            // Bump revision so every other map/overlay re-fetches too
            setBorderRevision(r => r + 1);
          }}
        />
      )}
      
      {/* Passkey Setup Modal */}
      <PasskeySetupModal
        isOpen={showPasskeySetupModal}
        onClose={() => setShowPasskeySetupModal(false)}
        onPasskeyEnabled={(credentialId) => {
// log('[App] Passkey enabled:', credentialId);
          setShowPasskeySetupModal(false);
        }}
        publicKey={walletAddress || ''}
        contractClient={contractClient}
      />

      {/* Per-wallet Profile Overlay (bio, handles, avatar, live, trustlines) */}
      {profileOverlayFor && (() => {
        // Resolve the numeric country code to scope the live-session policy.
        // Prefers the viewer's own location (for "My Profile"), then falls back
        // to the selected marker location (for "View profile") or map center.
        //
        // We also surface which SOURCE was used and the country NAME so the
        // overlay can show the user *what's being evaluated* — otherwise,
        // when the globe idle-spins, it looks like the policy silently
        // flips between countries and users have no way to correlate the
        // message to a real decision.
        let country: number | undefined;
        let countryName: string | undefined;
        let countrySource: 'location' | 'marker' | 'map' | undefined;
        try {
          const resolve = resolveCountryRef.current;
          const pickInfo = (loc?: [number, number] | null) => {
            if (!resolve || !loc) return null;
            return resolve(loc[0], loc[1], true);
          };
          let info = pickInfo(playerLocation);
          if (info) { countrySource = 'location'; }
          if (!info && selectedMarker?.location) {
            info = pickInfo(selectedMarker.location as [number, number]);
            if (info) { countrySource = 'marker'; }
          }
          if (!info && map.current) {
            const c = map.current.getCenter();
            info = pickInfo([c.lng, c.lat]);
            if (info) { countrySource = 'map'; }
          }
          country     = info?.code ?? undefined;
          countryName = info?.name ?? undefined;
        } catch { /* ignore resolver errors */ }
        // Resolve neighbour broadcasts (next/prev) through the
        // chosen algorithm.  Skipped when the overlay is showing
        // the viewer's own wallet — swiping between "yourself" and
        // yourself makes no sense, and we also hide the control if
        // only one other broadcaster is live so the arrows don't
        // look broken by immediately cycling back.
        const isViewingOwn = !!walletAddress && walletAddress === profileOverlayFor;
        const nextSession = (!isViewingOwn && liveFeed.sessions.length > 1)
          ? liveFeed.neighbor(profileOverlayFor, 'next')
          : null;
        const prevSession = (!isViewingOwn && liveFeed.sessions.length > 1)
          ? liveFeed.neighbor(profileOverlayFor, 'prev')
          : null;
        const goToNeighbor = (s: typeof nextSession) => {
          if (!s) return;
          const w = s.wallet || s.broadcasterId;
          if (!w || w === profileOverlayFor) return;
          setProfileOverlayTab('live');
          setProfileOverlayFor(w);
        };
        return (
          <ProfileOverlay
            isOpen={!!profileOverlayFor}
            onClose={() => {
              setProfileOverlayFor(null);
              setLiveSessionActive(false);
              // Reset to default so the next open from a marker click
              // lands on Profile rather than whichever chip was used last.
              setProfileOverlayTab('profile');
            }}
            wallet={profileOverlayFor}
            viewerAddress={walletAddress || undefined}
            isOwner={!!walletAddress && walletAddress === profileOverlayFor}
            stellarWallet={wallet as any}
            country={country}
            countryName={countryName}
            countrySource={countrySource}
            hasLocation={!!playerLocation}
            onRequestLocation={() => {
              // One-click parity with the main "Go LIVE" button on
              // the GamePanel: share the current location AND start
              // continuous watchPosition tracking so the user doesn't
              // have to click "Stay Live" separately after coming
              // back from this overlay.  `handleShareLocation` is
              // already idempotent for repeated calls (it just
              // re-places the player marker), and `startLiveTracking`
              // internally clears any pre-existing watch before
              // installing a new one, so double-clicking is safe.
              void handleShareLocation();
              startLiveTracking();
            }}
            approxLocation={playerLocation
              ? { lat: playerLocation[1], lng: playerLocation[0] }
              : undefined}
            onExecuteLivePayment={createLiveSessionPaymentHandler({
              gWalletSigner: wallet as any,
            })}
            onSessionActiveChange={setLiveSessionActive}
            initialTab={profileOverlayTab}
            onOpenWallet={openWalletProfileFromMarker}
            onNextBroadcast={nextSession ? () => goToNeighbor(nextSession) : undefined}
            onPrevBroadcast={prevSession ? () => goToNeighbor(prevSession) : undefined}
            nextBroadcastLabel={nextSession ? shortenStellarAddress(nextSession.wallet || nextSession.broadcasterId) : null}
            prevBroadcastLabel={prevSession ? shortenStellarAddress(prevSession.wallet || prevSession.broadcasterId) : null}
            myTrustGroups={myTrustGroups}
            onStartOnchainSession={async ({ isPublic, country: sessCountry, trustGroupIds }) => {
              // Create the on-chain live-presence session that scopes both
              // map presence and SFU visibility. Returns the numeric id.
              if (!contractClient || !walletAddress) return null;
              try {
                const id = await contractClient.startSession(
                  walletAddress,
                  isPublic,
                  sessCountry,
                  trustGroupIds,
                );
                return typeof id === 'number' ? id : null;
              } catch (e) {
                console.error('[App] start_session failed', e);
                return null;
              }
            }}
          />
        );
      })()}

      {/* Country Profile Overlay (for non-admins) */}
      {showCountryProfileOverlay && selectedCountry && contractClient && (
        <CountryProfileOverlay
          isOpen={showCountryProfileOverlay}
          onClose={() => {
            setShowCountryProfileOverlay(false);
            setSelectedCountry(null);
          }}
          countryCode={selectedCountry.code}
          countryName={selectedCountry.name}
          contractClient={contractClient}
          walletAddress={walletAddress}
          mainAdminAddress={mainAdminAddress}
          wallet={wallet}
          onCountryAllowedChange={(countryCode, allowed) => {
            setAllowedCountries(prev => {
              const next = new Set(prev);
              if (allowed) next.add(countryCode);
              else next.delete(countryCode);
              return next;
            });
            setTimeout(() => updateCountryOverlayRef.current(), 100);
          }}
          onManageCountry={(country) => {
            setSelectedCountry(country);
            setShowCountryProfileOverlay(false);
            setShowCountryManagementOverlay(true);
          }}
          onDepositSuccess={refreshAccountBalance}
        />
      )}

      {/* Floating "Live now" FAB.
          Draggable so users can park it wherever doesn't conflict
          with their map UX — position persists via localStorage.
          Hidden while a live session is in-flight — the minimised
          pill already covers that case so we don't want two
          competing bubbles. */}
      {walletAddress && !liveSessionActive && activeOverlay !== 'live-directory' && !profileOverlayFor && (
        <LiveFab onOpen={() => setActiveOverlay('live-directory')} />
      )}

      <LiveDirectoryOverlay
        isOpen={activeOverlay === 'live-directory'}
        onClose={() => setActiveOverlay(null)}
        viewerWallet={walletAddress}
        viewerLocation={viewerLocationForFeed}
        onOpenProfile={(w) => {
          // Route the viewer straight into the target's Live tab.
          // Keeping ProfileOverlay as the sink means all the existing
          // watch / tip / countdown plumbing works unchanged.
          setActiveOverlay(null);
          setProfileOverlayTab('live');
          setProfileOverlayFor(w);
        }}
        onGoLive={walletAddress ? () => {
          setActiveOverlay(null);
          setProfileOverlayTab('live');
          setProfileOverlayFor(walletAddress);
        } : undefined}
      />

      {/* Navigation Menu - Top Right */}
      <NavigationMenu
        onNavigate={(section) => {
          if (section === 'pending-deposits') {
            setDashboardInitialSection('deposits');
            setActiveOverlay('dashboard');
          } else if (section === 'spatial-objects') {
            // Hamburger-menu invocation: open the spatial admin
            // overlay in centered modal mode (the country-tooltip
            // path uses 'floating' via openSpatialAdminWith). Clear
            // any leftover openParams so we don't accidentally
            // reapply yesterday's edit hand-off.
            setDashboardInitialSection(undefined);
            setSpatialAdminOpenParams(null);
            setSpatialAdminOpenMode('centered');
            setActiveOverlay('spatial-objects');
          } else {
            setDashboardInitialSection(undefined);
            setActiveOverlay(section);
          }
        }}
        activeSection={activeOverlay === 'dashboard' && dashboardInitialSection === 'deposits' ? 'pending-deposits' : activeOverlay}
        walletAddress={walletAddress}
        isAdmin={isAdmin}
        isAdminOrCountryAdmin={isAdminOrCountryAdmin}
        onSettingsClick={() => {
          setShowSettings(true);
        }}
        map={map.current}
        markerUpdateTimeoutRef={markerUpdateTimeoutRef}
        pendingDepositsCount={pendingGeoLinkCount}
      />

      {/* GeoTrust Dashboard — full-screen view accessible from hamburger menu */}
      <GeoTrustDashboard
        isOpen={activeOverlay === 'dashboard'}
        onClose={() => { setActiveOverlay(null); setDashboardInitialSection(undefined); }}
        initialSection={dashboardInitialSection as any}
        onPendingCountChange={setPendingGeoLinkCount}
        walletAddress={walletAddress}
        wallet={wallet as any}
        passkeyKitService={passkeyKitService as any}
        contractClient={contractClient}
        readOnlyClient={readOnlyClient}
        isAdmin={isAdmin}
        mainAdminAddress={mainAdminAddress}
        allowedCountries={allowedCountries}
        defaultAllowAll={defaultAllowAll}
        totalCountries={totalCountries}
        stellarBalance={accountBalance}
        onDepositSuccess={refreshAccountBalance}
        onWalletBalanceChange={refreshAccountBalance}
        playerLocation={playerLocation}
        otherUsers={otherUsers}
        nearbyNFTs={nearbyNFTs}
        nearbyContracts={nearbyContracts}
        mapStyle={mapStyle}
        borderRevision={borderRevision}
        onBordersChanged={() => {
          loadCountryOverlay({ force: true }).then(() => {
            setTimeout(() => updateCountryOverlay(), 600);
            setTimeout(() => updateCountryOverlay(), 1500);
          });
          setBorderRevision(r => r + 1);
        }}
        onCountryAllowedChange={(countryCode, allowed) => {
          setAllowedCountries(prev => {
            const next = new Set(prev);
            if (allowed) next.add(countryCode); else next.delete(countryCode);
            return next;
          });
        }}
        onPolicyChanged={async () => {
          // Re-read allowed countries from the contract so the dashboard map re-colors
          const client = readOnlyClient || contractClient;
          if (!client) return;
          try {
            const listResult = await (client as any).listAllowedCountries(0, 1000);
            if (Array.isArray(listResult) && listResult.length > 0) {
              setAllowedCountries(new Set(listResult));
            }
          } catch { /* keep existing */ }
          fetchCountryPolicy();
        }}
      />

      {/* Smart Contracts Overlay */}
      <SmartContractsOverlay
        isOpen={activeOverlay === 'smart-contracts'}
        onClose={() => setActiveOverlay(null)}
        walletAddress={walletAddress}
        wallet={wallet}
        passkeyKitService={passkeyKitService}
        allowedCountries={allowedCountries}
      />

      {/* Spatial Objects admin overlay (CRUD on the GeoLink registry) */}
      <SpatialObjectsAdminOverlay
        isOpen={activeOverlay === 'spatial-objects'}
        onClose={() => { setActiveOverlay(null); setSpatialAdminOpenParams(null); }}
        map={map.current}
        walletAddress={walletAddress}
        playerLocation={playerLocation ? {
          longitude: playerLocation[0],
          latitude:  playerLocation[1],
        } : null}
        mapboxToken={process.env.REACT_APP_MAPBOX_TOKEN ?? null}
        openParams={spatialAdminOpenParams}
        onParamsConsumed={() => setSpatialAdminOpenParams(null)}
        openMode={spatialAdminOpenMode}
        isAdmin={isAdmin}
        resolveCountry={resolveSpatialRowCountry}
        signer={contractClient?.getWallet() ?? null}
        networkPassphrase={contractClient?.getNetwork()}
      />

      {/* Admin Overlay */}
      {isAdmin && contractClient && (
        <AdminOverlay
          isOpen={activeOverlay === 'admin'}
          onClose={() => setActiveOverlay(null)}
          contractClient={contractClient}
          allowedCountries={allowedCountries}
          defaultAllowAll={defaultAllowAll}
          onCountryToggle={async () => {
            await fetchCountryPolicy();
          }}
          onDefaultAllowAllChange={(value) => {
            setDefaultAllowAll(value);
            // useEffect watching defaultAllowAll will call updateCountryOverlay automatically
          }}
          onCountryAllowedChange={(countryCode, allowed) => {
            setAllowedCountries(prev => {
              const next = new Set(prev);
              if (allowed) {
                next.add(countryCode);
              } else {
                next.delete(countryCode);
              }
              return next;
            });
            // Directly trigger map update — don't wait for the useEffect timer
            setTimeout(() => updateCountryOverlayRef.current(), 100);
          }}
          map={map.current}
          walletAddress={walletAddress}
          mainAdminAddress={mainAdminAddress}
          onAdminChanged={async () => {
            // Re-check admin status after admin change
            setTimeout(async () => {
              if (readOnlyClient && walletAddress) {
                try {
                  const newMainAdmin = await readOnlyClient.getAdmin();
                  setMainAdminAddress(newMainAdmin);
                } catch (error) {
// error('[App] Failed to refresh admin address:', error);
                }
              }
            }, 2000);
          }}
          onManageCountry={(country) => {
            setSelectedCountry(country);
            setActiveOverlay(null);
            setShowCountryManagementOverlay(true);
          }}
        />
      )}

      {/* Drone / vehicle / GCS telemetry layer — lazy adapter + REST
          poll for entities.  Opt out via REACT_APP_TELEMETRY_ENABLED=0. */}
      <TelemetryLayer
        map={map.current}
        enabled={process.env.REACT_APP_TELEMETRY_ENABLED !== '0'}
      />

      {/* GeoLink spatial-objects layer — buildings/floors/zones/slices
          rendered on top of the existing Mapbox map. Mapbox stays the
          basemap; MapTiler features (provider=maptiler render hints +
          optional satellite/topo raster overlay) are purely additive
          and gate behind REACT_APP_MAPTILER_KEY. The whole subsystem
          is toggleable from its floating controls cluster, and can be
          killed entirely with REACT_APP_SPATIAL_OBJECTS_ENABLED=0. */}
      {process.env.REACT_APP_SPATIAL_OBJECTS_ENABLED !== '0' && map.current && (
        <SpatialObjectsMapLayer
          map={map.current}
          publicKey={walletAddress}
          location={playerLocation
            ? { longitude: playerLocation[0], latitude: playerLocation[1] }
            : null}
          controlsStyle={{ top: 80, right: 16 }}
          /* Floating view-controls (provider/floor/slice/explode/MapTiler/
             opacity) are intentionally hidden — spatial objects are
             managed from the country tooltip's spatial section now, and
             those controls were just clutter when no buildings are
             rendered yet. Re-enable by setting showControls back to true
             if we want to expose floor/slice picking again. */
          showControls={false}
          /* Admin-only: stream OGC 3D Tiles (photogrammetry / BIM /
             point clouds) from a tileset.json or Cesium Ion asset via
             deck.gl, rendered in register with our spatial extrusions. */
          showTiles3D={isAdmin}
          /* Reuse the app's shared cinematic 3D zoom-in for inspector
             attribute-row clicks, so selecting a building glides in with
             pitch + 3D buildings instead of snapping top-down. */
          cinematicFlyTo={(center) => { if (map.current) flyToWithMode(map.current, center, true); }}
          /* Wallet bridges so a linked execution rule can actually be
             authorised + signed + broadcast (fixes "No wallet bridge"). */
          bridges={spatialInteractionBridges}
        />
      )}

      {/* Admin-only: seed a synthetic drone/vehicle/GCS to exercise the
          TelemetryLayer without a real MAVLink/PX4 gateway. */}
      {isAdmin && (
        <RegisterTestEntityOverlay
          isOpen={activeOverlay === 'register-test-entity'}
          onClose={() => setActiveOverlay(null)}
          playerLocation={playerLocation}
        />
      )}

      {/* Compliance Lists (watchlist / deny-list) admin — main admin
          sees global scope, country admins see their country scope.
          Server enforces authorisation on every mutation. */}
      {(isAdmin || isAdminOrCountryAdmin) && (
        <WatchlistAdminOverlay
          isOpen={activeOverlay === 'compliance-lists'}
          onClose={() => setActiveOverlay(null)}
          allowedCountries={Array.from(adminCountryScope)}
          canEditGlobal={isAdmin}
          defaultCountry={selectedCountry?.code}
        />
      )}

      {/* Border Proposals Dashboard — main admin + country admins */}
      {walletAddress && (
        <BorderProposalsDashboard
          isOpen={activeOverlay === 'border-proposals'}
          onClose={() => setActiveOverlay(null)}
          walletAddress={walletAddress}
          mainAdminAddress={mainAdminAddress}
          isAdmin={isAdmin}
          accountBalance={accountBalance}
          passkeyAccountAddress={
            walletAddress
              ? (passkeyKitService.getAccountByWallet(walletAddress)?.accountAddress ?? null)
              : null
          }
          onRefreshWalletBalances={refreshAccountBalance}
        />
      )}

      {/* Sessions Overlay */}
      <SessionsOverlay
        isOpen={activeOverlay === 'sessions'}
        onClose={() => setActiveOverlay(null)}
        userCurrentSession={userCurrentSession}
        activeSessions={activeSessions}
        walletAddress={walletAddress}
        onJoinSession={handleJoinSession}
        isAdmin={isAdmin}
        onDeleteSession={async (sessionId: number) => {
          if (!contractClient || !isAdmin) return;
          
          try {
            await contractClient.adminDeleteSession(sessionId);
            
            // Remove from local state
            endedSessionsRef.current.add(sessionId);
            if (userCurrentSession === sessionId) {
              setUserCurrentSession(null);
              setSessionLink('');
            }
            
            // Refresh sessions list
            setTimeout(async () => {
              await fetchActiveSessions();
            }, 2000);
            
            setNotificationState({
              isOpen: true,
              title: 'Success',
              message: `Session #${sessionId} has been deleted.`,
              type: 'success',
              autoClose: 3000,
            });
          } catch (error: any) {
// error('[App] Failed to delete session:', error);
            setNotificationState({
              isOpen: true,
              title: 'Error',
              message: `Failed to delete session: ${error.message || 'Unknown error'}`,
              type: 'error',
              autoClose: 5000,
            });
          }
        }}
        onDeleteAllSessions={async () => {
          if (!contractClient || !isAdmin) return;
          
          try {
            const deletedCount = await contractClient.adminDeleteAllSessions();
            
            // Clear user's current session
            setUserCurrentSession(null);
            setSessionLink('');
            recentlyJoinedSessionRef.current = null;
            
            // Clear all session tracking
            knownActiveSessionIdsRef.current.clear();
            maxSessionIdSeenRef.current = 0;
            endedSessionsRef.current.clear();
            
            // Refresh sessions list
            setTimeout(async () => {
              await fetchActiveSessions();
            }, 2000);
            
            setNotificationState({
              isOpen: true,
              title: 'Success',
              message: `Deleted ${deletedCount} session(s). All sessions have been cleared.`,
              type: 'success',
              autoClose: 5000,
            });
          } catch (error: any) {
// error('[App] Failed to delete all sessions:', error);
            setNotificationState({
              isOpen: true,
              title: 'Error',
              message: `Failed to delete all sessions: ${error.message || error}`,
              type: 'error',
              autoClose: 5000,
            });
          }
        }}
        onEndSession={async (sessionId, _isPlayer1, _isPlayer2, sessionState) => {
          if (!contractClient) return;
          
          setConfirmationState({
            isOpen: true,
            title: 'End Session',
            message: `Are you sure you want to end session #${sessionId}? This ends the live session for everyone in it.`,
            confirmText: 'End Session',
            cancelText: 'Cancel',
            type: 'danger',
            onConfirm: async () => {
              setConfirmationState(null);
              
              try {
                if (sessionState !== 'Active' && sessionState !== 'Waiting') {
                  setNotificationState({
                    isOpen: true,
                    title: 'Error',
                    message: `Cannot end session in ${sessionState} state.`,
                    type: 'error',
                    autoClose: 5000,
                  });
                  return;
                }

                // Session lifecycle is fully internal now — end_session on
                // the geo-session contract (the external Game Hub is retired).
                await contractClient.endSession(sessionId);

                endedSessionsRef.current.add(sessionId);
                
                if (userCurrentSession === sessionId) {
                  setUserCurrentSession(null);
                  setSessionLink('');
                }
                
                setTimeout(async () => {
                  await fetchActiveSessions();
                }, 2000);
                
                setNotificationState({
                  isOpen: true,
                  title: 'Session Ended',
                  message: `Session #${sessionId} ended successfully!`,
                  type: 'success',
                  autoClose: 5000,
                });
              } catch (error: any) {
// error('Failed to end session:', error);
                setNotificationState({
                  isOpen: true,
                  title: 'Error',
                  message: `Failed to end session: ${error.message || error}`,
                  type: 'error',
                  autoClose: 7000,
                });
              }
            },
          });
        }}
        onViewSessionDetails={async () => {
          setActiveOverlay(null);
          // Fetch session details if not in activeSessions
          if (userCurrentSession && !activeSessions.find(s => s.sessionId === userCurrentSession)) {
            try {
              if (readOnlyClient) {
                const session = await readOnlyClient.getSession(userCurrentSession);
                if (session) {
                  setSessionDetailsData({
                    sessionId: userCurrentSession,
                    player1: session.player1 || null,
                    player2: session.player2 || null,
                    state: session.state || 'Active',
                    p1CellId: session.p1CellId || session.p1_cell_id,
                    p2CellId: session.p2CellId || session.p2_cell_id,
                    p1Country: session.p1Country || session.p1_country,
                    p2Country: session.p2Country || session.p2_country,
                    createdLedger: session.createdLedger || session.created_ledger,
                    players: session.players,
                    isPublic: session.isPublic || session.is_public,
                    is_public: session.is_public || session.isPublic,
                  });
                }
              }
            } catch (error) {
// error('[App] Failed to fetch session details:', error);
            }
          } else {
            setSessionDetailsData(null);
          }
          setShowSessionDetailsOverlay(true);
        }}
        contractClient={contractClient}
        onShowOnlyTrustGroups={handleShowOnlyTrustGroups}
      />

      {/* Pending Deposits Overlay */}
      {walletAddress && (
        <PendingDepositsOverlay
          isOpen={activeOverlay === 'pending-deposits'}
          onClose={() => setActiveOverlay(null)}
          deposits={pendingDeposits}
          onApprove={handleApproveDeposit}
          onDecline={handleDeclineDeposit}
          walletAddress={walletAddress}
        />
      )}

      {/* Trust Group Manager Overlay */}
      {walletAddress && contractClient && readOnlyClient && activeOverlay === 'trust-groups' && (
        <div
          className="overlay modal-overlay"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'max(8px, env(safe-area-inset-top)) 8px max(12px, env(safe-area-inset-bottom))',
            boxSizing: 'border-box',
          }}
        >
          <TrustGroupManager
            contractClient={contractClient}
            readOnlyClient={readOnlyClient}
            walletAddress={walletAddress}
            onClose={() => setActiveOverlay(null)}
            onTrustGroupMemberFilterChange={handleTrustGroupMemberFilterChange}
            onCreatePrivateSession={async (trustGroupId: number) => {
              if (!contractClient || !navigator.geolocation) {
                alert('Geolocation is not supported');
                return;
              }

              navigator.geolocation.getCurrentPosition(
                async (position) => {
                  const { latitude, longitude } = position.coords;
                  const countryCode = await getCountryCode(longitude, latitude).catch(() => null);
                  if (!countryCode) {
                    alert('Could not determine country code');
                    return;
                  }

                  const cellId = calculateCellId(latitude, longitude);
                  const assetTag = await getAssetTag();
                  
                  try {
                    const sessionId = await contractClient.createPrivateSessionWithTrustGroup(trustGroupId);
                    await contractClient.joinSession(sessionId, cellId, assetTag, countryCode);
                    setUserCurrentSession(sessionId);
                    setSessionLink(`${window.location.origin}?session=${sessionId}`);
                    setActiveOverlay(null);
                    setNotificationState({
                      isOpen: true,
                      title: 'Success',
                      message: `Private session created with trust group! Session ID: ${sessionId}`,
                      type: 'success',
                      autoClose: 3000,
                    });
                  } catch (error: any) {
// error('[App] Failed to create private session:', error);
                    setNotificationState({
                      isOpen: true,
                      title: 'Error',
                      message: `Failed to create private session: ${error.message || 'Unknown error'}`,
                      type: 'error',
                      autoClose: 5000,
                    });
                  }
                },
                (error) => {
                  alert('Failed to get location: ' + error.message);
                }
              );
            }}
          />
        </div>
      )}

      {/* Scene controller — applies globe / atmosphere / terrain and
          re-applies them across basemap style swaps. Renders nothing. */}
      <MapSceneController
        map={map.current}
        enable3D={enable3D}
        showGlobe={showGlobe}
        showAtmosphere={showAtmosphere}
        showTerrain={showTerrain}
        terrainExaggeration={terrainExaggeration}
        mapStyle={mapStyle}
      />

      {/* Settings Overlay */}
      <SettingsOverlay
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        map={map.current}
        mapFilters={mapFilters}
        onMapFiltersChange={setMapFilters}
        mapStyle={mapStyle}
        onMapStyleChange={setMapStyle}
        enable3D={enable3D}
        onEnable3DChange={setEnable3D}
        show3DBuildings={showBuildings}
        onShow3DBuildingsChange={setShowBuildings}
        showTerrain={showTerrain}
        onShowTerrainChange={setShowTerrain}
        showGlobe={showGlobe}
        onShowGlobeChange={setShowGlobe}
        showAtmosphere={showAtmosphere}
        onShowAtmosphereChange={setShowAtmosphere}
        terrainExaggeration={terrainExaggeration}
        onTerrainExaggerationChange={setTerrainExaggeration}
      />
      
      {/* Country Vault Balance Tooltip — desktop float / mobile bottom sheet */}
      {hoveredCountry && (() => {
        const accentColor = isLoadingVaultBalance
          ? '#4a9eff'
          : hoveredVaultInfo?.enabled ? '#4CAF50'
          : hoveredVaultInfo?.registered ? '#ffa500'
          : '#475569';

        const touchDevice = isMobileViewport;

        // Compute the country's bbox + centroid from the loaded
        // GeoJSON so the inline spatial-objects section can scope its
        // queries (in-country bbox + nearby radius). Falls back to
        // null silently if the geojson hasn't loaded yet — the section
        // gracefully renders just the "nearby" group in that case.
        const spatialBounds = getCountryBounds(geojsonDataRef.current, {
          code: hoveredCountry.countryCode,
          iso2: hoveredCountry.iso2,
        });
        // Live map center/zoom — passed through so the spatial section's
        // "+ Building / + Zone / + Event" quick-add can use the operator's
        // current view as the geometry pre-fill. (Country centroids are a
        // continent-scale guess and used to drop new objects in the wrong
        // place; the live camera is a much better proxy for intent.)
        const liveMapCenter = (() => {
          try {
            const c = map.current?.getCenter();
            return c ? { lng: c.lng, lat: c.lat } : null;
          } catch { return null; }
        })();
        const liveMapZoom = (() => {
          try { return map.current?.getZoom() ?? null; } catch { return null; }
        })();
        const spatialScope = {
          countryCode: hoveredCountry.countryCode,
          countryIso2: hoveredCountry.iso2,
          countryName: hoveredCountry.countryName,
          bbox:     spatialBounds?.bbox     ?? null,
          centroid: spatialBounds?.centroid ?? null,
          // Country polygon so the section can point-in-polygon filter
          // spatial objects to the ones actually inside this country
          // (bbox alone leaks neighbours across shared borders).
          polygon:  spatialBounds?.geometry ?? null,
          mapCenter: liveMapCenter,
          mapZoom:   liveMapZoom,
        };
        // Spatial-section "📍 Fly to" — always lands the camera at a 3D
        // bird's-eye angle (pitch 60), and uses the known building height
        // to pick a zoom that *fits* the tower instead of stuffing the
        // camera into its tip. Without this, flying to the Burj Khalifa
        // from a flat (pitch=0) view drops you at zoom 17 staring
        // straight down at the spire — which is how the bug originally
        // showed up.
        const spatialFlyTo = (lng: number, lat: number, heightMeters?: number | null) => {
          const m = map.current;
          if (!m) return;
          const baseZoom   = 17;
          const targetZoom = (heightMeters && heightMeters > 0)
            ? buildingAwareZoom(heightMeters, baseZoom)
            : baseZoom;
          // Suppress the post-zoomend building-aware auto-fit for a
          // moment — flyTo's intermediate zoom values would otherwise
          // schedule a competing easeTo that yanks the camera mid-flight.
          autoFitCooldownUntilRef.current = Date.now() + 4000;
          m.flyTo({
            center: [lng, lat],
            zoom:   targetZoom,
            pitch:  60,
            bearing: m.getBearing(),
            speed:  1.4,
            essential: true,
          });
        };

        // Desktop position and chrome now come from MapEntityTooltip,
        // which is the same card the spatial-object tooltip uses. The
        // avoidance arithmetic — keep clear of the top-right nav and the
        // bottom-right Inspector button — moved with it, unchanged, into
        // mapTooltipPlacement where it can be tested.
        const pos = hoveredCountry.position;
        const tipViewport = {
          width: mapContainer.current?.offsetWidth ?? window.innerWidth,
          height: mapContainer.current?.offsetHeight ?? window.innerHeight,
        };

        const dismissTooltip = () => {
          tooltipHoveredRef.current = false;
          mobileTooltipCountryRef.current = null;
          setHoveredCountry(null);
          setTooltipSearchVal('');
          setTooltipSearchResults([]);
          currentFetchingCountryRef.current = null;
          currentHoveredCountryCodeRef.current = null;
          setVaultBalance(null);
          setTotalVaultBalance(null);
          setIsLoadingVaultBalance(false);
          setHoveredVaultInfo(null);
        };

        const openVault = () => {
          dismissTooltip();
          setSelectedCountry({ code: hoveredCountry.countryCode, name: hoveredCountry.countryName });
          setShowCountryProfileOverlay(true);
        };

        const handleTooltipSearch = (q: string) => {
          setTooltipSearchVal(q);
          if (tooltipSearchDebounce.current) clearTimeout(tooltipSearchDebounce.current);
          if (!q.trim()) { setTooltipSearchResults([]); return; }
          tooltipSearchDebounce.current = setTimeout(async () => {
            setTooltipSearchLoading(true);
            try {
              const r = await fetch(
                `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?access_token=${mapboxgl.accessToken}&limit=4&types=place,locality,country,region`
              );
              const d = await r.json();
              setTooltipSearchResults(d.features || []);
            } catch { /* ignore */ }
            finally { setTooltipSearchLoading(false); }
          }, 320);
        };

        const flyToResult = (feature: any) => {
          if (!map.current) return;
          const [lng, lat] = feature.center;
          const targetZoom = feature.place_type?.[0] === 'country' ? 4 : 12;

          // Dismiss current tooltip immediately so the map is clear during animation.
          dismissTooltip();

          map.current.flyTo({ center: [lng, lat], zoom: targetZoom, speed: 1.4 });

          // After the fly animation ends, resolve the country at the new center
          // and show its tooltip automatically.
          const onMoveEnd = () => {
            map.current?.off('moveend', onMoveEnd);
            const center = map.current?.getCenter();
            if (!center) return;
            const resolved = resolveCountryRef.current?.(center.lng, center.lat, false)
              ?? resolveCountryRef.current?.(center.lng, center.lat, true);
            if (resolved) {
              const cachedName = countryNameCache.get(resolved.code) || resolved.name || `Country ${resolved.code}`;
              const iso2Upper = resolved.iso2 ? resolved.iso2.toUpperCase() : null;
              mobileTooltipCountryRef.current = resolved.code;
              setHoveredCountry({
                countryCode: resolved.code,
                countryName: cachedName,
                iso2: iso2Upper,
                position: { x: (mapContainer.current?.offsetWidth ?? window.innerWidth) / 2, y: (mapContainer.current?.offsetHeight ?? window.innerHeight) / 2 },
              });
              // Load vault balance for the newly arrived country.
              if (iso2Upper) loadVaultInfoRef.current?.(iso2Upper);
            }
          };
          map.current.once('moveend', onMoveEnd);
        };

        const flyToCountry = () => {
          fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(hoveredCountry.countryName)}.json?access_token=${mapboxgl.accessToken}&types=country&limit=1`)
            .then(r => r.json())
            .then(d => {
              if (d.features?.[0]) {
                const bb = d.features[0].bbox;
                const [lng, lat] = d.features[0].center;
                if (bb && map.current) map.current.fitBounds([[bb[0], bb[1]], [bb[2], bb[3]]], { padding: 40, speed: 1.4 });
                else map.current?.flyTo({ center: [lng, lat], zoom: 4, speed: 1.4 });
              }
            }).catch(() => {});
        };

        // Shared nav icon button
        const navBtn = (icon: string, title: string, onClick: () => void, highlight?: boolean, key?: string) => (
          <button
            key={key}
            title={title}
            onClick={e => { e.stopPropagation(); onClick(); }}
            style={{
              width: touchDevice ? '40px' : '30px', height: touchDevice ? '40px' : '30px',
              borderRadius: '8px', border: 'none',
              background: highlight ? 'rgba(99,102,241,0.25)' : 'rgba(255,255,255,0.09)',
              color: highlight ? '#818cf8' : '#94a3b8',
              fontSize: touchDevice ? '16px' : '14px',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, transition: 'background 0.15s',
            }}
          >
            {icon}
          </button>
        );

        // Quick map-style switch from the tooltip. MapSceneController
        // re-applies the globe/atmosphere/terrain after the style swap, so
        // switching here keeps the current 3D scene intact.
        const switchMapStyle = (url: string) => {
          const m = map.current;
          if (!m) return;
          try { m.setStyle(url); } catch { /* ignore */ }
          setMapStyle(url);
        };
        const mapStyleChoices: { key: string; label: string; icon: string; url: string }[] = [
          { key: 'light',     label: 'Light',     icon: '☀️', url: 'mapbox://styles/mapbox/light-v11' },
          { key: 'dark',      label: 'Dark',      icon: '🌙', url: 'mapbox://styles/mapbox/dark-v11' },
          { key: 'satellite', label: 'Satellite', icon: '🛰️', url: 'mapbox://styles/mapbox/satellite-streets-v12' },
          { key: 'streets',   label: 'Streets',   icon: '🛣️', url: 'mapbox://styles/mapbox/streets-v12' },
          { key: 'outdoors',  label: 'Outdoors',  icon: '⛰️', url: 'mapbox://styles/mapbox/outdoors-v12' },
        ];

        // ── VAULT INFO BLOCK (shared between mobile and desktop) ──
        const vaultBlock = (
          <div style={{ padding: touchDevice ? '10px 16px' : '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.06)', minHeight: '42px' }}>
            {!walletAddress ? (
              <div style={{ fontSize: '12px', color: '#475569', fontStyle: 'italic' }}>Connect wallet to see your balance</div>
            ) : !hoveredCountry.iso2 ? (
              <div style={{ fontSize: '12px', color: '#475569', fontStyle: 'italic' }}>Country code unavailable</div>
            ) : isLoadingVaultBalance ? (
              <div style={{ fontSize: '12px', color: '#4a9eff', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ display: 'inline-block', width: '7px', height: '7px', borderRadius: '50%', background: '#4a9eff', animation: 'pulse 1s ease-in-out infinite' }} />
                Checking vault…
              </div>
            ) : hoveredVaultInfo?.registered ? (
              hoveredVaultInfo.enabled ? (
                (() => {
                  const wb = vaultBalance ?? 0;
                  const sb = smartAccountVaultBalance ?? 0;
                  const combined = wb + sb;
                  const hasSmart = smartAccountVaultBalance !== null;
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        {hasSmart && wb > 0 && <div style={{ fontSize: '11px', color: '#64748b' }}>Wallet {(wb / 10_000_000).toFixed(2)} · Passkey {(sb / 10_000_000).toFixed(2)}</div>}
                        <div style={{ fontSize: touchDevice ? '15px' : '13px', fontWeight: 700, color: '#e2e8f0' }}>
                          Your: {((hasSmart ? combined : wb) / 10_000_000).toFixed(2)} XLM
                        </div>
                      </div>
                      {totalVaultBalance !== null && (
                        <div style={{ textAlign: 'right' as const }}>
                          <div style={{ fontSize: '10px', color: '#64748b' }}>Vault total</div>
                          <div style={{ fontSize: '12px', fontWeight: 700, color: '#4CAF50' }}>{(totalVaultBalance / 10_000_000).toFixed(2)} XLM</div>
                        </div>
                      )}
                    </div>
                  );
                })()
              ) : (
                <div style={{ fontSize: '12px', color: '#ffa500' }}>● Vault registered but disabled</div>
              )
            ) : (
              <div style={{ fontSize: '12px', color: '#475569' }}>○ No vault registered</div>
            )}
          </div>
        );

        // ── SEARCH BLOCK (shared) ──
        const searchBlock = (
          <div style={{ padding: touchDevice ? '10px 16px' : '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.06)', position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
              <span style={{ fontSize: '14px', color: '#475569', flexShrink: 0 }}>🔍</span>
              <input
                value={tooltipSearchVal}
                onChange={e => handleTooltipSearch(e.target.value)}
                placeholder="Search any place…"
                style={{
                  flex: 1, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)',
                  borderRadius: '7px', color: '#e2e8f0',
                  padding: touchDevice ? '8px 10px' : '5px 8px',
                  fontSize: touchDevice ? '14px' : '12px',
                  outline: 'none',
                }}
              />
              {tooltipSearchLoading && <span style={{ fontSize: '11px', color: '#4a9eff', flexShrink: 0 }}>…</span>}
            </div>
            {tooltipSearchResults.length > 0 && (
              <div style={{
                position: touchDevice ? 'static' : 'absolute',
                top: touchDevice ? undefined : '100%',
                left: touchDevice ? undefined : '10px',
                right: touchDevice ? undefined : '10px',
                marginTop: touchDevice ? '8px' : undefined,
                zIndex: 10002, background: '#0e1623', border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '8px',
                overflowY: touchDevice ? 'auto' : 'hidden',
                overflowX: 'hidden',
                maxHeight: touchDevice ? '180px' : undefined,
                boxShadow: '0 8px 28px rgba(0,0,0,0.75)',
              }}>
                {tooltipSearchResults.map((f: any, i: number) => (
                  <div
                    key={i}
                    onMouseDown={() => flyToResult(f)}
                    onClick={() => flyToResult(f)}
                    style={{
                      padding: touchDevice ? '11px 14px' : '7px 11px',
                      fontSize: touchDevice ? '13px' : '11px', color: '#cbd5e1', cursor: 'pointer',
                      borderBottom: i < tooltipSearchResults.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                      display: 'flex', gap: '8px', alignItems: 'center',
                    }}
                  >
                    <span style={{ color: '#475569', fontSize: '12px', flexShrink: 0 }}>
                      {f.place_type?.[0] === 'country' ? '🌍' : f.place_type?.[0] === 'region' ? '📍' : '🏙'}
                    </span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{f.place_name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

        // ── NAV BUTTONS ROW ──
        const liveTrackTitle = isLiveTracking
          ? 'End Live'
          : 'Go Live – auto-update your position while moving';
        const liveTrackLabel = isLiveTracking
          ? (effectiveMode === 'walking' ? 'Walking' : "You're Live")
          : 'Go Live';
        const modeColorMap: Record<string, string> = {
          walking: '#4CAF50', driving: '#FF6B35', flying: '#818cf8', stationary: '#94a3b8',
        };
        const modeIconMap: Record<string, string> = {
          stationary: '📍', walking: '🚶', driving: '🚗', flying: '✈️',
        };
        // Use effectiveMode (manual override wins) for colour + icon
        const liveTrackDotColor = isLiveTracking ? (modeColorMap[effectiveMode] ?? '#4CAF50') : undefined;
        const liveTrackIcon     = modeIconMap[effectiveMode] ?? '📍';
        const modeRankMap: Record<string, number> = { stationary: 0, walking: 1, driving: 2, flying: 3 };
        const isAutoMode = manualMode === null;

        const navRow = (
          <div style={{ padding: touchDevice ? '10px 16px 14px' : '8px 10px', display: 'flex', gap: '7px', alignItems: 'center', flexWrap: 'wrap' as const }}>
            {navBtn('🎯', `Fly to ${hoveredCountry.countryName}`, flyToCountry)}
            {navBtn('+', 'Zoom in', () => map.current?.zoomIn())}
            {navBtn('−', 'Zoom out', () => map.current?.zoomOut())}
            {navBtn('🌍', 'World view', () => map.current?.flyTo({ center: [0, 20], zoom: 2, pitch: 0, bearing: 0, speed: 1.2 }))}
            {navBtn('📍', 'My location', () => navigator.geolocation?.getCurrentPosition(({ coords }) => map.current?.flyTo({ center: [coords.longitude, coords.latitude], zoom: 12, speed: 1.4 })))}
            {navBtn('⛶', 'Reset pitch', () => map.current?.easeTo({ pitch: 0, bearing: 0, duration: 600 }))}
            {navBtn(
              auto3DBuildings ? '🏢' : '🗺️',
              auto3DBuildings
                ? 'Auto 3D buildings: ON — click to disable'
                : 'Auto 3D buildings: OFF — click to enable',
              () => setAuto3DBuildings(v => !v),
              auto3DBuildings,
            )}
            {/* Map style switcher — divider then one chip per basemap */}
            <span style={{ width: '1px', alignSelf: 'stretch', background: 'rgba(255,255,255,0.12)', margin: '0 1px', flexShrink: 0 }} />
            {mapStyleChoices.map(s =>
              navBtn(s.icon, `Map style: ${s.label}`, () => switchMapStyle(s.url), mapStyle === s.url, `style-${s.key}`),
            )}
            {/* Live tracking toggle */}
            {walletAddress && (
              <button
                title={liveTrackTitle}
                onClick={e => { e.stopPropagation(); isLiveTracking ? stopLiveTracking() : startLiveTracking(); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '5px',
                  padding: touchDevice ? '6px 12px' : '4px 9px',
                  borderRadius: '8px', border: 'none',
                  background: isLiveTracking
                    ? `${liveTrackDotColor}28`
                    : 'rgba(255,255,255,0.09)',
                  color: isLiveTracking ? liveTrackDotColor : '#94a3b8',
                  fontSize: touchDevice ? '13px' : '12px', fontWeight: 700, cursor: 'pointer',
                  flexShrink: 0, transition: 'background 0.15s',
                  outline: isLiveTracking ? `1px solid ${liveTrackDotColor}` : 'none',
                }}
              >
                <span style={{ fontSize: touchDevice ? '15px' : '13px' }}>
                  {isLiveTracking ? liveTrackIcon : '●'}
                </span>
                <span>{liveTrackLabel}</span>
                {isLiveTracking && (
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: liveTrackDotColor, animation: 'pulse 1.4s ease-in-out infinite', flexShrink: 0 }} />
                )}
              </button>
            )}
            {/* Mode icon buttons — only shown when live tracking is active */}
            {isLiveTracking && (() => {
              const sz = touchDevice ? '40px' : '30px';
              const fs = touchDevice ? '16px' : '14px';
              const btnBase: React.CSSProperties = {
                width: sz, height: sz, borderRadius: '8px', border: 'none',
                fontSize: fs, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, transition: 'background 0.15s, outline 0.15s',
              };
              const driveLocked = (modeRankMap['driving'] ?? 2) < (modeRankMap[movementMode] ?? 0);
              const autoActive  = isAutoMode;
              const driveActive = !isAutoMode && effectiveMode === 'driving';
              const flyActive   = !isAutoMode && effectiveMode === 'flying';
              const autoColor   = '#94a3b8';
              const driveColor  = modeColorMap['driving'];
              const flyColor    = modeColorMap['flying'];
              return (
                <>
                  <button
                    title="Auto — follow GPS"
                    onClick={e => { e.stopPropagation(); handleClearMode(); }}
                    style={{ ...btnBase, background: autoActive ? 'rgba(148,163,184,0.22)' : 'rgba(255,255,255,0.09)', color: autoActive ? autoColor : '#94a3b8', outline: autoActive ? `1px solid ${autoColor}` : 'none' }}
                  >🔄</button>
                  <button
                    title={driveLocked ? `Locked — GPS detects ${movementMode}` : 'Override to Driving mode'}
                    onClick={e => { e.stopPropagation(); if (!driveLocked) handleSetMode('driving'); }}
                    disabled={driveLocked}
                    style={{ ...btnBase, background: driveActive ? `${driveColor}28` : 'rgba(255,255,255,0.09)', color: driveActive ? driveColor : '#94a3b8', outline: driveActive ? `1px solid ${driveColor}` : 'none', opacity: driveLocked ? 0.3 : 1, cursor: driveLocked ? 'not-allowed' : 'pointer' }}
                  >🚗</button>
                  <button
                    title="Override to Flying mode"
                    onClick={e => { e.stopPropagation(); handleSetMode('flying'); }}
                    style={{ ...btnBase, background: flyActive ? `${flyColor}28` : 'rgba(255,255,255,0.09)', color: flyActive ? flyColor : '#94a3b8', outline: flyActive ? `1px solid ${flyColor}` : 'none' }}
                  >✈️</button>
                </>
              );
            })()}
          </div>
        );

        // ══════════════ MOBILE COMPACT CARD (TOP-DOCKED) ══════════════
        if (touchDevice) {
          return (
            <>
              {/* No backdrop click-catcher: handleMobileGlobalClick handles dismiss/switch for us */}
              <div
                key={`tooltip-${hoveredCountry.countryCode}-${hoveredCountry.iso2}-${tooltipRenderKey}`}
                onClick={e => e.stopPropagation()}
                style={{
                  position: 'absolute',
                  top: '76px',
                  left: '8px',
                  right: '8px',
                  margin: '0 auto',
                  maxWidth: '360px',
                  // The ladder, not a hand-picked number. This card is
                  // its own markup rather than `MapEntityTooltip`, and
                  // the 9999 it used to carry put an incidental hover
                  // card above every deliberate surface — including a
                  // conversation, which it covered completely.
                  zIndex: surfaceZIndex('country_tooltip'),
                  background: '#0b1120',
                  borderRadius: '14px',
                  border: `2px solid ${accentColor}`,
                  boxShadow: '0 10px 36px rgba(0,0,0,0.65)',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  maxHeight: '58vh',
                  overflowY: 'auto',
                }}
              >
                {/* Header */}
                <div style={{ padding: '10px 14px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: accentColor, boxShadow: `0 0 7px ${accentColor}`, flexShrink: 0 }} />
                    {hoveredCountry.iso2 && (
                      <img
                        src={getFlagUrl(hoveredCountry.iso2)}
                        alt={hoveredCountry.iso2}
                        title={hoveredCountry.countryName}
                        style={{ width: '22px', height: 'auto', borderRadius: '2px', flexShrink: 0, display: 'block' }}
                        onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    )}
                    <span style={{ fontWeight: 700, fontSize: '16px', color: '#f0f4ff' }}>{hoveredCountry.countryName}</span>
                    {hoveredCountry.iso2 && (
                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', background: 'rgba(255,255,255,0.1)', borderRadius: '5px', padding: '2px 7px' }}>
                        {hoveredCountry.iso2}
                      </span>
                    )}
                  </div>
                  <button onClick={dismissTooltip} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', width: '30px', height: '30px', color: '#94a3b8', fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                </div>

                {/* Vault info */}
                {vaultBlock}

                {/* Open Vault CTA (prominent on mobile) */}
                <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  <button
                    onClick={openVault}
                    style={{
                      width: '100%', padding: '12px', borderRadius: '10px', border: 'none',
                      background: hoveredVaultInfo?.enabled ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'rgba(255,255,255,0.08)',
                      color: hoveredVaultInfo?.enabled ? '#fff' : '#64748b',
                      fontSize: '14px', fontWeight: 700, cursor: 'pointer',
                      boxShadow: hoveredVaultInfo?.enabled ? '0 4px 16px rgba(99,102,241,0.4)' : 'none',
                    }}
                  >
                    {hoveredVaultInfo?.enabled ? '🏦 Open Vault' : '🏦 View Country'}
                  </button>
                </div>

                {/* Spatial objects (in-country + nearby) */}
                <SpatialObjectsCountrySection
                  scope={spatialScope}
                  onOpenAdmin={openSpatialAdminWith}
                  onFlyTo={spatialFlyTo}
                  walletAddress={walletAddress}
                />

                {/* Search */}
                {searchBlock}

                {/* Nav buttons */}
                {navRow}
              </div>
            </>
          );
        }

        // ══════════════ DESKTOP FLOATING TOOLTIP ══════════════
        return (
          <MapEntityTooltip
            key={`tooltip-${hoveredCountry.countryCode}-${hoveredCountry.iso2}-${tooltipRenderKey}`}
            anchor={pos}
            viewport={tipViewport}
            // Declared to the overlay coordinator so this card yields to
            // a conversation, a payment form or a player — surfaces that
            // render from the spatial layer, which this subtree cannot
            // see. The id is constant because only one country is
            // hovered at a time; mounting and unmounting is what opens
            // and closes the surface.
            surfaceId="country-tooltip"
            surfaceKind="country_tooltip"
            accentColor={accentColor}
            title={hoveredCountry.countryName}
            leading={hoveredCountry.iso2 ? (
              <img
                src={getFlagUrl(hoveredCountry.iso2)}
                alt={hoveredCountry.iso2}
                title={hoveredCountry.countryName}
                style={{ width: '20px', height: 'auto', borderRadius: '2px', flexShrink: 0, display: 'block' }}
                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            ) : null}
            badge={hoveredCountry.iso2 ? (
              <span style={tooltipBadgeStyle}>{hoveredCountry.iso2}</span>
            ) : null}
            onMouseEnter={() => {
              tooltipHoveredRef.current = true;
              // Cancel any pending country-leave dismiss.
              if (countryLeaveTimerRef.current) {
                clearTimeout(countryLeaveTimerRef.current);
                countryLeaveTimerRef.current = null;
              }
            }}
            onMouseLeave={() => {
              tooltipHoveredRef.current = false;
              // Short grace period so rapid re-entry doesn't flicker.
              setTimeout(() => {
                if (!tooltipHoveredRef.current) dismissTooltip();
              }, 200);
            }}
          >
            {vaultBlock}

            {/* Spatial objects (in-country + nearby) */}
            <SpatialObjectsCountrySection
              scope={spatialScope}
              onOpenAdmin={openSpatialAdminWith}
              onFlyTo={spatialFlyTo}
              walletAddress={walletAddress}
            />

            {searchBlock}

            {/* Desktop nav row — includes 🏦 vault button */}
            <div style={{ padding: '8px 10px', display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' as const }}>
              {navBtn('🎯', `Fly to ${hoveredCountry.countryName}`, flyToCountry)}
              {navBtn('🏦', 'Open country vault', openVault, hoveredVaultInfo?.enabled)}
              {navBtn('+', 'Zoom in', () => map.current?.zoomIn())}
              {navBtn('−', 'Zoom out', () => map.current?.zoomOut())}
              {navBtn('🌍', 'World view', () => map.current?.flyTo({ center: [0, 20], zoom: 2, pitch: 0, bearing: 0, speed: 1.2 }))}
              {navBtn('📍', 'My location', () => navigator.geolocation?.getCurrentPosition(({ coords }) => map.current?.flyTo({ center: [coords.longitude, coords.latitude], zoom: 12, speed: 1.4 })))}
              {navBtn('⛶', 'Reset pitch', () => map.current?.easeTo({ pitch: 0, bearing: 0, duration: 600 }))}
              {navBtn(
                auto3DBuildings ? '🏢' : '🗺️',
                auto3DBuildings
                  ? 'Auto 3D buildings: ON — click to disable'
                  : 'Auto 3D buildings: OFF — click to enable',
                () => setAuto3DBuildings(v => !v),
                auto3DBuildings,
              )}
              {/* Map style switcher — divider then one chip per basemap */}
              <span style={{ width: '1px', alignSelf: 'stretch', background: 'rgba(255,255,255,0.12)', margin: '0 1px', flexShrink: 0 }} />
              {mapStyleChoices.map(s =>
                navBtn(s.icon, `Map style: ${s.label}`, () => switchMapStyle(s.url), mapStyle === s.url, `style-${s.key}`),
              )}
              {/* Live tracking toggle */}
              {walletAddress && (
                <button
                  title={liveTrackTitle}
                  onClick={e => { e.stopPropagation(); isLiveTracking ? stopLiveTracking() : startLiveTracking(); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '4px',
                    padding: '4px 9px',
                    borderRadius: '8px', border: 'none',
                    background: isLiveTracking
                      ? `${liveTrackDotColor}28`
                      : 'rgba(255,255,255,0.09)',
                    color: isLiveTracking ? liveTrackDotColor : '#94a3b8',
                    fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                    flexShrink: 0, transition: 'background 0.15s',
                    outline: isLiveTracking ? `1px solid ${liveTrackDotColor}` : 'none',
                  }}
                >
                  <span style={{ fontSize: '13px' }}>
                    {isLiveTracking ? liveTrackIcon : '●'}
                  </span>
                  <span>{liveTrackLabel}</span>
                  {isLiveTracking && (
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: liveTrackDotColor, animation: 'pulse 1.4s ease-in-out infinite', flexShrink: 0 }} />
                  )}
                </button>
              )}
              {/* Mode icon buttons — only shown when live tracking is active */}
              {isLiveTracking && (() => {
                const btnBase: React.CSSProperties = {
                  width: '30px', height: '30px', borderRadius: '8px', border: 'none',
                  fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, transition: 'background 0.15s, outline 0.15s',
                };
                const driveLocked = (modeRankMap['driving'] ?? 2) < (modeRankMap[movementMode] ?? 0);
                const autoActive  = isAutoMode;
                const driveActive = !isAutoMode && effectiveMode === 'driving';
                const flyActive   = !isAutoMode && effectiveMode === 'flying';
                const autoColor   = '#94a3b8';
                const driveColor  = modeColorMap['driving'];
                const flyColor    = modeColorMap['flying'];
                return (
                  <>
                    <button
                      title="Auto — follow GPS"
                      onClick={e => { e.stopPropagation(); handleClearMode(); }}
                      style={{ ...btnBase, background: autoActive ? 'rgba(148,163,184,0.22)' : 'rgba(255,255,255,0.09)', color: autoActive ? autoColor : '#94a3b8', outline: autoActive ? `1px solid ${autoColor}` : 'none' }}
                    >🔄</button>
                    <button
                      title={driveLocked ? `Locked — GPS detects ${movementMode}` : 'Override to Driving mode'}
                      onClick={e => { e.stopPropagation(); if (!driveLocked) handleSetMode('driving'); }}
                      disabled={driveLocked}
                      style={{ ...btnBase, background: driveActive ? `${driveColor}28` : 'rgba(255,255,255,0.09)', color: driveActive ? driveColor : '#94a3b8', outline: driveActive ? `1px solid ${driveColor}` : 'none', opacity: driveLocked ? 0.3 : 1, cursor: driveLocked ? 'not-allowed' : 'pointer' }}
                    >🚗</button>
                    <button
                      title="Override to Flying mode"
                      onClick={e => { e.stopPropagation(); handleSetMode('flying'); }}
                      style={{ ...btnBase, background: flyActive ? `${flyColor}28` : 'rgba(255,255,255,0.09)', color: flyActive ? flyColor : '#94a3b8', outline: flyActive ? `1px solid ${flyColor}` : 'none' }}
                    >✈️</button>
                  </>
                );
              })()}
            </div>
          </MapEntityTooltip>
        );
      })()}
    </div>
  );
};

export default App;
